import React, { useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { MarkdownRenderer } from 'components/ui/MarkdownRenderer'
import { normalizeHtmlBreaks } from 'components/ui/TableRenderer'
import { isOfferQuestionnaireResponse, OfferQuestionnaireWizard } from '../components/OfferQuestionnaireWizard'
import {
  OfferRegenerationWorkspace,
  applyQualityProposalsToOffer,
  buildQualityProposalValues,
} from '../components/OfferRegenerationWorkspace'
import type { RegenerationQualityAnalysis } from '../components/OfferRegenerationWorkspace'
import { DynamicCandidateOption, DynamicFormAnswers, DynamicFormCard, DynamicFormSchema } from '../components/DynamicFormCard'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Send,
  Loader2,
  CheckCircle,
  X,
  ArrowLeft,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Pencil,
  Check,
  Briefcase,
  Bot,
  Sparkles,
  Search,
  FileText,
  MessageSquare,
  Copy,
  RefreshCw,
  History,
  UserRound,
  Users,
  Scale,
  Lightbulb,
  AlertTriangle,
  ArrowRight,
  Minus,
  RotateCcw,
  Replace,
} from 'lucide-react'
import { AI_API_TIMEOUTS, aiAssistantApi, candidatesApi, ChatSession, ChatMessage, ClientMessageMetadata, AIStatus, offersApi, teamsApi, Team, CursorPaginatedMessages, ContextLimitState, hiringProcessApi, HiringProcessTemplate } from 'lib/api'
import { CustomSelect } from 'components/ui/CustomSelect'
import { LanguageField, type LanguageItem } from 'components/ui/LanguageField'
import { SingleFieldWithLabels } from 'components/ui/SingleFieldWithLabels'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { useAiChrome } from 'components/layout/Layout'
import { startNonCriticalRefreshes, withGenerationLoading } from '../generationFlow'
import { getOfferEditQuickActions, getPrimaryOfferQuickActions, OfferQuickAction } from '../offerQuickActions'
import { getLocalizedOfferStatus } from '../offerStatus'
import { buildOfferChangeSummary, buildQualityAnalysisFromOfferChanges, buildUpdatedOfferPreview, needsOfferDetailForRichPreview, usesRichOfferPreview, OfferChange } from '../offerEditPreview'
import { isLatestSessionRequest } from '../sessionRequestGuard'
import { DeveloperContextInspector } from '../components/DeveloperContextInspector'
import { CandidateOfferMatchingCard } from '../components/CandidateOfferMatchingCard'
import { EmailComposerCard } from '../components/EmailComposerCard'

declare const process: {
  env: {
    REACT_APP_API_URL?: string
  }
}

interface Message {
  id: string
  type: 'user' | 'ai'
  content: string
  timestamp: Date
  candidateId?: number | null
  candidateIds?: number[]
  offerId?: number | null
  metadata?: any
}

interface OfferSummary {
  id: number
  job_title: string
  department?: string
  contract_type?: string
  experience_level?: string
  status: string
  allowed_actions?: string[]
}

interface Candidate {
  id: number
  name: string
  email?: string
  role?: string
  profile_picture?: string
}

// Helper function to get proper image URL
const getImageUrl = (profilePicture?: string, candidateName?: string): string => {
  if (!profilePicture) {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(candidateName || 'User')}&background=4f46e5&color=fff&size=128`
  }

  const configuredApiUrl = process.env.REACT_APP_API_URL?.trim()
  const mediaBaseUrl = configuredApiUrl
    ? configuredApiUrl.replace(/\/+$/, '').replace(/\/api\/?$/, '')
    : `${window.location.protocol}//${window.location.hostname}:8001`
  const normalizedUrl = profilePicture.trim()

  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    try {
      const imageUrl = new URL(normalizedUrl)
      if (/^(localhost|127\.0\.0\.1|backend)$/i.test(imageUrl.hostname)) {
        return `${mediaBaseUrl}${imageUrl.pathname}${imageUrl.search}${imageUrl.hash}`
      }
    } catch {
      return normalizedUrl
    }
    return normalizedUrl
  }

  return `${mediaBaseUrl}${normalizedUrl.startsWith('/') ? '' : '/'}${normalizedUrl}`
}

const MESSAGE_PAGE_SIZE = 30
const MULTI_OFFER_SELECTION_ENABLED = true
const MAX_SELECTED_OFFERS = MULTI_OFFER_SELECTION_ENABLED ? 10 : 1
const MAX_SELECTED_CANDIDATES = 5
const MAX_SELECTED_CANDIDATES_WITH_OFFER = 2

const extractResults = <T,>(data: any): T[] => {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.results)) return data.results
  return []
}

const mergePaginatedMessages = (olderMessages: Message[], existingMessages: Message[]): Message[] => {
  const existingIds = new Set(existingMessages.map(m => m.id))
  const uniqueOlder = olderMessages.filter(m => !existingIds.has(m.id))
  return [...uniqueOlder, ...existingMessages]
}

const normalizeCandidateIds = (ids: unknown): number[] => {
  if (!Array.isArray(ids)) return []
  return ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
}

const collectCandidateIdsFromMessages = (messages: Message[]): number[] => {
  const ids = new Set<number>()
  messages.forEach((message) => {
    if (message.type !== 'user') return
    normalizeCandidateIds(message.candidateIds).forEach((id) => ids.add(id))
    if (message.candidateId) ids.add(Number(message.candidateId))
    normalizeCandidateIds(message.metadata?.candidate_ids).forEach((id) => ids.add(id))
    if (message.metadata?.candidate_id) ids.add(Number(message.metadata.candidate_id))
  })
  return Array.from(ids)
}

const mapChatMessages = (messages: ChatMessage[]): Message[] => {
  const mappedMessages = messages.map((message) => {
    const candidateIds = normalizeCandidateIds(message.metadata?.candidate_ids)
    const candidateId = message.metadata?.candidate_id
      ? Number(message.metadata.candidate_id)
      : null
    const resolvedIds = candidateIds.length > 0
      ? candidateIds
      : (candidateId ? [candidateId] : [])

    return {
      id: message.id.toString(),
      type: message.message_type,
      content: message.content,
      timestamp: new Date(message.timestamp),
      candidateId: resolvedIds[0] ?? null,
      candidateIds: resolvedIds,
      offerId: message.metadata?.offer_id ?? null,
      metadata: { ...(message.metadata || {}) }
    }
  })

  // Backfill generation metadata for conversations created before response
  // versioning was introduced. A hidden regeneration request always points to
  // the reply it replaces, and the next AI message is its new version.
  const messagesById = new Map(mappedMessages.map((message) => [message.id, message]))
  let pendingGeneration: { groupId: string; version: number } | null = null

  mappedMessages.forEach((message) => {
    if (message.type === 'user' && message.metadata?.hidden && message.metadata?.regeneration_for) {
      const target = messagesById.get(String(message.metadata.regeneration_for))
      if (!target) return

      const groupId = String(target.metadata?.generation_group_id || target.id)
      const targetVersion = Number(target.metadata?.generation_version || 1)
      target.metadata = {
        ...(target.metadata || {}),
        generation_group_id: groupId,
        generation_version: targetVersion,
        superseded: false,
      }
      pendingGeneration = { groupId, version: targetVersion + 1 }
      return
    }

    if (message.type === 'ai' && pendingGeneration) {
      message.metadata = {
        ...(message.metadata || {}),
        generation_group_id: String(message.metadata?.generation_group_id || pendingGeneration.groupId),
        generation_version: Number(message.metadata?.generation_version || pendingGeneration.version),
      }
      pendingGeneration = null
    }
  })

  return mappedMessages
}

const getGenerationGroupId = (message: Message): string =>
  String(message.metadata?.generation_group_id || message.id)

const getGenerationVersion = (message: Message): number =>
  Number(message.metadata?.generation_version || 1)

const buildGenerationGroups = (messages: Message[]): Record<string, Message[]> =>
  messages.reduce<Record<string, Message[]>>((groups, message) => {
    if (message.type !== 'ai' || message.metadata?.hidden) return groups
    const groupId = getGenerationGroupId(message)
    groups[groupId] = [...(groups[groupId] || []), message].sort(
      (left, right) => getGenerationVersion(left) - getGenerationVersion(right)
    )
    return groups
  }, {})

const isSelectableUserMessage = (message: Message): boolean =>
  message.type === 'user'
  && !message.metadata?.hidden
  && !message.metadata?.technical
  && !message.metadata?.context_handoff

const getUserMessagePreview = (message: Message, fallback = ''): string => {
  const friendly = message.metadata?.friendly_message
  if (typeof friendly === 'string' && friendly.trim()) return friendly.trim()
  const formTitle = message.metadata?.ui_form_display?.title
  if (typeof formTitle === 'string' && formTitle.trim()) return formTitle.trim()
  const content = (message.content || '').replace(/\s+/g, ' ').trim()
  return content || fallback
}

const collectTurnAiMessageIds = (messages: Message[], userMessageId: string | null): Set<string> => {
  const ids = new Set<string>()
  if (!userMessageId) return ids
  const userIndex = messages.findIndex((message) => message.id === userMessageId)
  if (userIndex < 0) return ids
  for (let index = userIndex + 1; index < messages.length; index += 1) {
    const message = messages[index]
    if (isSelectableUserMessage(message)) break
    if (message.type === 'ai' && !message.metadata?.hidden && !message.metadata?.context_handoff) {
      ids.add(message.id)
    }
  }
  return ids
}

const proposedFieldsRegex = /(?:python)?\s*proposed_fields\s*=\s*\[([\s\S]*?)\]/i

const extractProposedFields = (content: string): { fields: string[]; cleaned: string } => {
  // Skip regex on very large content (>100KB)
  if (content.length > CONTENT_SIZE_LIMIT) {
    return { fields: [], cleaned: content }
  }
  const match = content.match(proposedFieldsRegex)
  if (!match) {
    return { fields: [], cleaned: content }
  }

  const listBody = match[1] || ''
  const fields: string[] = []
  const fieldRegex = /['"]([^'"]+)['"]/g
  let fieldMatch = fieldRegex.exec(listBody)
  while (fieldMatch) {
    fields.push(fieldMatch[1])
    fieldMatch = fieldRegex.exec(listBody)
  }

  const cleaned = content.replace(match[0], '').replace(/\n{3,}/g, '\n\n').trim()
  return { fields, cleaned }
}

const formatFieldsAsPythonList = (fields: string[]): string =>
  `[${fields.map((field) => `'${field}'`).join(', ')}]`

// Regex patterns to detect offer data from AI responses
// Pattern 1: offer_data = {...} (preferred format) - greedy match to capture full JSON
const offerDataRegex = /offer_data\s*=\s*(\{[\s\S]*\})/im
// Pattern 2: JSON inside markdown code fence (fallback for models that ignore formatting instructions)
const offerDataCodeFenceRegex = /```(?:json)?\s*\n(\{[\s\S]*?"job_title"[\s\S]*?\})\s*\n```/im

const tryParseOfferJson = (jsonStr: string): Record<string, any> | null => {
  // Step 1: Try parsing as-is (AI typically outputs valid double-quoted JSON)
  try {
    const parsed = JSON.parse(jsonStr)
    if (typeof parsed === 'object' && parsed !== null && parsed.job_title) {
      return parsed
    }
  } catch {
    // Not valid JSON as-is, try cleanup
  }

  // Step 2: Try with minimal cleanup (trailing commas, Python booleans)
  try {
    const cleaned = jsonStr
      .replace(/,\s*([}\]])/g, '$1')  // Remove trailing commas
      .replace(/:\s*True\b/g, ': true')
      .replace(/:\s*False\b/g, ': false')
      .replace(/:\s*None\b/g, ': null')
    const parsed = JSON.parse(cleaned)
    if (typeof parsed === 'object' && parsed !== null && parsed.job_title) {
      return parsed
    }
  } catch {
    // Still not valid
  }

  // Step 3: Last resort - Python dict style with single-quoted keys/values
  // Only replace single quotes that are JSON delimiters, not those inside values
  try {
    // Replace single-quoted strings: 'value' -> "value"
    const cleaned = jsonStr
      .replace(/'/g, '"')
      .replace(/,\s*([}\]])/g, '$1')
      .replace(/:\s*True\b/g, ': true')
      .replace(/:\s*False\b/g, ': false')
      .replace(/:\s*None\b/g, ': null')
    const parsed = JSON.parse(cleaned)
    if (typeof parsed === 'object' && parsed !== null && parsed.job_title) {
      return parsed
    }
  } catch {
    // Could not parse at all
  }

  return null
}

const CONTENT_SIZE_LIMIT = 100_000

const extractOfferData = (content: string): { offerData: Record<string, any> | null; cleaned: string } => {
  // Skip regex on very large content (>100KB) — offer_data patterns are never that large
  if (content.length > CONTENT_SIZE_LIMIT) {
    return { offerData: null, cleaned: content }
  }
  // Try Pattern 1: offer_data = {...}
  const match1 = content.match(offerDataRegex)
  if (match1) {
    const parsed = tryParseOfferJson(match1[1])
    if (parsed) {
      const cleaned = content.replace(match1[0], '').replace(/\n{3,}/g, '\n\n').trim()
      return { offerData: parsed, cleaned }
    }
  }

  // Try Pattern 2: JSON inside code fence (```json ... ```)
  const match2 = content.match(offerDataCodeFenceRegex)
  if (match2) {
    const parsed = tryParseOfferJson(match2[1])
    if (parsed) {
      const cleaned = content.replace(match2[0], '').replace(/\n{3,}/g, '\n\n').trim()
      return { offerData: parsed, cleaned }
    }
  }

  return { offerData: null, cleaned: content }
}

const resolveOfferDataForMessage = (message: Message): { offerData: Record<string, any> | null; cleaned: string } => {
  const fromContent = extractOfferData(message.content)
  if (fromContent.offerData) {
    // The interactive card owns the résumé; keep only non-technical prose (e.g. comparison).
    return fromContent
  }

  const metaOffer = message.metadata?.offer_data
  if (metaOffer && typeof metaOffer === 'object' && metaOffer.job_title) {
    // Metadata already has the structured offer; do not re-show the raw LLM prose/JSON.
    const intent = message.metadata?.intent
    if (intent === 'compare_and_create_offer') {
      return { offerData: metaOffer, cleaned: extractOfferData(message.content).cleaned }
    }
    return { offerData: metaOffer, cleaned: '' }
  }

  return { offerData: null, cleaned: message.content }
}

const HIDDEN_OFFER_SUMMARY_KEYS = new Set(['status', 'comparison_fields', 'comparison_analysis', 'source_url'])

const hasSubstantiveOfferContent = (offerData: Record<string, any>): boolean =>
  ['description_text', 'requirements', 'main_missions', 'soft_skills'].some((field) => {
    const value = offerData[field]
    if (Array.isArray(value)) return value.length > 0
    if (value && typeof value === 'object') return Object.keys(value).length > 0
    return typeof value === 'string' && value.trim().length > 0
  })

const extractRequestedJobTitle = (content: string): string => {
  const plain = String(content || '').split(/\n\n(?:Candidate|OFFRE EN COURS)/i, 1)[0].trim()
  const patterns = [
    /\bposte\s+(?:de|d[\u2019'])\s*(.+?)(?:\s+avec\b|[.!?]*$)/i,
    /\boffre\s+pour\s+(?:un|une)?\s*(.+?)(?:\s+avec\b|[.!?]*$)/i,
    /\b(?:position|role)\s+(?:of|as)\s+(.+?)(?:\s+with\b|[.!?]*$)/i,
    /\b(?:job offer|job description)\s+for\s+(?:an?\s+)?(.+?)(?:\s+with\b|[.!?]*$)/i,
  ]
  for (const pattern of patterns) {
    const match = plain.match(pattern)
    if (match?.[1]) return match[1].trim()
  }
  return ''
}

const extractRequestedExperienceLevel = (content: string): string => {
  const match = String(content || '').match(/\b(\d{1,2})\s*(?:ans?|années?|years?)\s+(?:d[\u2019']?expérience|of experience)/i)
  return match?.[1] || ''
}

const ChatMarkdown = ({ content, className = '' }: { content: string; className?: string }) => (
  <div className={`w-full min-w-0 max-w-full overflow-x-hidden break-words [overflow-wrap:anywhere] ${className}`}>
    <MarkdownRenderer content={content} />
  </div>
)

type OfferSuccessKind = 'created' | 'updated' | 'regenerated'

const OfferSuccessCard = ({
  kind,
  offerId,
  offerTitle,
  onOpen,
}: {
  kind: OfferSuccessKind
  offerId: number
  offerTitle?: string
  onOpen: (offerId: number) => void
}) => {
  const { t } = useTranslation()
  const title = kind === 'created'
    ? t('aiAssistant.offerCreatedSuccess', 'Offre créée avec succès')
    : kind === 'regenerated'
      ? t('aiAssistant.offerRegeneratedSuccess', 'Nouvelle offre créée à partir de la régénération')
      : t('aiAssistant.offerUpdatedSuccess', 'Offre mise à jour avec succès')
  const buttonLabel = kind === 'created'
    ? t('aiAssistant.openCreatedOffer', 'Ouvrir l’offre créée')
    : kind === 'regenerated'
      ? t('aiAssistant.openRegeneratedOffer', 'Ouvrir la nouvelle offre')
      : t('aiAssistant.openUpdatedOffer', 'Ouvrir l’offre mise à jour')

  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 shadow-sm dark:border-emerald-900/70 dark:from-emerald-950/40 dark:to-teal-950/30">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
          <CheckCircle className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-emerald-900 dark:text-emerald-100">{title}</p>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/80">
            {t('aiAssistant.offerLabel', 'Offre')}
          </p>
          <p className="mt-0.5 break-words text-sm font-medium text-foreground">
            {offerTitle || `#${offerId}`}
          </p>
        </div>
      </div>
      <div className="mt-4 border-t border-emerald-200/80 pt-3 dark:border-emerald-900/60">
        <Button type="button" size="sm" onClick={() => onOpen(offerId)}>
          <Briefcase className="mr-2 h-4 w-4" />
          {buttonLabel}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

const getAiResponseDurationMs = (messages: Message[], messageIndex: number): number | null => {
  const message = messages[messageIndex]
  if (!message || message.type !== 'ai' || message.metadata?.system) return null

  const recordedDuration = Number(message.metadata?.generation_duration_ms)
  if (Number.isFinite(recordedDuration) && recordedDuration >= 0) return recordedDuration

  for (let index = messageIndex - 1; index >= 0; index -= 1) {
    const previous = messages[index]
    if (previous.type !== 'user') continue
    const duration = message.timestamp.getTime() - previous.timestamp.getTime()
    return Number.isFinite(duration) && duration >= 0 ? duration : null
  }
  return null
}

const formatResponseDuration = (durationMs: number): string => {
  if (durationMs < 1000) return `${Math.max(1, Math.round(durationMs))} ms`
  const seconds = durationMs / 1000
  if (seconds < 10) return `${seconds.toFixed(1)} s`
  if (seconds < 60) return `${Math.round(seconds)} s`
  const wholeMinutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  return `${wholeMinutes} min ${remainingSeconds.toString().padStart(2, '0')} s`
}

const formatApiValidationDetails = (details: any): string => {
  if (!details) return ''
  if (typeof details === 'string') return details
  if (Array.isArray(details)) return details.map(String).join(', ')
  if (typeof details !== 'object') return String(details)
  return Object.entries(details)
    .map(([field, value]) => `${OFFER_FIELD_LABELS[field] || field}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
    .join('\n')
}

const OFFER_SUMMARY_FIELD_ORDER = [
  'job_title',
  'department',
  'contract_type',
  'experience_level',
  'education_level',
  'description_text',
  'requirements',
  'main_missions',
  'soft_skills',
  'benefits',
  'languages',
  'certifications',
  'remote_allowed',
]

const OFFER_SUMMARY_MULTI_LINE_FIELDS = new Set([
  'description_text',
  'requirements',
  'main_missions',
  'soft_skills',
  'benefits',
  'certifications',
])

const OFFER_SUMMARY_LIST_FIELDS = new Set([
  'requirements',
  'main_missions',
  'soft_skills',
  'benefits',
  'certifications',
])

const OFFER_SUMMARY_PILL_FIELDS = new Set(['requirements', 'soft_skills', 'certifications', 'benefits', 'languages'])
const OFFER_SUMMARY_BULLET_FIELDS = new Set(['main_missions'])
const OFFER_SUMMARY_PILL_CLASS = 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100'

const OFFER_SUMMARY_BOOLEAN_FIELDS = new Set(['remote_allowed'])
const OFFER_SUMMARY_NUMBER_FIELDS = new Set(['experience_level'])
const OFFER_SUMMARY_LANGUAGE_FIELD = 'languages'
const OFFER_SUMMARY_EDUCATION_OPTIONS = [
  { value: 'Bac', label: 'Bac' },
  { value: 'Bac+2', label: 'Bac +2' },
  { value: 'Bac+3', label: 'Bac +3' },
  { value: 'Bac+5', label: 'Bac +5' },
  { value: 'Bac+8', label: 'Bac +8' },
]

const readHiringTemplateId = (value: unknown): number | null => {
  if (Array.isArray(value)) return null
  if (value && typeof value === 'object') {
    const record = value as { id?: unknown; hiring_template?: unknown; hiring_template_id?: unknown }
    if ('hiring_template' in record || 'hiring_template_id' in record) {
      return readHiringTemplateId(record.hiring_template ?? record.hiring_template_id)
    }
    if ('id' in record) return readHiringTemplateId(record.id)
    return null
  }
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}

const cloneOfferData = (offerData: Record<string, any>): Record<string, any> => {
  try {
    return JSON.parse(JSON.stringify(offerData || {}))
  } catch {
    return { ...(offerData || {}) }
  }
}

const stringifyOfferArrayItem = (item: any): string => {
  if (typeof item === 'string') return item
  if (!item || typeof item !== 'object') return String(item ?? '')

  if (item.language || item.level) {
    return [item.language, item.level ? `: ${item.level}` : ''].join('').trim()
  }

  const directLabel = String(item.label || item.name || item.value || '').trim()
  if (directLabel) return directLabel

  return Object.values(item)
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .filter((value) => ['string', 'number'].includes(typeof value) && String(value).trim())
    .map((value) => String(value).trim())
    .join(', ')
}

// AI-generated offers sometimes store a list field as an object grouping its
// entries under sub-keys (soft_skills_mentionnées, …) instead of a flat array.
// Regeneration must show one merged list (mentioned ∪ AI-recommended), never two fields.
const dedupeOfferItems = (items: any[]): any[] => {
  const seen = new Set<string>()
  const result: any[] = []
  for (const item of items) {
    const label = stringifyOfferArrayItem(item).trim()
    if (!label) continue
    const key = label.toLocaleLowerCase('fr')
    if (seen.has(key)) continue
    seen.add(key)
    result.push(typeof item === 'string' ? item : label)
  }
  return result
}

const toOfferItemList = (value: any): any[] => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
      || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(trimmed)
        if (parsed && typeof parsed === 'object') return toOfferItemList(parsed)
      } catch {
        // Keep the original text when it is not stored JSON.
      }
    }
  }
  if (Array.isArray(value)) return dedupeOfferItems(value)
  if (value && typeof value === 'object') {
    const mentioned: any[] = []
    const unmentioned: any[] = []
    const other: any[] = []
    Object.entries(value).forEach(([key, entry]) => {
      const items = Array.isArray(entry) ? entry : entry == null || entry === '' ? [] : [entry]
      const keyL = String(key).toLowerCase()
      if (keyL.includes('non_mention') || keyL.includes('non-mention')) {
        unmentioned.push(...items)
      } else if (keyL.includes('mention')) {
        mentioned.push(...items)
      } else {
        other.push(...items)
      }
    })
    return dedupeOfferItems([...mentioned, ...unmentioned, ...other])
  }
  return value === null || value === undefined || value === '' ? [] : [value]
}

const offerSummaryListKind = (field: string): 'pill' | 'bullet' | null => {
  if (OFFER_SUMMARY_BULLET_FIELDS.has(field)) return 'bullet'
  if (OFFER_SUMMARY_PILL_FIELDS.has(field)) return 'pill'
  return null
}

const offerSummaryItemTexts = (value: any): string[] => (
  toOfferItemList(value).map((item) => stringifyOfferArrayItem(item)).filter(Boolean)
)

const OfferSummaryValueList = ({
  items,
  kind,
  field,
  tone = 'default',
}: {
  items: string[]
  kind: 'pill' | 'bullet'
  field?: string
  tone?: 'default' | 'added' | 'replaced' | 'removed'
}) => {
  if (!items.length) return null
  const bulletTone = tone === 'added'
    ? 'text-emerald-600 dark:text-emerald-300'
    : tone === 'replaced'
      ? 'text-amber-700 dark:text-amber-200'
      : tone === 'removed'
        ? 'text-rose-700 line-through dark:text-rose-300'
        : 'text-foreground'
  const bulletMark = tone === 'added'
    ? 'text-emerald-500'
    : tone === 'replaced'
      ? 'text-amber-500'
      : tone === 'removed'
        ? 'text-rose-500'
        : 'text-primary'
  if (kind === 'bullet') {
    return (
      <ul className="space-y-1">
        {items.map((item, index) => (
          <li key={`${tone}-${item}-${index}`} className={`flex items-start gap-2 text-sm leading-5 ${bulletTone}`}>
            <span className={`mt-0.5 ${bulletMark}`}>•</span>
            <span className="min-w-0 break-words [overflow-wrap:anywhere]">{item}</span>
          </li>
        ))}
      </ul>
    )
  }
  const pillClass = tone === 'added'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
    : tone === 'replaced'
      ? 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
      : tone === 'removed'
        ? 'border-rose-200 bg-rose-50 text-rose-800 line-through dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200'
        : OFFER_SUMMARY_PILL_CLASS
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, index) => {
        const separator = item.indexOf(':')
        const language = field === 'languages' && separator > 0 ? item.slice(0, separator).trim() : ''
        const level = language ? item.slice(separator + 1).trim() : ''
        return (
          <span
            key={`${tone}-${item}-${index}`}
            className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${pillClass}`}
          >
            <span className="break-words [overflow-wrap:anywhere]">{language || item}</span>
            {level ? (
              <span className="rounded-full bg-white/80 px-1.5 py-px text-[10px] font-semibold leading-4 dark:bg-black/20">
                {level}
              </span>
            ) : null}
          </span>
        )
      })}
    </div>
  )
}

const formatOfferSummaryDisplayValue = (value: any): string => {
  if (typeof value === 'boolean') return value ? '✅ Oui' : '❌ Non'
  if (Array.isArray(value)) {
    return toOfferItemList(value).map((item) => stringifyOfferArrayItem(item)).filter(Boolean).join(', ')
  }
  if (value && typeof value === 'object') {
    const looksLikeBuckets = Object.values(value).some((entry) => Array.isArray(entry))
    if (looksLikeBuckets) {
      return toOfferItemList(value).map((item) => stringifyOfferArrayItem(item)).filter(Boolean).join(', ')
    }
    return stringifyOfferArrayItem(value)
  }
  return String(value || '')
}

type OfferFieldChangeKind = 'unchanged' | 'added' | 'changed' | 'removed'

const isOfferSummaryValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value)) return toOfferItemList(value).length === 0
  if (typeof value === 'object') {
    const looksLikeBuckets = Object.values(value).some((entry) => Array.isArray(entry))
    if (looksLikeBuckets) return toOfferItemList(value).length === 0
    return !stringifyOfferArrayItem(value).trim()
  }
  return !String(value).trim()
}

const offerSummaryValuesEqual = (field: string, left: any, right: any): boolean => {
  if (left === right) return true
  if (isOfferSummaryValueEmpty(left) && isOfferSummaryValueEmpty(right)) return true
  return formatOfferValueForEditing(field, left).trim().toLowerCase()
    === formatOfferValueForEditing(field, right).trim().toLowerCase()
}

const classifyOfferFieldChange = (field: string, baseline: any, current: any): OfferFieldChangeKind => {
  const baselineEmpty = isOfferSummaryValueEmpty(baseline)
  const currentEmpty = isOfferSummaryValueEmpty(current)
  if (baselineEmpty && currentEmpty) return 'unchanged'
  if (baselineEmpty && !currentEmpty) return 'added'
  if (!baselineEmpty && currentEmpty) return 'removed'
  if (offerSummaryValuesEqual(field, baseline, current)) return 'unchanged'
  if (OFFER_SUMMARY_LIST_FIELDS.has(field) || field === OFFER_SUMMARY_LANGUAGE_FIELD) {
    const diff = diffOfferListItems(baseline, current)
    if (diff.added.length > 0 && diff.removed.length === 0) return 'added'
    if (diff.removed.length > 0 && diff.added.length === 0) return 'removed'
  }
  return 'changed'
}

const diffOfferListItems = (baseline: any, current: any): {
  kept: string[]
  added: string[]
  removed: string[]
} => {
  const baselineItems = toOfferItemList(baseline).map((item) => stringifyOfferArrayItem(item)).filter(Boolean)
  const currentItems = toOfferItemList(current).map((item) => stringifyOfferArrayItem(item)).filter(Boolean)
  const baselineKeys = new Set(baselineItems.map((item) => item.toLowerCase()))
  const currentKeys = new Set(currentItems.map((item) => item.toLowerCase()))
  return {
    kept: currentItems.filter((item) => baselineKeys.has(item.toLowerCase())),
    added: currentItems.filter((item) => !baselineKeys.has(item.toLowerCase())),
    removed: baselineItems.filter((item) => !currentKeys.has(item.toLowerCase())),
  }
}

const formatOfferValueForEditing = (field: string, value: any): string => {
  if (OFFER_SUMMARY_BOOLEAN_FIELDS.has(field)) {
    if (value === true) return 'true'
    if (value === false) return 'false'
    return ''
  }

  if (field === OFFER_SUMMARY_LANGUAGE_FIELD) {
    return toOfferItemList(value)
      .map((item) => {
        if (!item || typeof item !== 'object') return stringifyOfferArrayItem(item)
        const language = String(item.language || item.name || '').trim()
        const level = String(item.level || '').trim()
        return language ? `${language}${level ? `: ${level}` : ''}` : ''
      })
      .filter(Boolean)
      .join('\n')
  }

  if (Array.isArray(value) || (OFFER_SUMMARY_LIST_FIELDS.has(field) && value && typeof value === 'object')) {
    return toOfferItemList(value).map((item) => stringifyOfferArrayItem(item)).filter(Boolean).join('\n')
  }

  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return stringifyOfferArrayItem(value)
  return String(value)
}

const parseOfferValueFromEditing = (field: string, rawValue: string): any => {
  const trimmed = String(rawValue || '').trim()

  if (OFFER_SUMMARY_BOOLEAN_FIELDS.has(field)) {
    if (!trimmed) return undefined
    if (['true', '1', 'yes', 'oui'].includes(trimmed.toLowerCase())) return true
    if (['false', '0', 'no', 'non'].includes(trimmed.toLowerCase())) return false
    return undefined
  }

  if (field === OFFER_SUMMARY_LANGUAGE_FIELD) {
    if (!trimmed) return []
    return trimmed
      .split(/\r?\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separatorIndex = line.indexOf(':')
        if (separatorIndex === -1) {
          return { language: line, level: '' }
        }
        const language = line.slice(0, separatorIndex).trim()
        const level = line.slice(separatorIndex + 1).trim()
        return language ? { language, level } : null
      })
      .filter(Boolean)
  }

  if (OFFER_SUMMARY_LIST_FIELDS.has(field)) {
    if (!trimmed) return []
    return trimmed
      .split(/\r?\n+/)
      .map((line) => line.replace(/^[-*•]\s*/, '').trim())
      .filter(Boolean)
  }

  if (OFFER_SUMMARY_NUMBER_FIELDS.has(field)) {
    return trimmed
  }

  return trimmed
}

const buildOfferDraft = (
  offerData: Record<string, any>,
  options?: { includeEmptyFields?: boolean },
): Record<string, string> => {
  const draft: Record<string, string> = {}
  const includeEmptyFields = Boolean(options?.includeEmptyFields)
  const fields = includeEmptyFields
    ? OFFER_SUMMARY_FIELD_ORDER
    : Object.keys(offerData || {})

  fields.forEach((field) => {
    if (HIDDEN_OFFER_SUMMARY_KEYS.has(field)) return
    if (!includeEmptyFields && offerData?.[field] === undefined) return
    draft[field] = formatOfferValueForEditing(field, offerData?.[field])
  })

  return draft
}

const serializeOfferDraft = (offerData: Record<string, any>, draft: Record<string, string>): Record<string, any> => {
  const nextOfferData = cloneOfferData(offerData)

  Object.entries(draft).forEach(([field, rawValue]) => {
    if (HIDDEN_OFFER_SUMMARY_KEYS.has(field)) return
    const parsedValue = parseOfferValueFromEditing(field, rawValue)
    if (parsedValue === undefined) {
      delete nextOfferData[field]
      return
    }
    nextOfferData[field] = parsedValue
  })

  return nextOfferData
}

const composeResumeDraftPatch = (
  messageId: string,
  resumeChatPatch: { messageId: string; updates: Record<string, any>; token: number } | null,
  regenerationDraftPatch: { messageId: string; field: string; value: any; token: number } | null,
) => {
  const resume = resumeChatPatch?.messageId === messageId ? resumeChatPatch : null
  const regen = regenerationDraftPatch?.messageId === messageId ? regenerationDraftPatch : null
  if (!resume && !regen) return null
  const updates: Record<string, any> = {
    ...(resume?.updates || {}),
    ...(regen?.field ? { [regen.field]: regen.value } : {}),
  }
  if (!Object.keys(updates).length) return null
  return {
    updates,
    token: Math.max(resume?.token || 0, regen?.token || 0),
  }
}

// Editing a saved offer does not go through the creation-proposal endpoints:
// changes become a pending offer_update that the recruiter then confirms.
interface OfferSummaryEditContext {
  offerId: number
  onSaveEdits: (
    offerId: number,
    offerData: Record<string, any>,
  ) => Promise<void>
  onRefineField: (
    offerId: number,
    offerData: Record<string, any>,
    field: string,
    instruction: string,
  ) => Promise<any>
}

interface OfferSummaryCardProps {
  message: Message
  offerData: Record<string, any>
  offerCleaned: string
  language: string
  isLatestMessage: boolean
  isOfferCompleteEnough: boolean
  isOfferCancelled: boolean
  isOfferConfirmed: boolean
  confirmedInfo?: { offerId: number; status: string }
  isCreating: boolean
  isCancelling: boolean
  presentationOnly?: boolean
  /** Open directly in the Résumé edit mode (creation, regeneration, modification). */
  startInEditMode?: boolean
  /** Primary action label: create, regenerate, or apply edits. */
  resumeAction?: 'create' | 'regenerate' | 'modify'
  /** Keep empty fields visible so recommendations can fill them. */
  includeEmptyFields?: boolean
  /** Original offer snapshot used to highlight added / changed / removed fields. */
  baselineOfferData?: Record<string, any> | null
  /** Quality proposals so every suggested field gets « Appliquer la proposition ». */
  qualityAnalysis?: RegenerationQualityAnalysis | null
  /** Apply a recommendation or chat « Modifier avec l’IA » result into the live edit draft. */
  draftPatch?: { field?: string; value?: any; updates?: Record<string, any>; token: number } | null
  /** Open « Modifier avec l’IA » on a résumé field with a prefilled instruction. */
  aiAssistPatch?: { field: string; instruction: string; token: number } | null
  /** Notify the parent of the live résumé draft (regeneration Apply/Reset buttons). */
  onDraftChange?: (offerData: Record<string, any>) => void
  /** Hiring template already stored on the source offer, when the résumé preview omits it. */
  initialHiringTemplateId?: number | null
  editContext?: OfferSummaryEditContext
  onConfirm: (messageId: string, offerData: Record<string, any>) => Promise<void>
  onRefineField: (messageId: string, offerData: Record<string, any>, field: string, instruction: string) => Promise<any>
  onCancel: (messageId: string) => Promise<void>
  onOpenOffer: (offerId: number) => void
}

const OfferSummaryCard = ({
  message,
  offerData,
  offerCleaned,
  language,
  isLatestMessage,
  isOfferCompleteEnough,
  isOfferCancelled,
  isOfferConfirmed,
  confirmedInfo,
  isCreating,
  isCancelling,
  presentationOnly = false,
  startInEditMode = false,
  resumeAction,
  includeEmptyFields = false,
  baselineOfferData = null,
  qualityAnalysis = null,
  draftPatch = null,
  aiAssistPatch = null,
  onDraftChange,
  initialHiringTemplateId = null,
  editContext,
  onConfirm,
  onRefineField,
  onCancel,
  onOpenOffer,
}: OfferSummaryCardProps) => {
  const { t } = useTranslation()
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  const [isEditing, setIsEditing] = useState(Boolean(startInEditMode))
  const [activeEditingField, setActiveEditingField] = useState<string | null>(null)
  const [activeAiField, setActiveAiField] = useState<string | null>(null)
  const [aiInstruction, setAiInstruction] = useState('')
  const [generatingField, setGeneratingField] = useState<string | null>(null)
  const [aiFieldError, setAiFieldError] = useState('')
  const qualityProposals = useMemo(
    () => (
      qualityAnalysis && baselineOfferData
        ? buildQualityProposalValues(qualityAnalysis, baselineOfferData)
        : {}
    ),
    [qualityAnalysis, baselineOfferData],
  )
  const resumeDraftSource = useMemo(() => {
    if (!(baselineOfferData && startInEditMode)) return offerData
    const base = offerData && Object.keys(offerData).length ? offerData : baselineOfferData
    return applyQualityProposalsToOffer(base, qualityProposals)
  }, [baselineOfferData, startInEditMode, offerData, qualityProposals])
  const [draft, setDraft] = useState<Record<string, string>>(() => (
    startInEditMode ? buildOfferDraft(resumeDraftSource, { includeEmptyFields }) : {}
  ))
  const [rememberedUserEdits, setRememberedUserEdits] = useState<Record<string, string>>({})
  const lastDraftJsonRef = useRef('')
  const appliedDraftPatchTokenRef = useRef<number | null>(null)
  const appliedAiAssistTokenRef = useRef<number | null>(null)
  const resumeDraftSourceRef = useRef(resumeDraftSource)
  const draftPatchRef = useRef(draftPatch)
  const aiAssistPatchRef = useRef(aiAssistPatch)
  const baselineOfferDataRef = useRef(baselineOfferData)
  const qualityProposalsRef = useRef(qualityProposals)
  resumeDraftSourceRef.current = resumeDraftSource
  draftPatchRef.current = draftPatch
  aiAssistPatchRef.current = aiAssistPatch
  baselineOfferDataRef.current = baselineOfferData
  qualityProposalsRef.current = qualityProposals

  const rememberFieldValue = (field: string, value: string, fromUserInput = false) => {
    const baseline = baselineOfferDataRef.current
    if (!baseline) return
    const baselineText = formatOfferValueForEditing(field, baseline[field])
    const proposal = qualityProposalsRef.current[field]
    const proposalText = proposal ? formatOfferValueForEditing(field, proposal.value) : null
    const trimmed = value.trim()
    const matchesBaseline = trimmed === baselineText.trim()
    const matchesProposal = proposalText != null && trimmed === proposalText.trim()
    if (matchesBaseline || matchesProposal) {
      if (!fromUserInput) return
      setRememberedUserEdits((previous) => {
        if (!(field in previous)) return previous
        const next = { ...previous }
        delete next[field]
        return next
      })
      return
    }
    setRememberedUserEdits((previous) => (
      previous[field] === value ? previous : { ...previous, [field]: value }
    ))
  }
  const rememberFieldValueRef = useRef(rememberFieldValue)
  rememberFieldValueRef.current = rememberFieldValue
  const [isSaving, setIsSaving] = useState(false)
  const [hiringTemplates, setHiringTemplates] = useState<HiringProcessTemplate[]>([])
  const hiringTemplateTouchedRef = useRef(false)
  const [hiringTemplateId, setHiringTemplateId] = useState<number | null>(() => (
    readHiringTemplateId(offerData)
    || readHiringTemplateId(baselineOfferData)
    || readHiringTemplateId(message.metadata?.offer_form)
    || readHiringTemplateId(message.metadata?.regeneration_state?.original_offer_data)
    || readHiringTemplateId(message.metadata?.selected_hiring_template_id)
    || initialHiringTemplateId
  ))

  useEffect(() => {
    let cancelled = false
    hiringProcessApi.getTemplates()
      .then((response) => {
        if (cancelled) return
        const payload = response.data as HiringProcessTemplate[] | { results?: HiringProcessTemplate[] }
        const items = Array.isArray(payload) ? payload : (payload?.results || [])
        const active = items.filter((template) => template.is_active)
        setHiringTemplates(active)
        setHiringTemplateId((current) => {
          if (current) return current
          return active.find((template) => template.is_default)?.id ?? active[0]?.id ?? null
        })
      })
      .catch(() => {
        if (!cancelled) setHiringTemplates([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (hiringTemplateTouchedRef.current || !initialHiringTemplateId) return
    setHiringTemplateId(initialHiringTemplateId)
  }, [initialHiringTemplateId])

  useEffect(() => {
    if (!onDraftChange || !isEditing) return
    const next = serializeOfferDraft(resumeDraftSource, draft)
    if (hiringTemplateId != null) next.hiring_template = hiringTemplateId
    const json = JSON.stringify(next)
    if (json === lastDraftJsonRef.current) return
    lastDraftJsonRef.current = json
    onDraftChange(next)
  }, [draft, onDraftChange, resumeDraftSource, isEditing, hiringTemplateId])

  useEffect(() => {
    const patch = draftPatchRef.current
    if (!patch || appliedDraftPatchTokenRef.current === patch.token) return
    appliedDraftPatchTokenRef.current = patch.token
    const updates = patch.updates
      || (patch.field ? { [patch.field]: patch.value } : null)
    if (!updates || !Object.keys(updates).length) return
    const formattedUpdates: Record<string, string> = {}
    Object.entries(updates).forEach(([field, value]) => {
      formattedUpdates[field] = formatOfferValueForEditing(field, value)
    })
    setIsEditing(true)
    setDraft((previous) => {
      const base = Object.keys(previous).length
        ? previous
        : buildOfferDraft(resumeDraftSourceRef.current, { includeEmptyFields })
      return { ...base, ...formattedUpdates }
    })
    Object.entries(formattedUpdates).forEach(([field, text]) => {
      rememberFieldValueRef.current(field, text)
    })
    setActiveEditingField(null)
    setActiveAiField(null)
  }, [draftPatch?.token, includeEmptyFields])

  useEffect(() => {
    const patch = aiAssistPatchRef.current
    if (!patch?.field || appliedAiAssistTokenRef.current === patch.token) return
    appliedAiAssistTokenRef.current = patch.token
    setIsEditing(true)
    setDraft((previous) => (
      Object.keys(previous).length
        ? previous
        : buildOfferDraft(resumeDraftSourceRef.current, { includeEmptyFields })
    ))
    setActiveEditingField(null)
    setActiveAiField(patch.field)
    setAiInstruction(String(patch.instruction || ''))
    setAiFieldError('')
  }, [aiAssistPatch?.token, includeEmptyFields])

  const visibleFields = useMemo(() => {
    const sourceEntries = includeEmptyFields
      ? OFFER_SUMMARY_FIELD_ORDER
          .filter((field) => !HIDDEN_OFFER_SUMMARY_KEYS.has(field))
          .map((field) => [field, offerData?.[field]] as [string, any])
      : Object.entries(offerData || {}).filter(([field, value]) => {
          return !HIDDEN_OFFER_SUMMARY_KEYS.has(field) && value !== undefined && value !== null && String(value).trim() !== ''
        })

    const order = new Map(OFFER_SUMMARY_FIELD_ORDER.map((field, index) => [field, index]))
    return sourceEntries.sort(([leftField], [rightField]) => {
      const leftIndex = order.has(leftField) ? order.get(leftField)! : Number.MAX_SAFE_INTEGER
      const rightIndex = order.has(rightField) ? order.get(rightField)! : Number.MAX_SAFE_INTEGER
      if (leftIndex !== rightIndex) return leftIndex - rightIndex
      return leftField.localeCompare(rightField)
    })
  }, [includeEmptyFields, offerData])

  const bulkResumeAction = useMemo(() => {
    if (!baselineOfferData) return null
    const applyFields: Array<{ field: string; value: string }> = []
    const resetFields: Array<{ field: string; value: string }> = []
    for (const [field] of visibleFields) {
      const baselineValue = baselineOfferData[field]
      const baselineText = formatOfferValueForEditing(field, baselineValue)
      const draftValue = draft[field] ?? formatOfferValueForEditing(field, offerData?.[field])
      const parsedDraftValue = parseOfferValueFromEditing(field, draftValue)
      const proposal = qualityProposals[field]
      const proposedValue = proposal?.value
      const proposalText = proposal && proposedValue !== undefined
        ? formatOfferValueForEditing(field, proposedValue)
        : null
      const rememberedText = rememberedUserEdits[field]
      const effectiveProposalText = rememberedText
        ?? (
          proposalText
          && !offerSummaryValuesEqual(field, baselineValue, proposedValue)
            ? proposalText
            : null
        )
      const parsedEffectiveProposal = effectiveProposalText != null
        ? parseOfferValueFromEditing(field, effectiveProposalText)
        : undefined
      const matchesEffectiveProposal = effectiveProposalText != null && (
        draftValue.trim() === effectiveProposalText.trim()
        || offerSummaryValuesEqual(field, parsedEffectiveProposal, parsedDraftValue)
      )
      if (
        effectiveProposalText
        && effectiveProposalText.trim() !== baselineText.trim()
        && !matchesEffectiveProposal
      ) {
        applyFields.push({
          field,
          value: effectiveProposalText,
        })
      }
      const differsFromBaseline = (
        draftValue.trim() !== baselineText.trim()
        || !offerSummaryValuesEqual(field, baselineValue, parsedDraftValue)
      )
      if (differsFromBaseline) {
        resetFields.push({
          field,
          value: baselineText,
        })
      }
    }
    if (applyFields.length > 0) return { kind: 'apply' as const, fields: applyFields }
    if (resetFields.length > 0) return { kind: 'reset' as const, fields: resetFields }
    return null
  }, [baselineOfferData, draft, offerData, qualityProposals, rememberedUserEdits, visibleFields])

  const applyBulkResumeAction = () => {
    if (!bulkResumeAction || !baselineOfferData) return
    if (bulkResumeAction.kind === 'reset') {
      for (const [field, value] of Object.entries(draft)) {
        rememberFieldValue(field, value)
      }
      const next = buildOfferDraft(baselineOfferData, { includeEmptyFields: true })
      for (const field of Object.keys(draft)) {
        if (next[field] === undefined) {
          next[field] = formatOfferValueForEditing(field, baselineOfferData[field])
        }
      }
      setDraft(next)
    } else {
      setDraft((previous) => {
        const next = { ...previous }
        for (const item of bulkResumeAction.fields) {
          next[item.field] = item.value
        }
        return next
      })
    }
    setActiveEditingField(null)
    setActiveAiField(null)
    setAiInstruction('')
    setAiFieldError('')
  }

  const startEditing = () => {
    setDraft(buildOfferDraft(resumeDraftSource, { includeEmptyFields }))
    setActiveEditingField(null)
    setActiveAiField(null)
    setAiInstruction('')
    setAiFieldError('')
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setActiveEditingField(null)
    setActiveAiField(null)
    setAiInstruction('')
    setAiFieldError('')
    if (startInEditMode) {
      // Regenerate workspace stays on the résumé form; reset fields instead of leaving edit.
      setDraft(buildOfferDraft(resumeDraftSource, { includeEmptyFields }))
      setIsEditing(true)
      return
    }
    setIsEditing(false)
    setDraft({})
  }

  const updateDraftField = (field: string, value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
    rememberFieldValue(field, value, true)
  }

  const saveEditedOffer = async () => {
    if (!message.id || isSaving || isCreating) return
    setIsSaving(true)
    try {
      const edited = serializeOfferDraft(offerData, draft)
      if (hiringTemplateId != null) edited.hiring_template = hiringTemplateId
      if (editContext) {
        await editContext.onSaveEdits(editContext.offerId, edited)
      } else {
        await onConfirm(message.id, edited)
      }
      if (startInEditMode) {
        setActiveEditingField(null)
        setActiveAiField(null)
        setAiInstruction('')
        setAiFieldError('')
      } else {
        cancelEditing()
      }
    } catch {
      // The handler reports the failure; keep the draft so it can be corrected.
    } finally {
      setIsSaving(false)
    }
  }

  const refineDraftFieldWithAI = async (field: string, originalValue: any) => {
    const instruction = aiInstruction.trim()
    if (!instruction || generatingField) return
    setGeneratingField(field)
    setAiFieldError('')
    try {
      const currentOffer = serializeOfferDraft(offerData, draft)
      const value = editContext
        ? await editContext.onRefineField(editContext.offerId, currentOffer, field, instruction)
        : await onRefineField(message.id, currentOffer, field, instruction)
      updateDraftField(field, formatOfferValueForEditing(field, value ?? originalValue))
      setActiveAiField(null)
      setAiInstruction('')
    } catch (error: any) {
      setAiFieldError(
        error?.response?.data?.error
        || (locale === 'fr' ? 'La génération de ce champ a échoué. Réessayez.' : 'This field could not be generated. Please try again.'),
      )
    } finally {
      setGeneratingField(null)
    }
  }

  const renderInlineEditor = (field: string, originalValue: any) => {
    const label = OFFER_FIELD_LABELS[field] || field
    const currentValue = draft[field] ?? formatOfferValueForEditing(field, originalValue)

    if (field === OFFER_SUMMARY_LANGUAGE_FIELD) {
      const items = (Array.isArray(parseOfferValueFromEditing(field, currentValue))
        ? parseOfferValueFromEditing(field, currentValue)
        : []) as LanguageItem[]
      return (
        <LanguageField
          className="min-w-0 flex-1"
          items={items}
          onItemsChange={(next) => updateDraftField(field, formatOfferValueForEditing(field, next))}
          placeholder={t('createOffer.form.languagePlaceholder', 'Sélectionnez une langue...')}
          description={t('createOffer.form.languagesHelp', 'Ajoutez chaque langue avec son niveau.')}
        />
      )
    }

    if (OFFER_SUMMARY_BOOLEAN_FIELDS.has(field)) {
      const options = [
        { value: 'true', label: locale === 'fr' ? 'Oui' : 'Yes' },
        { value: 'false', label: locale === 'fr' ? 'Non' : 'No' },
      ]
      return (
        <div role="radiogroup" className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {options.map((option) => {
            const selected = currentValue === option.value
            return (
              <span
                key={option.value}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => updateDraftField(field, option.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    updateDraftField(field, option.value)
                  }
                }}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  selected
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                <span
                  className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${
                    selected ? 'border-primary' : 'border-muted-foreground'
                  }`}
                >
                  {selected ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
                </span>
                {option.label}
              </span>
            )
          })}
        </div>
      )
    }

    if (field === 'education_level') {
      const options = OFFER_SUMMARY_EDUCATION_OPTIONS.some((option) => option.value === currentValue) || !currentValue.trim()
        ? OFFER_SUMMARY_EDUCATION_OPTIONS
        : [...OFFER_SUMMARY_EDUCATION_OPTIONS, { value: currentValue, label: currentValue }]
      return (
        <CustomSelect
          className="min-w-0 flex-1"
          value={currentValue}
          onChange={(value) => updateDraftField(field, value)}
          options={options}
          placeholder={t('createOffer.form.educationLevelPlaceholder', 'Bac à Bac +8')}
        />
      )
    }

    if (field === 'contract_type') {
      const options = [
        { value: 'CDI', label: t('createOffer.contractTypes.cdi', 'CDI') },
        { value: 'CDD', label: t('createOffer.contractTypes.cdd', 'CDD') },
        { value: 'Stage', label: t('createOffer.contractTypes.stage', 'Stage') },
        { value: 'Freelance', label: t('createOffer.contractTypes.freelance', 'Freelance') },
        { value: 'Consultant', label: t('createOffer.contractTypes.consultant', 'Consultant') },
        { value: 'Temps partiel', label: t('createOffer.contractTypes.partTime', 'Temps partiel') },
      ]
      const known = options.some((option) => option.value === currentValue)
      return (
        <CustomSelect
          className="min-w-0 flex-1"
          value={currentValue}
          onChange={(value) => updateDraftField(field, value)}
          options={known || !currentValue.trim() ? options : [...options, { value: currentValue, label: currentValue }]}
          placeholder={t('createOffer.form.contractTypePlaceholder', 'Sélectionnez un type de contrat')}
        />
      )
    }

    if (OFFER_SUMMARY_LIST_FIELDS.has(field)) {
      const parsedItems = parseOfferValueFromEditing(field, currentValue)
      const items = Array.isArray(parsedItems) ? parsedItems.map((item) => stringifyOfferArrayItem(item)).filter(Boolean) : []
      return (
        <SingleFieldWithLabels
          className="min-w-0 flex-1"
          items={items}
          onItemsChange={(next) => updateDraftField(field, formatOfferValueForEditing(field, next))}
          placeholder={locale === 'fr' ? 'Ajouter un élément' : 'Add an item'}
        />
      )
    }

    if (OFFER_SUMMARY_MULTI_LINE_FIELDS.has(field)) {
      return (
        <Textarea
          value={currentValue}
          onChange={(event) => updateDraftField(field, event.target.value)}
          rows={field === 'description_text' ? 4 : 3}
          placeholder={locale === 'fr' ? 'Une idée par ligne' : 'One idea per line'}
          className={field === 'description_text' ? 'min-h-[112px] flex-1' : 'min-h-[84px] flex-1'}
          autoFocus
        />
      )
    }

    const inputType = OFFER_SUMMARY_NUMBER_FIELDS.has(field) ? 'number' : 'text'
    return (
      <Input
        type={inputType}
        value={currentValue}
        onChange={(event) => updateDraftField(field, event.target.value)}
        placeholder={label}
        className="h-9 min-w-0 flex-1"
        autoFocus
      />
    )
  }

  return (
    <div className="space-y-3">
      {offerCleaned && <ChatMarkdown content={offerCleaned} />}
      <div className="max-w-full rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 p-4 shadow-sm">
        <div className="flex items-center space-x-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center">
            <Briefcase className="h-4 w-4 text-primary" />
          </div>
          <p className="text-sm font-bold text-foreground">
            {t('aiAssistant.offerSummaryTitle', 'Résumé de l\'offre')}
          </p>
        </div>

        <div className="mb-3 rounded-lg border border-border/70 bg-background/80 p-3">
          <p className="text-sm font-semibold text-foreground">
            {t('hiringProcess.template', 'Modèle')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              'createOffer.form.hiringTemplateHelp',
              'Choisissez le workflow de recrutement que cette offre devra suivre.',
            )}
          </p>
          <div className="mt-2">
            <CustomSelect
              options={hiringTemplates.map((template) => ({
                value: String(template.id),
                label: template.is_default
                  ? `${template.name} (${t('common.default', 'Défaut')})`
                  : template.name,
              }))}
              value={hiringTemplateId != null ? String(hiringTemplateId) : ''}
              onChange={(value) => {
                hiringTemplateTouchedRef.current = true
                setHiringTemplateId(value ? Number(value) : null)
              }}
              placeholder={t('createOffer.form.hiringTemplatePlaceholder', 'Sélectionnez un modèle')}
              disabled={isOfferConfirmed || isOfferCancelled || isSaving || isCreating}
            />
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {baselineOfferData && Object.keys(qualityProposals).length > 0
                ? (locale === 'fr'
                  ? 'Les propositions sont appliquées par défaut. Réinitialisez un champ pour revenir à sa valeur d\'origine, puis réappliquez-la si besoin.'
                  : 'Proposals are applied by default. Reset a field to restore its original value, then re-apply it if needed.')
                : editContext
                  ? (locale === 'fr'
                    ? 'Sélectionnez le crayon du champ à modifier. Vos changements seront résumés pour confirmation avant d’être appliqués à l’offre.'
                    : 'Select the pencil beside a field. Your changes will be summarized for confirmation before they are applied to the offer.')
                  : (locale === 'fr'
                    ? 'Sélectionnez le crayon du champ à modifier, ou décrivez vos changements dans le chat, puis validez.'
                    : 'Select the pencil beside a field, or describe your changes in the chat, then save.')}
            </p>

            <ul className="divide-y divide-border min-w-0">
              {visibleFields.map(([field, originalValue]) => {
                const label = OFFER_FIELD_LABELS[field] || field
                const isActive = activeEditingField === field
                const isAiActive = activeAiField === field
                const draftValue = draft[field] ?? formatOfferValueForEditing(field, originalValue)
                const parsedDraftValue = parseOfferValueFromEditing(field, draftValue)
                const displayValue = formatOfferSummaryDisplayValue(parsedDraftValue)
                const baselineValue = baselineOfferData ? baselineOfferData[field] : undefined
                const qualityProposal = qualityProposals[field]
                const hasQualityProposal = Boolean(qualityProposal)
                const proposedValue = hasQualityProposal ? qualityProposal.value : offerData?.[field]
                const inferredKind = baselineOfferData
                  ? classifyOfferFieldChange(field, baselineValue, parsedDraftValue)
                  : 'unchanged'
                const changeKind = (
                  inferredKind !== 'unchanged' && qualityProposal?.action
                    ? (
                      qualityProposal.action === 'add'
                        ? 'added'
                        : qualityProposal.action === 'remove'
                          ? 'removed'
                          : 'changed'
                    )
                    : inferredKind
                ) as OfferFieldChangeKind
                const matchesBaseline = !baselineOfferData
                  || offerSummaryValuesEqual(field, baselineValue, parsedDraftValue)
                const rememberedText = rememberedUserEdits[field]
                const qualityText = hasQualityProposal
                  ? formatOfferValueForEditing(field, proposedValue)
                  : null
                const qualityDiffersFromBaseline = Boolean(
                  baselineOfferData
                  && qualityText
                  && !offerSummaryValuesEqual(field, baselineValue, proposedValue),
                )
                const effectiveProposalText = rememberedText
                  ?? (qualityDiffersFromBaseline ? qualityText : null)
                const matchesEffectiveProposal = Boolean(
                  effectiveProposalText
                  && (
                    draftValue.trim() === effectiveProposalText.trim()
                    || offerSummaryValuesEqual(
                      field,
                      parseOfferValueFromEditing(field, effectiveProposalText),
                      parsedDraftValue,
                    )
                  ),
                )
                const canApplyProposal = Boolean(
                  baselineOfferData
                  && effectiveProposalText
                  && effectiveProposalText.trim() !== formatOfferValueForEditing(field, baselineValue).trim()
                  && !matchesEffectiveProposal
                )
                const canResetToBaseline = Boolean(baselineOfferData && !matchesBaseline)
                const isListField = OFFER_SUMMARY_LIST_FIELDS.has(field) || field === OFFER_SUMMARY_LANGUAGE_FIELD
                const listDiff = (
                  baselineOfferData
                  && isListField
                  && !matchesBaseline
                )
                  ? diffOfferListItems(baselineValue, parsedDraftValue)
                  : null
                const badgeKind: OfferFieldChangeKind = listDiff
                  ? (
                    listDiff.added.length > 0 && listDiff.removed.length === 0
                      ? 'added'
                      : listDiff.removed.length > 0 && listDiff.added.length === 0
                        ? 'removed'
                        : 'changed'
                  )
                  : (
                    matchesBaseline
                      ? 'unchanged'
                      : isOfferSummaryValueEmpty(baselineValue)
                        ? 'added'
                        : isOfferSummaryValueEmpty(parsedDraftValue)
                          ? 'removed'
                          : 'changed'
                  )
                const changeMeta = badgeKind === 'added'
                  ? {
                      label: locale === 'fr' ? 'Ajouter' : 'Add',
                      className: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
                      Icon: Plus,
                      rowClass: 'bg-emerald-50/40 dark:bg-emerald-950/20',
                    }
                  : badgeKind === 'removed'
                    ? {
                        label: locale === 'fr' ? 'Retirer' : 'Remove',
                        className: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
                        Icon: Minus,
                        rowClass: 'bg-rose-50/40 dark:bg-rose-950/20',
                      }
                    : badgeKind === 'changed'
                      ? {
                          label: locale === 'fr' ? 'Remplacer' : 'Replace',
                          className: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
                          Icon: Replace,
                          rowClass: 'bg-amber-50/30 dark:bg-amber-950/15',
                        }
                      : null

                return (
                  <li
                    key={field}
                    className={`py-2 ${changeMeta ? `rounded-md px-2 ${changeMeta.rowClass}` : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {label}
                        {changeMeta ? (
                          <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-wide ${changeMeta.className}`}>
                            <changeMeta.Icon className="h-3 w-3" />
                            {changeMeta.label}
                          </span>
                        ) : null}
                      </span>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {(canApplyProposal || canResetToBaseline) && (
                          <div className="mr-1 flex items-center gap-1">
                            {canApplyProposal && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isSaving || isCreating || !!generatingField}
                                className="h-7 border-emerald-300 bg-emerald-50 px-2 text-xs text-emerald-900 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
                                onClick={() => {
                                  if (!effectiveProposalText) return
                                  setDraft((previous) => ({
                                    ...previous,
                                    [field]: effectiveProposalText,
                                  }))
                                  setActiveEditingField(null)
                                }}
                              >
                                <Check className="mr-1 h-3.5 w-3.5" />
                                {locale === 'fr' ? 'Appliquer' : 'Apply'}
                              </Button>
                            )}
                            {canResetToBaseline && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isSaving || isCreating || !!generatingField}
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  setDraft((previous) => ({
                                    ...previous,
                                    [field]: formatOfferValueForEditing(field, baselineValue),
                                  }))
                                  setActiveEditingField(null)
                                }}
                              >
                                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                                {locale === 'fr' ? 'Réinitialiser' : 'Reset'}
                              </Button>
                            )}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setActiveAiField(null)
                            setAiFieldError('')
                            setActiveEditingField(isActive ? null : field)
                          }}
                          disabled={isSaving || isCreating || !!generatingField}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={isActive
                            ? (locale === 'fr' ? `Terminer la modification de ${label}` : `Finish editing ${label}`)
                            : (locale === 'fr' ? `Modifier ${label}` : `Edit ${label}`)}
                          title={isActive
                            ? (locale === 'fr' ? 'Terminer' : 'Done')
                            : (locale === 'fr' ? 'Modifier ce champ' : 'Edit this field')}
                        >
                          {isActive ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveEditingField(null)
                            setAiFieldError('')
                            setAiInstruction('')
                            setActiveAiField(isAiActive ? null : field)
                          }}
                          disabled={isSaving || isCreating || (!!generatingField && generatingField !== field)}
                          className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-40 ${isAiActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-primary/10 hover:text-primary'}`}
                          aria-label={locale === 'fr' ? `Modifier ${label} avec l’IA` : `Edit ${label} with AI`}
                          title={locale === 'fr' ? 'Modifier avec l’IA' : 'Edit with AI'}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="min-w-0">
                        {isActive ? (
                          renderInlineEditor(field, originalValue)
                        ) : offerSummaryListKind(field) ? (
                          <div className="space-y-2 pt-1">
                            <OfferSummaryValueList
                              items={listDiff ? listDiff.kept : offerSummaryItemTexts(parsedDraftValue)}
                              kind={offerSummaryListKind(field) || 'pill'}
                              field={field}
                              tone={!listDiff && changeKind === 'added'
                                ? 'added'
                                : !listDiff && changeKind === 'changed'
                                  ? 'replaced'
                                  : !listDiff && changeKind === 'removed'
                                    ? 'removed'
                                    : 'default'}
                            />
                            {listDiff && listDiff.added.length > 0 ? (
                              <OfferSummaryValueList
                                items={listDiff.added}
                                kind={offerSummaryListKind(field) || 'pill'}
                                field={field}
                                tone={listDiff.removed.length > 0 ? 'replaced' : 'added'}
                              />
                            ) : null}
                            {listDiff && listDiff.removed.length > 0 ? (
                              <OfferSummaryValueList
                                items={listDiff.removed}
                                kind={offerSummaryListKind(field) || 'pill'}
                                field={field}
                                tone="removed"
                              />
                            ) : null}
                            {!listDiff && offerSummaryItemTexts(parsedDraftValue).length === 0 ? (
                              <span className="block text-sm text-foreground">—</span>
                            ) : null}
                          </div>
                        ) : listDiff ? (
                          <div className="space-y-1.5 pt-1">
                            {listDiff.kept.length > 0 && (
                              <p className="break-words text-sm text-foreground [overflow-wrap:anywhere]">
                                {listDiff.kept.join(', ')}
                              </p>
                            )}
                            {listDiff.added.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {listDiff.added.map((item) => (
                                  <span
                                    key={`add-${item}`}
                                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                                  >
                                    <Plus className="h-3 w-3" />
                                    {item}
                                  </span>
                                ))}
                              </div>
                            )}
                            {listDiff.removed.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {listDiff.removed.map((item) => (
                                  <span
                                    key={`rm-${item}`}
                                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-800 line-through dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
                                  >
                                    <Minus className="h-3 w-3" />
                                    {item}
                                  </span>
                                ))}
                              </div>
                            )}
                            {listDiff.kept.length === 0 && listDiff.added.length === 0 && listDiff.removed.length === 0 && (
                              <span className="block break-words text-sm text-foreground">—</span>
                            )}
                          </div>
                        ) : (
                          <span className={`block break-words pt-1 text-sm [overflow-wrap:anywhere] ${
                            changeKind === 'added'
                              ? 'text-emerald-800 dark:text-emerald-200'
                              : changeKind === 'changed'
                                ? 'text-amber-900 dark:text-amber-100'
                                : changeKind === 'removed'
                                  ? 'text-rose-700 line-through dark:text-rose-300'
                                  : 'text-foreground'
                          }`}>
                            {displayValue || '—'}
                          </span>
                        )}
                        {isAiActive && (
                          <div className="mt-2 rounded-lg border border-primary/20 bg-background/60 p-2">
                            <div className="flex items-center gap-2">
                              <Input
                                value={aiInstruction}
                                onChange={(event) => setAiInstruction(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    void refineDraftFieldWithAI(field, originalValue)
                                  }
                                  if (event.key === 'Escape') setActiveAiField(null)
                                }}
                                placeholder={locale === 'fr'
                                  ? 'Ex. Rendez ce champ plus attractif…'
                                  : 'E.g. Make this field more attractive…'}
                                className="h-9"
                                disabled={generatingField === field}
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => void refineDraftFieldWithAI(field, originalValue)}
                                disabled={!aiInstruction.trim() || generatingField === field}
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label={locale === 'fr' ? `Générer ${label}` : `Generate ${label}`}
                              >
                                {generatingField === field
                                  ? <Loader2 className="h-4 w-4 animate-spin" />
                                  : <Send className="h-4 w-4" />}
                              </button>
                            </div>
                            {aiFieldError && <p className="mt-1.5 text-xs text-destructive">{aiFieldError}</p>}
                          </div>
                        )}
                    </div>
                  </li>
                )
              })}
            </ul>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {isOfferConfirmed && confirmedInfo?.offerId ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onOpenOffer(confirmedInfo.offerId)}
                  className="flex items-center space-x-1 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <CheckCircle className="mr-1 h-3.5 w-3.5" />
                  <span>{t('aiAssistant.viewOffer', 'Voir l\'offre')}</span>
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={saveEditedOffer}
                    disabled={isSaving || isCreating || !!generatingField || !String(draft.job_title || '').trim()}
                    className="flex items-center space-x-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {isSaving || isCreating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                    <span>
                      {resumeAction === 'create'
                        ? (locale === 'fr' ? 'Créer' : 'Create')
                        : resumeAction === 'regenerate'
                          ? (locale === 'fr' ? 'Régénérer' : 'Regenerate')
                          : resumeAction === 'modify'
                            ? (locale === 'fr' ? 'Appliquer les modifs' : 'Apply changes')
                            : startInEditMode
                              ? (locale === 'fr' ? 'Valider' : 'Validate')
                              : (locale === 'fr' ? 'Valider les modifications' : 'Save changes')}
                    </span>
                  </Button>
                  {startInEditMode ? (
                    bulkResumeAction ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={applyBulkResumeAction}
                        disabled={isSaving || isCreating || !!generatingField}
                      >
                        {bulkResumeAction.kind === 'apply' ? (
                          <Check className="mr-1 h-3.5 w-3.5" />
                        ) : (
                          <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        )}
                        {bulkResumeAction.kind === 'apply'
                          ? (locale === 'fr' ? 'Appliquer les propositions' : 'Apply proposals')
                          : (locale === 'fr' ? 'Réinitialiser' : 'Reset')}
                      </Button>
                    ) : null
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEditing}
                      disabled={isSaving || isCreating || !!generatingField}
                    >
                      <X className="mr-1 h-3.5 w-3.5" />
                      {locale === 'fr' ? 'Annuler la modification' : 'Cancel edit'}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <ul className="mb-4 divide-y divide-border min-w-0">
              {visibleFields.map(([key, value]) => {
                const label = OFFER_FIELD_LABELS[key] || key
                let displayValue: string
                const listKind = offerSummaryListKind(key)
                if (listKind) {
                  const items = offerSummaryItemTexts(value)
                  if (!items.length) return null
                  return (
                    <li key={key} className="flex flex-col gap-1.5 py-2 sm:flex-row sm:items-start sm:gap-2">
                      <span className="w-full shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:w-40 sm:min-w-[8rem] sm:max-w-[38%] sm:pt-1">
                        {label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <OfferSummaryValueList items={items} kind={listKind} field={key} />
                      </div>
                    </li>
                  )
                }
                if (typeof value === 'boolean') {
                  displayValue = value ? '✅ Oui' : '❌ Non'
                } else if (Array.isArray(value)) {
                  if (value.length === 0) return null
                  if (typeof value[0] === 'object') {
                    displayValue = value.map((item: any) => stringifyOfferArrayItem(item)).join(', ')
                  } else {
                    displayValue = value.join(', ')
                  }
                } else if (value && typeof value === 'object') {
                  displayValue = stringifyOfferArrayItem(value)
                } else {
                  displayValue = String(value || '')
                }
                if (!displayValue) return null
                return (
                  <li key={key} className="flex flex-col gap-0.5 py-1.5 sm:flex-row sm:items-start sm:gap-2">
                    <span className="w-full shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:w-40 sm:min-w-[8rem] sm:max-w-[38%] sm:pr-2">
                      {label}
                    </span>
                    <span className="min-w-0 flex-1 break-words text-sm text-foreground [overflow-wrap:anywhere]">
                      {displayValue}
                    </span>
                  </li>
                )
              })}
            </ul>

            {editContext ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startEditing}
                  disabled={isSaving}
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  {t('aiAssistant.editOffer', 'Modifier')}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {locale === 'fr'
                    ? 'Modifiez les champs à la main, ou décrivez vos changements dans le chat.'
                    : 'Edit the fields by hand, or describe your changes in the chat.'}
                </span>
              </div>
            ) : presentationOnly ? null : isOfferCancelled ? (
              <div className="flex items-center gap-2 rounded-lg border border-muted bg-muted/50 px-3 py-2 text-muted-foreground">
                <X className="h-4 w-4" />
                <span className="text-sm font-medium">{t('aiAssistant.offerCancelled', 'Proposition annulée')}</span>
              </div>
            ) : isOfferConfirmed ? (
              <div className="flex items-center space-x-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg px-3 py-2">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {t('aiAssistant.offerValidatedSuccess', 'Offre validee avec succes !')}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => confirmedInfo?.offerId && onOpenOffer(confirmedInfo.offerId)}
                  className="ml-auto h-8 text-green-700 hover:text-primary dark:text-green-400"
                >
                  {t('aiAssistant.viewOffer', 'Voir l\'offre')}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            ) : isLatestMessage && !isOfferCompleteEnough ? (
              <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                <p className="text-sm font-medium">
                  {t('aiAssistant.incompleteOffer', 'Cette offre est trop incomplète pour être validée.')}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onConfirm(message.id, offerData)}
                  disabled={isCreating || isSaving}
                >
                  <Sparkles className="mr-1 h-3.5 w-3.5" />
                  {t('aiAssistant.completeWithAI', 'Compléter avec l’IA')}
                </Button>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={startEditing}
                    disabled={isCancelling || isCreating || isSaving}
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    {t('aiAssistant.editOffer', 'Modifier')}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => onCancel(message.id)}
                    disabled={isCancelling || isCreating || isSaving}
                  >
                    {isCancelling ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <X className="mr-1 h-3.5 w-3.5" />}
                    {t('aiAssistant.cancelOffer', 'Annuler')}
                  </Button>
                </div>
              </div>
            ) : isLatestMessage ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => onConfirm(message.id, offerData)}
                  disabled={isCreating || isSaving || isCancelling}
                  className="flex items-center space-x-1 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {isCreating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  <span>
                    {resumeAction === 'create'
                      ? (locale === 'fr' ? 'Créer' : 'Create')
                      : resumeAction === 'regenerate'
                        ? (locale === 'fr' ? 'Régénérer' : 'Regenerate')
                        : resumeAction === 'modify'
                          ? (locale === 'fr' ? 'Appliquer les modifs' : 'Apply changes')
                          : t('aiAssistant.validateOffer', 'Valider')}
                  </span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startEditing}
                  disabled={isCreating || isSaving || isCancelling}
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  {t('aiAssistant.editOffer', 'Modifier')}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onCancel(message.id)}
                  disabled={isCreating || isSaving || isCancelling}
                >
                  {isCancelling ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <X className="mr-1 h-3.5 w-3.5" />}
                  {t('aiAssistant.cancelOffer', 'Annuler')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}



const OFFER_FIELD_LABELS: Record<string, string> = {
  job_title: 'Titre du poste',
  department: 'Département',
  contract_type: 'Type de contrat',
  experience_level: 'Expérience (années)',
  description_text: 'Description',
  requirements: 'Compétences techniques',
  soft_skills: 'Soft skills',
  main_missions: 'Missions principales',
  benefits: 'Souhaitables (nice to have)',
  education_level: 'Niveau d\'études',
  languages: 'Langues',
  certifications: 'Certifications',
  remote_allowed: 'Télétravail',
}

const fieldLabelKeys: Record<string, string> = {
  name: 'aiAssistant.fieldLabels.name',
  email: 'aiAssistant.fieldLabels.email',
  phone: 'aiAssistant.fieldLabels.phone',
  role: 'aiAssistant.fieldLabels.role',
  experience: 'aiAssistant.fieldLabels.experience',
  location: 'aiAssistant.fieldLabels.location',
  skills: 'aiAssistant.fieldLabels.skills',
  soft_skills: 'aiAssistant.fieldLabels.soft_skills',
  nice_to_have: 'aiAssistant.fieldLabels.nice_to_have',
  main_missions: 'aiAssistant.fieldLabels.main_missions',
  certifications: 'aiAssistant.fieldLabels.certifications',
  languages: 'aiAssistant.fieldLabels.languages',
  education: 'aiAssistant.fieldLabels.education',
  projets: 'aiAssistant.fieldLabels.projets',
  work_history: 'aiAssistant.fieldLabels.work_history',
  bio: 'aiAssistant.fieldLabels.bio',
  age: 'aiAssistant.fieldLabels.age',
  genre: 'aiAssistant.fieldLabels.genre',
  career_analysis_sentences: 'aiAssistant.fieldLabels.career_analysis_sentences'
}

const CANDIDATES_PAGE_SIZE = 10
const AI_AGENT_ILLUSTRATION = '/illustrations/ai-agent-recruitment.png?v=3'

const HeroBackgroundIllustration = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-y-[4%] right-[1.5%] hidden w-1/2 overflow-hidden xl:block"
    style={{
      WebkitMaskImage: 'radial-gradient(ellipse 96% 92% at 50% 50%, #000 0%, #000 72%, transparent 100%)',
      maskImage: 'radial-gradient(ellipse 96% 92% at 50% 50%, #000 0%, #000 72%, transparent 100%)',
    }}
  >
    <img
      src={AI_AGENT_ILLUSTRATION}
      alt=""
      className="absolute left-1/2 top-1/2 h-[108%] w-auto max-w-none -translate-x-1/2 -translate-y-1/2"
    />
  </div>
)

export function AIAssistant() {
  const { t, i18n } = useTranslation()
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const { setHistoryOpen } = useAiChrome()
  const getFieldLabel = (field: string): string => {
    const key = fieldLabelKeys[field]
    if (!key) return field
    return t(key, field)
  }
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [aiStatus, setAiStatus] = useState<AIStatus | null>(null)
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null)
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [contextLimit, setContextLimit] = useState<ContextLimitState | null>(null)
  const [dismissedContextWarningKey, setDismissedContextWarningKey] = useState<string | null>(null)
  const [isContinuingSession, setIsContinuingSession] = useState(false)
  const [continuationError, setContinuationError] = useState<string | null>(null)
  const continuationInFlightRef = useRef(false)
  const [isLoadingStatus, setIsLoadingStatus] = useState(true)
  const [initError, setInitError] = useState<string | null>(null)
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [sessionLoadError, setSessionLoadError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [candidateCache, setCandidateCache] = useState<Record<number, Candidate>>({})
  const [candidatesTotalCount, setCandidatesTotalCount] = useState(0)
  const [candidatesPage, setCandidatesPage] = useState(1)
  const [hasMoreCandidates, setHasMoreCandidates] = useState(false)
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [loadingMoreCandidates, setLoadingMoreCandidates] = useState(false)
  const [searchingCandidates, setSearchingCandidates] = useState(false)
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([])
  const [isAnalyzeCandidateEnabled, setIsAnalyzeCandidateEnabled] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false)
  const [activeStarterAction, setActiveStarterAction] = useState<'candidate-analysis' | 'candidate-comparison' | 'offer-management' | null>(null)
  const [pendingQuickActionId, setPendingQuickActionId] = useState<string | null>(null)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [candidateGroupFilter, setCandidateGroupFilter] = useState('all')
  // ── Offer selector state ──────────────────────────────────────────────────
  const [selectedOfferIds, setSelectedOfferIds] = useState<number[]>([])
  const [isOfferSelectorOpen, setIsOfferSelectorOpen] = useState(false)
  const [offers, setOffers] = useState<OfferSummary[]>([])
  const [offersTotalCount, setOffersTotalCount] = useState(0)
  const [offersPage, setOffersPage] = useState(1)
  const [hasMoreOffers, setHasMoreOffers] = useState(false)
  const [loadingOffers, setLoadingOffers] = useState(false)
  const [loadingMoreOffers, setLoadingMoreOffers] = useState(false)
  const [searchingOffers, setSearchingOffers] = useState(false)
  const [offerSearch, setOfferSearch] = useState('')
  const [offerStatusFilter, setOfferStatusFilter] = useState('')
  const [offerSelectionMode, setOfferSelectionMode] = useState<'single' | 'multiple'>('single')
  const [pendingOfferQuickAction, setPendingOfferQuickAction] = useState<OfferQuickAction | null>(null)
  const [activeOfferSubmenu, setActiveOfferSubmenu] = useState<'edit' | null>(null)
  const [executingOfferActionId, setExecutingOfferActionId] = useState<string | null>(null)
  const offerSelectorRef = useRef<HTMLDivElement>(null)
  const offerListRef = useRef<HTMLDivElement>(null)
  const offerScrollSentinelRef = useRef<HTMLDivElement>(null)
  const offerSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const offerStatusFilterRef = useRef('')
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [historySearch, setHistorySearch] = useState('')
  const historySearchRef = useRef<HTMLInputElement>(null)
  const [fieldSelections, setFieldSelections] = useState<Record<string, string[]>>({})
  const [confirmedOffers, setConfirmedOffers] = useState<Record<string, { offerId: number; status: string }>>({})
  const [creatingOfferId, setCreatingOfferId] = useState<string | null>(null)
  const [cancellingOfferId, setCancellingOfferId] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null)
  const [regenerationAiAssistPatch, setRegenerationAiAssistPatch] = useState<{
    messageId: string
    field: string
    instruction: string
    token: number
  } | null>(null)
  const regenerationAiAssistPatchTokenRef = useRef(0)
  const [regenerationDraftPatch, setRegenerationDraftPatch] = useState<{
    messageId: string
    field: string
    value: any
    token: number
  } | null>(null)
  const regenerationDraftPatchTokenRef = useRef(0)
  const [resumeChatPatch, setResumeChatPatch] = useState<{
    messageId: string
    updates: Record<string, any>
    token: number
  } | null>(null)
  const resumeChatPatchTokenRef = useRef(0)
  const appliedResumeEditsRef = useRef<string | null>(null)
  const [regenerationCurrentDraft, setRegenerationCurrentDraft] = useState<{
    messageId: string
    data: Record<string, any>
  } | null>(null)
  const [contextInspectorEpoch, setContextInspectorEpoch] = useState(0)
  const [selectedGenerationVersions, setSelectedGenerationVersions] = useState<Record<string, number>>({})
  const [selectedUserMessageId, setSelectedUserMessageId] = useState<string | null>(null)
  const [isMobileUserListOpen, setIsMobileUserListOpen] = useState(false)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editedMessageContent, setEditedMessageContent] = useState('')
  // ── Message pagination state ────────────────────────────────────────────────
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [olderMessagesCursor, setOlderMessagesCursor] = useState<number | null>(null)
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false)
  const [olderMessagesError, setOlderMessagesError] = useState<string | null>(null)
  const [messageLoadError, setMessageLoadError] = useState<string | null>(null)
  const isPrependingRef = useRef(false)
  const olderMessagesSentinelRef = useRef<HTMLDivElement>(null)
  const loadedSessionIdRef = useRef<number | null>(null)
  const activeSessionIdRef = useRef<number | null>(null)
  const messageLoadTokenRef = useRef(0)
  const sessionEpochRef = useRef(0)
  const messageLoadAbortRef = useRef<AbortController | null>(null)
  const olderMessagesAbortRef = useRef<AbortController | null>(null)
  const sessionListTokenRef = useRef(0)
  const candidateRequestTokenRef = useRef(0)
  const offerRequestTokenRef = useRef(0)
  const prevScrollHeightRef = useRef(0)

  useEffect(() => {
    const latest = [...messages].reverse().find((message) => (
      message.type === 'ai'
      && Array.isArray(message.metadata?.resume_ai_edits)
      && message.metadata.resume_ai_edits.length > 0
    ))
    if (!latest || appliedResumeEditsRef.current === latest.id) return
    const updates: Record<string, any> = {}
    for (const item of latest.metadata.resume_ai_edits) {
      if (item?.field) updates[String(item.field)] = item.value
    }
    if (!Object.keys(updates).length) return
    appliedResumeEditsRef.current = latest.id
    resumeChatPatchTokenRef.current += 1
    setResumeChatPatch({
      messageId: String(latest.metadata.resume_target_message_id || latest.id),
      updates,
      token: resumeChatPatchTokenRef.current,
    })
    setContextInspectorEpoch((epoch) => epoch + 1)
  }, [messages])

  const isCurrentSessionRequest = (sessionId: number | null, token: number) =>
    isLatestSessionRequest(
      sessionId,
      activeSessionIdRef.current,
      token,
      sessionEpochRef.current,
    )
  const { addToast } = useToast()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const plusMenuRef = useRef<HTMLDivElement>(null)
  const messageInputRef = useRef<HTMLTextAreaElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const userMessagesContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const latestUserMessageIdRef = useRef<string | null>(null)
  const candidateListRef = useRef<HTMLDivElement>(null)
  const candidateScrollSentinelRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getCandidateById = (id: number): Candidate | undefined =>
    candidateCache[id] ?? candidates.find(c => c.id === id)

  const [offerCache, setOfferCache] = useState<Record<number, OfferSummary>>({})
  const [offerDetailCache, setOfferDetailCache] = useState<Record<number, Record<string, any> | null>>({})

  const pendingRichOfferIds = useMemo(() => Array.from(new Set(
    messages
      .filter((message) => (
        message.type === 'ai'
        && needsOfferDetailForRichPreview(message.metadata)
        && Number(message.metadata?.offer_id) > 0
      ))
      .map((message) => Number(message.metadata.offer_id)),
  )), [messages])
  const pendingRichOfferIdsKey = pendingRichOfferIds.join(',')

  useEffect(() => {
    const missingIds = pendingRichOfferIds.filter((offerId) => (
      !Object.prototype.hasOwnProperty.call(offerDetailCache, offerId)
    ))
    if (missingIds.length === 0) return

    let cancelled = false
    Promise.all(missingIds.map(async (offerId) => {
      try {
        const response = await offersApi.getById(offerId)
        return [offerId, response.data as Record<string, any>] as const
      } catch (error) {
        console.error(`Unable to hydrate offer ${offerId} for rich preview`, error)
        return [offerId, null] as const
      }
    })).then((entries) => {
      if (cancelled) return
      setOfferDetailCache((previous) => ({
        ...previous,
        ...Object.fromEntries(entries),
      }))
    })

    return () => {
      cancelled = true
    }
    // The stable key changes only when pending edit offer IDs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRichOfferIdsKey])

  const selectedOffers = useMemo(() => {
    if (selectedOfferIds.length === 0) return []
    return selectedOfferIds
      .map((id) => offerCache[id] ?? offers.find((o) => o.id === id))
      .filter(Boolean) as OfferSummary[]
  }, [selectedOfferIds, offerCache, offers])

  const getOfferById = (id: number): OfferSummary | undefined =>
    offerCache[id] ?? offers.find(o => o.id === id)

  const collectOfferIdsFromMessages = (msgs: Message[]): number[] => {
    const ids = new Set<number>()
    msgs.forEach((message) => {
      const oid = message.offerId ?? message.metadata?.offer_id
      if (oid != null) {
        const n = Number(oid)
        if (Number.isFinite(n) && n > 0) ids.add(n)
      }
      const oids = message.metadata?.offer_ids
      if (Array.isArray(oids)) {
        oids.forEach((id: any) => {
          const n = Number(id)
          if (Number.isFinite(n) && n > 0) ids.add(n)
        })
      }
    })
    return Array.from(ids)
  }

  const hydrateOffersForIds = async (ids: number[]) => {
    const uniqueIds = [...new Set(ids.filter((id) => Number.isFinite(id) && id > 0))]
    if (uniqueIds.length === 0) return

    // Only fetch uncached offers
    const missingIds = uniqueIds.filter((id) => !offerCache[id])
    if (missingIds.length === 0) return

    const fetched: Record<number, OfferSummary> = {}
    try {
      const response = await aiAssistantApi.getOffers({ page: 1, page_size: 50 })
      const allOffers = response.data.offers || []
      const idSet = new Set(missingIds)
      allOffers.forEach((offer) => {
        if (idSet.has(offer.id)) {
          fetched[offer.id] = {
            id: offer.id,
            job_title: offer.job_title,
            department: offer.department,
            contract_type: offer.contract_type,
            experience_level: offer.experience_level,
            status: offer.status,
            allowed_actions: offer.allowed_actions,
          }
        }
      })
    } catch (error) {
      console.error('Error hydrating offers:', error)
      return
    }

    if (Object.keys(fetched).length === 0) return

    setOfferCache((prev) => {
      let changed = false
      const updated = { ...prev }
      Object.values(fetched).forEach((offer) => {
        if (!updated[offer.id]) {
          updated[offer.id] = offer
          changed = true
        }
      })
      return changed ? updated : prev
    })
  }

  const loadDynamicFormCandidates = useCallback(async (search: string): Promise<DynamicCandidateOption[]> => {
    const response = await aiAssistantApi.getCandidates({
      page: 1,
      page_size: 20,
      ...(search.trim() ? { search: search.trim() } : {}),
    })
    const options = response.data.candidates || []
    setCandidateCache((previous) => {
      const updated = { ...previous }
      options.forEach((candidate) => { updated[candidate.id] = candidate })
      return updated
    })
    return options.map((candidate) => ({
      id: candidate.id,
      name: candidate.name,
      role: candidate.role,
      email: candidate.email,
    }))
  }, [])

  const hydrateCandidatesForIds = async (ids: number[]) => {
    const uniqueIds = [...new Set(ids.filter((id) => Number.isFinite(id) && id > 0))]
    if (uniqueIds.length === 0) return

    // Only fetch uncached candidates
    const missingIds = uniqueIds.filter((id) => !candidateCache[id])
    if (missingIds.length === 0) return

    const results = await Promise.allSettled(
      missingIds.map((id) => candidatesApi.getById(id, { timeout: AI_API_TIMEOUTS.candidateHydration }))
    )

    const fetched: Record<number, Candidate> = {}
    results.forEach((result) => {
      if (result.status !== 'fulfilled') return
      const candidate = result.value.data
      fetched[candidate.id] = {
        id: candidate.id,
        name: candidate.name,
        email: candidate.email,
        role: candidate.role,
        profile_picture: candidate.profile_picture,
      }
    })

    if (Object.keys(fetched).length === 0) return

    setCandidateCache((prev) => {
      const updated = { ...prev }
      Object.values(fetched).forEach((candidate) => {
        if (!updated[candidate.id]) {
          updated[candidate.id] = candidate
        }
      })
      return updated
    })
  }

  const fetchCandidates = async (
    page: number,
    options: { search?: string; team?: string; append?: boolean } = {}
  ) => {
    const search = options.search ?? candidateSearch
    const team = options.team ?? candidateGroupFilter
    const append = options.append ?? false
    const requestToken = ++candidateRequestTokenRef.current

    try {
      if (page === 1 && !append) {
        if (search.trim()) setSearchingCandidates(true)
        else setLoadingCandidates(true)
      } else {
        setLoadingMoreCandidates(true)
      }

      const params: { page: number; page_size: number; search?: string; team?: string } = {
        page,
        page_size: CANDIDATES_PAGE_SIZE,
      }
      if (search.trim()) params.search = search.trim()
      if (team && team !== 'all') params.team = team

      const response = await aiAssistantApi.getCandidates(params)
      if (requestToken !== candidateRequestTokenRef.current) return
      const newCandidates = response.data.candidates || []

      setCandidatesTotalCount(response.data.count)
      setHasMoreCandidates(response.data.next)
      setCandidatesPage(page)

      setCandidates(prev => {
        if (append && page > 1) {
          const existingIds = new Set(prev.map(c => c.id))
          return [...prev, ...newCandidates.filter(c => !existingIds.has(c.id))]
        }
        return newCandidates
      })

      setCandidateCache(prev => {
        const updated = { ...prev }
        newCandidates.forEach(c => { updated[c.id] = c })
        return updated
      })
    } catch (error) {
      if (requestToken !== candidateRequestTokenRef.current) return
      console.error('Error fetching candidates:', error)
    } finally {
      if (requestToken !== candidateRequestTokenRef.current) return
      setLoadingCandidates(false)
      setLoadingMoreCandidates(false)
      setSearchingCandidates(false)
    }
  }

  const loadMoreCandidates = () => {
    if (loadingMoreCandidates || loadingCandidates || searchingCandidates || !hasMoreCandidates) return
    fetchCandidates(candidatesPage + 1, { append: true })
  }

  // ── Offer selector helpers ──────────────────────────────────────────────────
  const OFFERS_PAGE_SIZE = 10

  const fetchOffers = async (
    page: number,
    options: { search?: string; append?: boolean; status?: string } = {}
  ) => {
    const search = options.search ?? offerSearch
    const status = options.status ?? offerStatusFilterRef.current
    const append = options.append ?? false
    const requestToken = ++offerRequestTokenRef.current

    try {
      if (page === 1 && !append) {
        if (search.trim()) setSearchingOffers(true)
        else setLoadingOffers(true)
      } else {
        setLoadingMoreOffers(true)
      }

      const params: { page: number; page_size: number; search?: string; status?: string } = {
        page,
        page_size: OFFERS_PAGE_SIZE,
      }
      if (search.trim()) params.search = search.trim()
      if (status && status !== 'all') params.status = status

      const response = await aiAssistantApi.getOffers(params)
      if (requestToken !== offerRequestTokenRef.current) return
      const newOffers = response.data.offers || []

      setOffersTotalCount(response.data.count)
      setHasMoreOffers(response.data.next)
      setOffersPage(page)

      setOffers(prev => {
        if (append && page > 1) {
          const existingIds = new Set(prev.map(o => o.id))
          return [...prev, ...newOffers.filter(o => !existingIds.has(o.id))]
        }
        return newOffers
      })
    } catch (error) {
      if (requestToken !== offerRequestTokenRef.current) return
      console.error('Error fetching offers:', error)
    } finally {
      if (requestToken !== offerRequestTokenRef.current) return
      setLoadingOffers(false)
      setLoadingMoreOffers(false)
      setSearchingOffers(false)
    }
  }

  const loadMoreOffers = () => {
    if (loadingMoreOffers || loadingOffers || searchingOffers || !hasMoreOffers) return
    fetchOffers(offersPage + 1, { append: true })
  }

  const openOfferDropdown = (status: string = '') => {
    setIsPlusMenuOpen(false)
    setIsOfferSelectorOpen(true)
    offerStatusFilterRef.current = status
    setOfferStatusFilter(status)
    if (offerSearchTimeoutRef.current) clearTimeout(offerSearchTimeoutRef.current)
    if (offerSearch) setOfferSearch('')
    fetchOffers(1, { search: '', status })
  }

  const handleSelectOffer = (offer: OfferSummary) => {
    setSelectedOfferIds((prev) => {
      if (offerSelectionMode === 'multiple') {
        if (prev.includes(offer.id)) return prev.filter((id) => id !== offer.id)
        return [...prev, offer.id].slice(0, MAX_SELECTED_OFFERS)
      }
      if (prev.length === 1 && prev[0] === offer.id) return []
      return [offer.id]
    })
    setOfferCache((prev) => ({ ...prev, [offer.id]: offer }))
    if (offerSelectionMode === 'single') setIsOfferSelectorOpen(false)
  }

  const handleClearOffer = () => {
    setSelectedOfferIds([])
  }

  // Initialize AI Agent
  const initAbortRef = useRef<AbortController | null>(null)
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const initializeAI = async () => {
    const controller = new AbortController()
    initAbortRef.current = controller

    console.debug('[AI INIT] starting')
    try {
      console.time('[AI INIT] getStatus')
      const statusResponse = await aiAssistantApi.getStatus()
      console.timeEnd('[AI INIT] getStatus')
      setAiStatus(statusResponse.data)

      console.time('[AI INIT] teams')
      const teamsResponse = await teamsApi.getAll()
      console.timeEnd('[AI INIT] teams')
      setTeams(teamsResponse.data.results || [])

      console.time('[AI INIT] sessions')
      await loadSessions()
      console.timeEnd('[AI INIT] sessions')

      console.debug('[AI INIT] complete', {
        sessionCount: sessions.length,
      })
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        console.debug('[AI INIT] aborted (component unmounted)')
        return
      }
      console.error('[AI INIT] error:', error)
      setInitError(error?.message || 'Initialization failed')
    } finally {
      if (!controller.signal.aborted) {
        setIsLoadingStatus(false)
      }
    }
  }

  useEffect(() => {
    console.count('[AI INIT] effect runs')
    initializeAI()
    return () => {
      console.debug('[AI INIT] cleanup')
      initAbortRef.current?.abort()
      if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current)
    }
  }, [])

  // Debounced server search
  useEffect(() => {
    if (!isDropdownOpen) return

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchCandidates(1)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [candidateSearch])

  // Refetch when group filter changes
  useEffect(() => {
    if (!isDropdownOpen) return
    fetchCandidates(1)
  }, [candidateGroupFilter])

  // Infinite scroll in candidate dropdown
  useEffect(() => {
    if (!isDropdownOpen || !candidateScrollSentinelRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreCandidates()
        }
      },
      { threshold: 0.1, root: candidateListRef.current, rootMargin: '100px' }
    )

    observer.observe(candidateScrollSentinelRef.current)
    return () => observer.disconnect()
  }, [isDropdownOpen, hasMoreCandidates, loadingMoreCandidates, loadingCandidates, searchingCandidates, candidatesPage, candidates.length])

  // Debounced offer search
  useEffect(() => {
    if (!isOfferSelectorOpen) return

    if (offerSearchTimeoutRef.current) {
      clearTimeout(offerSearchTimeoutRef.current)
    }

    offerSearchTimeoutRef.current = setTimeout(() => {
      fetchOffers(1)
    }, 300)

    return () => {
      if (offerSearchTimeoutRef.current) {
        clearTimeout(offerSearchTimeoutRef.current)
      }
    }
  }, [offerSearch])

  // Infinite scroll in offer dropdown
  useEffect(() => {
    if (!isOfferSelectorOpen || !offerScrollSentinelRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreOffers()
        }
      },
      { threshold: 0.1, root: offerListRef.current, rootMargin: '100px' }
    )

    observer.observe(offerScrollSentinelRef.current)
    return () => observer.disconnect()
  }, [isOfferSelectorOpen, hasMoreOffers, loadingMoreOffers, loadingOffers, searchingOffers, offersPage, offers.length])

  // Auto-scroll to bottom when new messages are sent or received (not when prepending history)
  useEffect(() => {
    if (messages.length === 0 && !isLoading) return
    if (isPrependingRef.current) {
      isPrependingRef.current = false
      return
    }
    scrollToBottom()
  }, [messages, isLoading])

  useEffect(() => {
    const userMessages = messages.filter(isSelectableUserMessage)
    const latestId = userMessages[userMessages.length - 1]?.id ?? null
    if (latestId !== latestUserMessageIdRef.current) {
      latestUserMessageIdRef.current = latestId
      setSelectedUserMessageId(latestId)
      return
    }
    setSelectedUserMessageId((current) => {
      if (current && userMessages.some((message) => message.id === current)) return current
      return latestId
    })
  }, [messages])

  const processedFieldMsgsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const updates: Record<string, string[]> = {}
    messages.forEach((message) => {
      if (message.type !== 'ai') return
      if (processedFieldMsgsRef.current.has(message.id)) return
      const { fields } = extractProposedFields(message.content)
      if (fields.length === 0) {
        processedFieldMsgsRef.current.add(message.id)
        return
      }
      updates[message.id] = fields
      processedFieldMsgsRef.current.add(message.id)
    })

    if (Object.keys(updates).length > 0) {
      setFieldSelections((prev) => ({ ...prev, ...updates }))
    }
  }, [messages])

  useEffect(() => {
    setHistoryOpen(showHistory)
    return () => setHistoryOpen(false)
  }, [setHistoryOpen, showHistory])

  useEffect(() => {
    const input = messageInputRef.current
    if (!input) return
    const lineHeight = 24
    const verticalPadding = 16
    const maxHeight = lineHeight * 3 + verticalPadding
    input.style.height = 'auto'
    input.style.height = `${Math.max(44, Math.min(input.scrollHeight, maxHeight))}px`
  }, [inputMessage])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
      if (plusMenuRef.current && !plusMenuRef.current.contains(event.target as Node)) {
        setIsPlusMenuOpen(false)
      }
      if (
        isOfferSelectorOpen
        && offerSelectorRef.current
        && !offerSelectorRef.current.contains(event.target as Node)
      ) {
        setIsOfferSelectorOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOfferSelectorOpen])

  // Clear candidate analysis mode if dropdown closed without selecting
  useEffect(() => {
    if (!isDropdownOpen && isAnalyzeCandidateEnabled && selectedCandidateIds.length === 0) {
      setIsAnalyzeCandidateEnabled(false)
    }
  }, [isDropdownOpen])

  useEffect(() => {
    const limit = selectedOfferIds.length > 0 ? MAX_SELECTED_CANDIDATES_WITH_OFFER : MAX_SELECTED_CANDIDATES
    setSelectedCandidateIds((prev) => (prev.length > limit ? prev.slice(0, limit) : prev))
  }, [selectedOfferIds.length])

  // Load older messages when user scrolls to top
  useEffect(() => {
    if (!currentSession || !hasMoreMessages || isLoadingOlderMessages) return
    const sentinel = olderMessagesSentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreMessages && !isLoadingOlderMessages) {
          const container = userMessagesContainerRef.current || messagesContainerRef.current
          if (container) {
            prevScrollHeightRef.current = container.scrollHeight
          }
          loadOlderMessages()
        }
      },
      { threshold: 0.1, rootMargin: '200px 0px 0px 0px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [currentSession, hasMoreMessages, isLoadingOlderMessages, messages.length])

  // Preserve scroll position after older messages are prepended
  useLayoutEffect(() => {
    if (prevScrollHeightRef.current === 0) return
    const container = userMessagesContainerRef.current || messagesContainerRef.current
    if (!container) return
    const newScrollHeight = container.scrollHeight
    const scrollDiff = newScrollHeight - prevScrollHeightRef.current
    if (scrollDiff > 0) {
      container.scrollTop = scrollDiff + container.scrollTop
    }
    prevScrollHeightRef.current = 0
  }, [messages])

  const scrollToBottom = () => {
    setTimeout(() => {
      const container = messagesContainerRef.current
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      }
      const userContainer = userMessagesContainerRef.current
      if (userContainer) {
        userContainer.scrollTo({ top: userContainer.scrollHeight, behavior: 'smooth' })
      }
    }, 100)
  }

  const openCandidateDropdown = () => {
    setIsPlusMenuOpen(false)
    setIsDropdownOpen(true)
    fetchCandidates(1)
  }

  const loadSessions = async () => {
    const requestToken = ++sessionListTokenRef.current
    setIsLoadingSessions(true)
    setSessionLoadError(null)
    try {
      const sessionsResponse = await aiAssistantApi.getSessions()
      if (requestToken !== sessionListTokenRef.current) return
      const loadedSessions = extractResults<ChatSession>(sessionsResponse.data)
      setSessions(loadedSessions)
      const active = loadedSessions.find((session) => session.id === activeSessionIdRef.current)
      if (active) {
        setCurrentSession((previous) => previous?.id === active.id ? { ...previous, ...active } : previous)
        setContextLimit(active.context_limit || null)
      }
    } catch (error) {
      if (requestToken !== sessionListTokenRef.current) return
      console.error('Error loading sessions:', error)
      setSessionLoadError(t('aiAssistant.loadSessionsError', 'Impossible de charger l’historique des conversations.'))
    } finally {
      if (requestToken === sessionListTokenRef.current) setIsLoadingSessions(false)
    }
  }

  const loadSessionMessages = async (sessionId: number) => {
    if (activeSessionIdRef.current !== sessionId) return
    messageLoadAbortRef.current?.abort()
    const controller = new AbortController()
    messageLoadAbortRef.current = controller
    const requestToken = ++messageLoadTokenRef.current
    loadedSessionIdRef.current = sessionId
    setIsLoadingOlderMessages(false)
    setOlderMessagesError(null)
    setOlderMessagesCursor(null)
    setHasMoreMessages(false)
    setMessageLoadError(null)
    try {
      const response = await aiAssistantApi.getMessages(
        sessionId,
        { limit: MESSAGE_PAGE_SIZE },
        controller.signal,
      )
      if (
        activeSessionIdRef.current !== sessionId
        || loadedSessionIdRef.current !== sessionId
        || messageLoadTokenRef.current !== requestToken
      ) return
      const data = response.data as CursorPaginatedMessages<ChatMessage>
      const serverMessages = data.results || []
      const mapped = mapChatMessages(serverMessages)
      setMessages(mapped)
      setHasMoreMessages(data.has_more)
      setOlderMessagesCursor(data.next_before)
      setContextLimit(data.context_limit || null)
      void hydrateCandidatesForIds(collectCandidateIdsFromMessages(mapped))
      void hydrateOffersForIds(collectOfferIdsFromMessages(mapped))
    } catch (error: any) {
      if (controller.signal.aborted || activeSessionIdRef.current !== sessionId || messageLoadTokenRef.current !== requestToken) return
      setMessages([])
      setMessageLoadError(t('aiAssistant.loadMessagesError', 'Impossible de charger cette conversation. Réessayez.'))
      console.error('Error loading messages:', error)
    }
  }

  const loadOlderMessages = async () => {
    if (isLoadingOlderMessages || !hasMoreMessages || !olderMessagesCursor || !currentSession) return
    setIsLoadingOlderMessages(true)
    setOlderMessagesError(null)
    const sessionId = currentSession.id
    const requestToken = messageLoadTokenRef.current
    olderMessagesAbortRef.current?.abort()
    const controller = new AbortController()
    olderMessagesAbortRef.current = controller
    try {
      const response = await aiAssistantApi.getMessages(sessionId, {
        limit: MESSAGE_PAGE_SIZE,
        before: olderMessagesCursor,
      }, controller.signal)
      if (activeSessionIdRef.current !== sessionId || loadedSessionIdRef.current !== sessionId || messageLoadTokenRef.current !== requestToken) return
      const data = response.data as CursorPaginatedMessages<ChatMessage>
      const serverMessages = data.results || []
      const mapped = mapChatMessages(serverMessages)
      isPrependingRef.current = true
      setMessages(prev => mergePaginatedMessages(mapped, prev))
      setHasMoreMessages(data.has_more)
      setOlderMessagesCursor(data.next_before)
      void hydrateCandidatesForIds(collectCandidateIdsFromMessages(mapped))
      void hydrateOffersForIds(collectOfferIdsFromMessages(mapped))
    } catch (error) {
      if (controller.signal.aborted) return
      if (loadedSessionIdRef.current !== sessionId) return
      setOlderMessagesError(t('aiAssistant.loadOlderError', 'Impossible de charger les anciens messages.'))
      console.error('Error loading older messages:', error)
    } finally {
      if (loadedSessionIdRef.current === sessionId) {
        setIsLoadingOlderMessages(false)
      }
    }
  }

  const handleSelectSession = async (session: ChatSession) => {
    messageLoadAbortRef.current?.abort()
    olderMessagesAbortRef.current?.abort()
    messageLoadTokenRef.current += 1
    sessionEpochRef.current += 1
    activeSessionIdRef.current = session.id
    loadedSessionIdRef.current = session.id
    setCurrentSession(session)
    setContextLimit(session.context_limit || null)
    setDismissedContextWarningKey(null)
    setContinuationError(null)
    setMessages([])
    setHasMoreMessages(false)
    setOlderMessagesCursor(null)
    setOlderMessagesError(null)
    setIsLoadingOlderMessages(false)
    setMessageLoadError(null)
    setIsLoading(false)
    setInputMessage('')
    setIsAnalyzeCandidateEnabled(false)
    setSelectedCandidateIds([])
    setIsDropdownOpen(false)
    setIsPlusMenuOpen(false)
    setActiveStarterAction(null)
    setActiveOfferSubmenu(null)
    setPendingQuickActionId(null)
    setPendingOfferQuickAction(null)
    setShowHistory(false)
    setSelectedOfferIds([])
    setIsOfferSelectorOpen(false)
    setOfferSelectionMode('single')
    setFieldSelections({})
    setConfirmedOffers({})
    setCreatingOfferId(null)
    setCancellingOfferId(null)
    setCopiedMessageId(null)
    setRegeneratingMessageId(null)
    setSelectedGenerationVersions({})
    setSelectedUserMessageId(null)
    setIsMobileUserListOpen(false)
    setEditingMessageId(null)
    setEditedMessageContent('')
    setExecutingOfferActionId(null)
    processedFieldMsgsRef.current.clear()
    isPrependingRef.current = false
    prevScrollHeightRef.current = 0
    latestUserMessageIdRef.current = null
    await loadSessionMessages(session.id)
  }

  const handleContinueInNewSession = async () => {
    if (!currentSession || continuationInFlightRef.current) return
    continuationInFlightRef.current = true
    setIsContinuingSession(true)
    setContinuationError(null)
    try {
      const continuation = await aiAssistantApi.continueSession(currentSession.id)
      const payload = continuation.data
      const sessionResponse = await aiAssistantApi.getSession(payload.new_session_id)
      await handleSelectSession(sessionResponse.data)
      setSelectedCandidateIds(payload.restored_candidate_ids || [])
      setIsAnalyzeCandidateEnabled((payload.restored_candidate_ids || []).length > 0)
      setSelectedOfferIds(payload.restored_offer_ids || [])
      await loadSessions()
    } catch (error) {
      console.error('Unable to continue conversation in a new session:', error)
      setContinuationError(t(
        'aiAssistant.contextContinuationError',
        'Impossible de créer la nouvelle conversation. Veuillez réessayer.'
      ))
    } finally {
      continuationInFlightRef.current = false
      setIsContinuingSession(false)
    }
  }

  const handleNewChat = () => {
    messageLoadAbortRef.current?.abort()
    olderMessagesAbortRef.current?.abort()
    messageLoadTokenRef.current += 1
    sessionEpochRef.current += 1
    activeSessionIdRef.current = null
    loadedSessionIdRef.current = null
    setCurrentSession(null)
    setContextLimit(null)
    setDismissedContextWarningKey(null)
    setContinuationError(null)
    setMessages([])
    setInputMessage('')
    setIsAnalyzeCandidateEnabled(false)
    setSelectedCandidateIds([])
    setIsDropdownOpen(false)
    setIsPlusMenuOpen(false)
    setActiveStarterAction(null)
    setActiveOfferSubmenu(null)
    setPendingQuickActionId(null)
    setPendingOfferQuickAction(null)
    setShowHistory(false)
    setSelectedOfferIds([])
    setIsOfferSelectorOpen(false)
    setOfferSelectionMode('single')
    setHasMoreMessages(false)
    setOlderMessagesCursor(null)
    setOlderMessagesError(null)
    setMessageLoadError(null)
    setIsLoadingOlderMessages(false)
    setIsLoading(false)
    setFieldSelections({})
    setConfirmedOffers({})
    setCreatingOfferId(null)
    setCancellingOfferId(null)
    setCopiedMessageId(null)
    setRegeneratingMessageId(null)
    setSelectedGenerationVersions({})
    setSelectedUserMessageId(null)
    setIsMobileUserListOpen(false)
    setEditingMessageId(null)
    setEditedMessageContent('')
    setExecutingOfferActionId(null)
    processedFieldMsgsRef.current.clear()
    isPrependingRef.current = false
    prevScrollHeightRef.current = 0
    latestUserMessageIdRef.current = null
    setOlderMessagesCursor(null)
    setIsLoadingOlderMessages(false)
    setOlderMessagesError(null)
  }

  const handleDeleteSession = async (sessionId: number, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent session selection when clicking delete

    try {
      await aiAssistantApi.deleteSession(sessionId)

      // Remove session from list
      setSessions(prev => prev.filter(s => s.id !== sessionId))

      // If deleted session was current session, clear it
      if (activeSessionIdRef.current === sessionId) {
        handleNewChat()
      }
    } catch (error) {
      console.error('Error deleting session:', error)
    }
  }

  const handleStartRename = (session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingSessionId(session.id)
    setEditingTitle(session.title || `Conversation ${session.id}`)
  }

  const handleCancelRename = () => {
    setEditingSessionId(null)
    setEditingTitle('')
  }

  const handleSaveRename = async (sessionId: number, e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation()

    if (!editingTitle.trim()) {
      alert(t('aiAssistant.emptyTitleError'))
      return
    }

    const requestedTitle = editingTitle.trim()
    try {
      await aiAssistantApi.updateSession(sessionId, { title: requestedTitle })

      // Update session in list
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, title: requestedTitle } : s
      ))

      // Update current session if it's the one being edited
      if (activeSessionIdRef.current === sessionId) {
        setCurrentSession(prev => prev?.id === sessionId ? { ...prev, title: requestedTitle } : prev)
      }

      setEditingSessionId(null)
      setEditingTitle('')
    } catch (error) {
      console.error('Error renaming session:', error)
      alert(t('aiAssistant.renameError'))
    }
  }

  const handleRenameKeyPress = (sessionId: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveRename(sessionId, e)
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  const getMessageContextMetadata = (message: Message): ClientMessageMetadata => {
    const source = message.metadata || {}
    const preservedKeys = [
      'candidate_id',
      'candidate_ids',
      'selected_fields',
      'quick_action_id',
      'offer_id',
      'offer_ids',
      'ui_form_display',
    ]

    return preservedKeys.reduce<Record<string, any>>((metadata, key) => {
      if (source[key] !== undefined) metadata[key] = source[key]
      return metadata
    }, {})
  }

  const handleSendMessage = async (messageText?: string, messageMetadata?: ClientMessageMetadata) => {
    const text = messageText ?? inputMessage
    if (!text.trim() || isLoading) return

    const metadataCandidateIds = normalizeCandidateIds(messageMetadata?.candidate_ids)
    const metadataCandidateId = messageMetadata?.candidate_id ? Number(messageMetadata.candidate_id) : null
    const candidateIdsFromMetadata = metadataCandidateIds.length > 0
      ? metadataCandidateIds
      : (metadataCandidateId ? [metadataCandidateId] : [])
    const usesComposerCandidates = candidateIdsFromMetadata.length === 0 && isAnalyzeCandidateEnabled && selectedCandidateIds.length > 0
    const candidateIds = candidateIdsFromMetadata.length > 0
      ? candidateIdsFromMetadata
      : (usesComposerCandidates ? [...selectedCandidateIds] : [])
    const metadataOfferIds = normalizeCandidateIds(messageMetadata?.offer_ids)
    const metadataOfferId = messageMetadata?.offer_id ? Number(messageMetadata.offer_id) : null
    const normalizedOfferIds = (metadataOfferIds.length > 0
      ? metadataOfferIds
      : (metadataOfferId ? [metadataOfferId] : selectedOfferIds)
    ).slice(0, MAX_SELECTED_OFFERS)
    const outgoingMetadata: ClientMessageMetadata = {
      ...(messageMetadata || {}),
      ...(!messageMetadata?.quick_action_id && pendingQuickActionId
        ? { quick_action_id: pendingQuickActionId }
        : {}),
      ...(candidateIds.length > 0
        ? { candidate_id: candidateIds[0], candidate_ids: candidateIds }
        : {}),
      ...(normalizedOfferIds.length > 0
        ? { offer_id: normalizedOfferIds[0], offer_ids: normalizedOfferIds }
        : {}),
      ...(regenerationCurrentDraft?.data
        ? {
            resume_draft: regenerationCurrentDraft.data,
            resume_draft_message_id: regenerationCurrentDraft.messageId,
          }
        : {}),
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date(),
      candidateId: candidateIds.length > 0 ? candidateIds[0] : null,
      candidateIds: candidateIds,
      offerId: normalizedOfferIds[0] ?? null,
      metadata: outgoingMetadata,
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setPendingQuickActionId(null)
    setSelectedOfferIds([])
    setIsOfferSelectorOpen(false)
    setIsDropdownOpen(false)
    let sessionForRefresh: ChatSession | null = currentSession
    const viewToken = sessionEpochRef.current
    let operationSessionId = currentSession?.id ?? null
    const setScopedLoading = (loading: boolean) => {
      if (isCurrentSessionRequest(operationSessionId, viewToken)) setIsLoading(loading)
    }
    try {
      await withGenerationLoading(setScopedLoading, async () => {
        let session = currentSession

        if (!session) {
          const sessionTitle = text.length > 60 ? `${text.slice(0, 57)}...` : text
          const currentLanguage = i18n.language.startsWith('fr') ? 'fr' : 'en'
          const sessionResponse = await aiAssistantApi.createSession(sessionTitle, currentLanguage)
          session = sessionResponse.data
          const keepNewSessionVisible = isCurrentSessionRequest(null, viewToken)
          operationSessionId = session.id
          if (keepNewSessionVisible) {
            activeSessionIdRef.current = session.id
            loadedSessionIdRef.current = session.id
            setCurrentSession(session)
          }
          setSessions((prev) => [session as ChatSession, ...prev])
        }
        sessionForRefresh = session

        const metadata = Object.keys(outgoingMetadata).length > 0 ? outgoingMetadata : undefined
        await aiAssistantApi.sendMessage(session.id, text, metadata)
        if (usesComposerCandidates && isCurrentSessionRequest(operationSessionId, viewToken)) {
          setSelectedCandidateIds([])
          setIsAnalyzeCandidateEnabled(false)
          setIsDropdownOpen(false)
        }
      })
    } catch (error: any) {
      console.error('Error sending message:', error)
      const limit = error?.response?.data?.context_limit
      if (limit?.level === 'hard') {
        setContextLimit(limit)
        setContinuationError(null)
      } else {
        addToast(
          t(
            'aiAssistant.generationRequestFailed',
            'La génération n’a pas pu se terminer. Veuillez réessayer.',
          ),
          'error',
        )
      }
    }
    if (sessionForRefresh) {
      try {
        // The POST creates both the user message and the eventual AI reply.
        // Load messages deterministically before refreshing the sidebar so a
        // slow generation/error cannot finish with only the optimistic user
        // bubble visible.
        await loadSessionMessages(sessionForRefresh.id)
      } catch (error) {
        console.error('AI assistant messages refresh failed:', error)
      }
      startNonCriticalRefreshes([
        { name: 'sessions', run: loadSessions },
      ])
    }
  }

  const primaryOfferQuickActions = useMemo(() => getPrimaryOfferQuickActions({
    offerCount: selectedOfferIds.length,
    offerStatus: selectedOffers[0]?.status,
    t,
    canViewOffers: hasPermission('view_offers'),
    canCreateOffers: hasPermission('create_offers'),
    canEditOffers: hasPermission('edit_offers'),
  }), [selectedOfferIds.length, selectedOffers, i18n.language, hasPermission])

  const offerEditQuickActions = useMemo(() => getOfferEditQuickActions({
    offerCount: selectedOfferIds.length,
    offerStatus: selectedOffers[0]?.status,
    t,
    allowedActions: selectedOffers[0]?.allowed_actions,
    canEditOffers: hasPermission('edit_offers'),
  }), [selectedOfferIds.length, selectedOffers, i18n.language, hasPermission])

  const handleOfferQuickAction = (action: OfferQuickAction) => {
    if (action.disabled || isLoading) return
    if (action.id === 'offer-edit-menu') {
      setActiveOfferSubmenu(null)
      setSelectedOfferIds([])
      setPendingOfferQuickAction(action)
      setOfferSelectionMode('single')
      openOfferDropdown()
      return
    }
    if (action.id === 'offer-create') {
      handleSendMessage(action.prompt, {
        quick_action_id: action.id,
        ...(action.uiEvent ? { ui_event: action.uiEvent } : {}),
      })
      return
    }
    const needsSingle = action.selection === 'single' && selectedOfferIds.length !== 1
    if (needsSingle) {
      setPendingOfferQuickAction(action)
      setOfferSelectionMode('single')
      openOfferDropdown()
      return
    }
    if (action.requiresCandidate && selectedCandidateIds.length !== 1) {
      setPendingOfferQuickAction(action)
      setIsAnalyzeCandidateEnabled(true)
      openCandidateDropdown()
      return
    }
    if (action.id === 'offer-update') {
      setPendingOfferQuickAction(null)
      handleSendMessage(action.prompt, {
        quick_action_id: action.id,
        ...(action.uiEvent ? {
          ui_event: { ...action.uiEvent, offer_id: selectedOfferIds[0] },
        } : {}),
        offer_id: selectedOfferIds[0],
        offer_ids: selectedOfferIds,
      })
      return
    }
    setPendingOfferQuickAction(null)
    handleSendMessage(action.prompt, {
      quick_action_id: action.id,
      ...(action.uiEvent ? {
        ui_event: { ...action.uiEvent, offer_id: selectedOfferIds[0] },
      } : {}),
      offer_id: selectedOfferIds[0],
      offer_ids: selectedOfferIds,
    })
  }

  useEffect(() => {
    if (!pendingOfferQuickAction) return
    const ready = pendingOfferQuickAction.selection === 'none' || selectedOfferIds.length === 1
    if (!ready) return
    const action = pendingOfferQuickAction
    setPendingOfferQuickAction(null)
    setIsOfferSelectorOpen(false)
    if (action.id === 'offer-edit-menu') {
      setActiveOfferSubmenu('edit')
      return
    }
    handleOfferQuickAction(action)
    // The selected IDs are the trigger; the handler reads the latest render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOfferIds])

  useEffect(() => {
    if (!pendingOfferQuickAction?.requiresCandidate || selectedCandidateIds.length !== 1) return
    if (pendingOfferQuickAction.selection === 'single' && selectedOfferIds.length !== 1) return
    const action = pendingOfferQuickAction
    setPendingOfferQuickAction(null)
    setIsDropdownOpen(false)
    handleOfferQuickAction(action)
    // The selected candidate ID is the trigger; the handler reads the latest render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCandidateIds])

  const handlePendingOfferAction = async (message: Message, operation: 'confirm' | 'cancel') => {
    if (!currentSession || !message.metadata?.pending_action_id || executingOfferActionId) return
    const pendingId = String(message.metadata.pending_action_id)
    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    setExecutingOfferActionId(pendingId)
    try {
      const response = operation === 'confirm'
        ? await aiAssistantApi.confirmOfferAction(pendingId, sessionId)
        : await aiAssistantApi.cancelOfferAction(pendingId, sessionId)
      if (operation === 'confirm') {
        const offerId = Number(response.data.offer_id)
        if (response.data.deleted && isCurrentSessionRequest(sessionId, viewToken)) {
          setSelectedOfferIds((prev) => prev.filter((id) => id !== offerId))
          setOffers((prev) => prev.filter((offer) => offer.id !== offerId))
          setOfferCache((prev) => {
            const next = { ...prev }
            delete next[offerId]
            return next
          })
        } else if (response.data.offer) {
          const updated = response.data.offer as OfferSummary
          setOfferCache((prev) => ({ ...prev, [updated.id]: updated }))
          setOffers((prev) => prev.map((offer) => offer.id === updated.id ? { ...offer, ...updated } : offer))
        }
      }
      await loadSessionMessages(sessionId)
    } catch (error: any) {
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        addToast(error?.response?.data?.detail || error?.response?.data?.error || t('common.error', 'Une erreur est survenue.'), 'error')
      }
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) setExecutingOfferActionId(null)
    }
  }

  const handleRegenerationDraftChange = useCallback((messageId: string, data: Record<string, any>) => {
    setRegenerationCurrentDraft((previous) => {
      if (
        previous?.messageId === messageId
        && JSON.stringify(previous.data) === JSON.stringify(data)
      ) {
        return previous
      }
      return { messageId, data }
    })
  }, [])

  const handleRegenerationCommand = async (
    message: Message,
    command: 'select_mode' | 'retry_analysis' | 'regenerate_again' | 'refine' | 'apply_draft',
    label: string,
    mode?: string,
    draftValues?: Record<string, unknown>,
  ) => {
    const offerId = Number(message.metadata?.offer_id)
    if (!offerId || isLoading) return
    await handleSendMessage(label, {
      ui_event: {
        type: 'regeneration_command',
        action: 'offer.regenerate',
        offer_id: offerId,
        regeneration_command: command,
        ...(mode ? { regeneration_mode: mode } : {}),
        ...(draftValues ? { draft_values: draftValues } : {}),
      },
      regeneration_command: command,
      ...(mode ? { regeneration_mode: mode } : {}),
      ...(draftValues ? { draft_values: draftValues } : {}),
      offer_id: offerId,
      offer_ids: [offerId],
    })
  }

  const handleCopyMessage = async (message: Message) => {
    const { cleaned } = resolveOfferDataForMessage(message)
    const copyText = cleaned || message.content

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyText)
      } else {
        const temporaryTextarea = document.createElement('textarea')
        temporaryTextarea.value = copyText
        temporaryTextarea.style.position = 'fixed'
        temporaryTextarea.style.opacity = '0'
        document.body.appendChild(temporaryTextarea)
        temporaryTextarea.select()
        document.execCommand('copy')
        document.body.removeChild(temporaryTextarea)
      }
      setCopiedMessageId(message.id)
      window.setTimeout(() => setCopiedMessageId((current) => current === message.id ? null : current), 1800)
    } catch (error) {
      console.error('Error copying AI response:', error)
    }
  }

  const handleRegenerateResponse = async (message: Message, messageIndex: number) => {
    if (!currentSession || isLoading || regeneratingMessageId) return

    const sourceMessage = [...messages.slice(0, messageIndex)]
      .reverse()
      .find((entry) => entry.type === 'user' && !entry.metadata?.technical && !entry.metadata?.hidden)
    const responseId = Number(message.id)
    if (!sourceMessage || !Number.isFinite(responseId)) return
    const generationGroupId = getGenerationGroupId(message)
    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    const generationVersions = buildGenerationGroups(messages)[generationGroupId] || [message]
    const nextGenerationVersion = Math.max(...generationVersions.map(getGenerationVersion)) + 1

    setRegeneratingMessageId(message.id)
    setIsLoading(true)
    try {
      await aiAssistantApi.sendMessage(sessionId, sourceMessage.content, {
        ...getMessageContextMetadata(sourceMessage),
        regeneration_for: responseId,
      })
      await aiAssistantApi.registerMessageVersion(sessionId, responseId)
      await loadSessionMessages(sessionId)
      await loadSessions()
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        setSelectedGenerationVersions((previous) => ({
          ...previous,
          [generationGroupId]: nextGenerationVersion,
        }))
      }
    } catch (error) {
      console.error('Error regenerating AI response:', error)
      await loadSessionMessages(sessionId)
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        setRegeneratingMessageId(null)
        setIsLoading(false)
      }
    }
  }

  const handleStartEditingMessage = (message: Message) => {
    setEditingMessageId(message.id)
    setEditedMessageContent(message.content)
  }

  const handleCancelEditingMessage = () => {
    setEditingMessageId(null)
    setEditedMessageContent('')
  }

  const handleResendEditedMessage = async (message: Message) => {
    const revisedPrompt = editedMessageContent.trim()
    const sourceMessageId = Number(message.id)
    if (!revisedPrompt || isLoading || !Number.isFinite(sourceMessageId)) return

    // An edited prompt starts a replacement branch. Remove the old prompt and
    // everything generated after it immediately; the server preserves those
    // records as hidden audit history and returns the new branch on refresh.
    setMessages((previous) => {
      const sourceIndex = previous.findIndex((entry) => entry.id === message.id)
      return sourceIndex >= 0 ? previous.slice(0, sourceIndex) : previous
    })
    handleCancelEditingMessage()
    await handleSendMessage(revisedPrompt, {
      ...getMessageContextMetadata(message),
      edit_of_message_id: sourceMessageId,
    })
  }

  const handleConfirmFields = async (messageId: string, fields: string[]) => {
    if (!currentSession || isLoading) return
    if (fields.length === 0) return

    const friendlyMessage = t('aiAssistant.fieldsSelected', 'Vous avez choisi: {{fields}}', {
      fields: fields.join(', ')
    })
    const lastCandidateId = [...messages]
      .reverse()
      .find((message) => message.type === 'user' && message.candidateId)?.candidateId || null

    const metadata: ClientMessageMetadata = {
      selection_for: Number(messageId),
      friendly_message: friendlyMessage
    }

    if (lastCandidateId) {
      metadata.candidate_id = lastCandidateId
    }

    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    setIsLoading(true)
    try {
      await aiAssistantApi.sendMessage(sessionId, formatFieldsAsPythonList(fields), metadata)
      await loadSessionMessages(sessionId)
      await loadSessions()
    } catch (error) {
      console.error('Error sending field selection:', error)
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) setIsLoading(false)
    }
  }


  const handleConfirmCreateOffer = async (messageId: string, offerData: Record<string, any>) => {
    if (isLoading || creatingOfferId || !currentSession) return

    setCreatingOfferId(messageId)
    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    try {
      // Explicit UI edits become the trusted proposal before final creation.
      // This preserves the server-side exact-match guard on the create endpoint.
      await aiAssistantApi.updateOfferProposal(sessionId, messageId, offerData)
      // Confirmation hands the trusted proposal to the established offer
      // enrichment pipeline before serializer validation and persistence.
      const response = await aiAssistantApi.createOfferFromChat(offerData, true, sessionId, messageId)
      const result = response.data
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        setConfirmedOffers((prev) => ({
          ...prev,
          [messageId]: { offerId: result.offer_id, status: result.status }
        }))
        // Reflect confirmation on the same résumé message (no new chat turn).
        setMessages((prev) => prev.map((entry) => {
          if (String(entry.id) !== String(messageId)) return entry
          return {
            ...entry,
            metadata: {
              ...(entry.metadata || {}),
              offer_created: true,
              created_offer_id: result.offer_id,
              offer_id: result.offer_id,
              job_title: result.job_title,
              offer_status: result.status,
            },
          }
        }))
      }

      await loadSessionMessages(sessionId)
      await loadSessions()
    } catch (error: any) {
      console.error('Error creating offer from chat:', error)
      const responseData = error.response?.data || {}
      const validationDetails = formatApiValidationDetails(responseData.details)
      const failureReason = [responseData.error || error.message, validationDetails].filter(Boolean).join('\n\n')
      // Surface errors as a toast — Valider must not append a chat message.
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        addToast(
          failureReason || t('aiAssistant.offerCreationFailedDesc', 'Une erreur est survenue. Veuillez réessayer.'),
          'error',
        )
      }
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) setCreatingOfferId(null)
    }
  }

  const handleRefineOfferProposalField = async (
    messageId: string,
    offerData: Record<string, any>,
    field: string,
    instruction: string,
  ) => {
    if (!currentSession) throw new Error('No active chat session')
    const response = await aiAssistantApi.refineOfferProposalField(
      currentSession.id,
      messageId,
      offerData,
      field,
      instruction,
    )
    setContextInspectorEpoch((epoch) => epoch + 1)
    return response.data.value
  }

  // Manual edits on a saved offer apply immediately on Valider (no confirmation
  // chat turn). The résumé card stays on the same message and becomes a link.
  const handleSaveExistingOfferEdits = async (
    offerId: number,
    offerData: Record<string, any>,
    sourceMessageId?: string,
  ) => {
    if (!currentSession) throw new Error('No active chat session')
    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    try {
      const response = await aiAssistantApi.prepareManualOfferUpdate(
        sessionId,
        offerId,
        offerData,
        sourceMessageId,
      )
      const resultOfferId = Number(response.data.offer_id || offerId)
      if (sourceMessageId && isCurrentSessionRequest(sessionId, viewToken)) {
        setConfirmedOffers((prev) => ({
          ...prev,
          [sourceMessageId]: { offerId: resultOfferId, status: 'updated' },
        }))
        setMessages((prev) => prev.map((entry) => {
          if (String(entry.id) !== String(sourceMessageId)) return entry
          return {
            ...entry,
            metadata: {
              ...(entry.metadata || {}),
              offer_action_applied: true,
              action: 'offer_update',
              offer_id: resultOfferId,
              edit_state: 'APPLIED',
            },
          }
        }))
      }
      await loadSessionMessages(sessionId)
      await loadSessions()
    } catch (error: any) {
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        const responseData = error?.response?.data
        const body = responseData && typeof responseData === 'object' ? responseData : {}
        addToast(
          formatApiValidationDetails(body.detail)
          || formatApiValidationDetails(body.offer_data)
          || formatApiValidationDetails(body.error)
          // Any other serializer or lifecycle rejection, e.g. a non-draft offer.
          || formatApiValidationDetails(body)
          || t('common.error', 'Une erreur est survenue.'),
          'error',
        )
      }
      throw error
    }
  }

  const handleApplyRegenerationDraft = async (
    message: Message,
    offerId: number,
    draftValues: Record<string, any>,
  ) => {
    if (!currentSession || isLoading) return
    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    setIsLoading(true)
    try {
      const response = await aiAssistantApi.applyRegenerationDraft(
        sessionId,
        offerId,
        draftValues,
        String(message.id),
      )
      const resultOfferId = Number(response.data.offer_id)
      if (isCurrentSessionRequest(sessionId, viewToken) && Number.isFinite(resultOfferId) && resultOfferId > 0) {
        setConfirmedOffers((prev) => ({
          ...prev,
          [message.id]: { offerId: resultOfferId, status: 'regenerated' },
        }))
        setMessages((prev) => prev.map((entry) => {
          if (String(entry.id) !== String(message.id)) return entry
          return {
            ...entry,
            metadata: {
              ...(entry.metadata || {}),
              offer_action_applied: true,
              action: 'offer_regenerate',
              offer_id: resultOfferId,
              created_new_offer: true,
              regeneration_state: {
                ...((entry.metadata?.regeneration_state as Record<string, any>) || {}),
                status: 'CONFIRMED',
              },
            },
          }
        }))
      }
      await loadSessionMessages(sessionId)
      await loadSessions()
    } catch (error: any) {
      if (isCurrentSessionRequest(sessionId, viewToken)) {
        const responseData = error?.response?.data
        const body = responseData && typeof responseData === 'object' ? responseData : {}
        addToast(
          formatApiValidationDetails(body.detail)
          || formatApiValidationDetails(body.draft_values)
          || formatApiValidationDetails(body.error)
          || formatApiValidationDetails(body)
          || t('common.error', 'Une erreur est survenue.'),
          'error',
        )
      }
      throw error
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) setIsLoading(false)
    }
  }

  const handleRefineExistingOfferField = async (
    offerId: number,
    offerData: Record<string, any>,
    field: string,
    instruction: string,
  ) => {
    if (!currentSession) throw new Error('No active chat session')
    const response = await aiAssistantApi.refineOfferField(
      currentSession.id,
      offerId,
      offerData,
      field,
      instruction,
    )
    setContextInspectorEpoch((epoch) => epoch + 1)
    return response.data.value
  }

  const handleSubmitDynamicForm = async (
    messageId: string,
    form: DynamicFormSchema,
    answers: DynamicFormAnswers
  ) => {
    if (!currentSession || isLoading) return

    const friendlyMessage = i18n.language.startsWith('fr')
      ? `Formulaire « ${form.title} » envoyé.`
      : `Form “${form.title}” submitted.`
    const metadata = {
      form_for: Number(messageId),
      friendly_message: friendlyMessage,
      ui_form_submission: {
        form_id: form.form_id,
        answers,
      },
    }

    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    setIsLoading(true)
    try {
      await aiAssistantApi.sendMessage(
        sessionId,
        `UI form submitted: ${form.form_id}`,
        metadata
      )
      await loadSessionMessages(sessionId)
      await loadSessions()
    } catch (error) {
      console.error('Error submitting dynamic form:', error)
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) setIsLoading(false)
    }
  }

  const handleCancelOfferProposal = async (messageId: string) => {
    if (!currentSession || isLoading || cancellingOfferId) return
    setCancellingOfferId(messageId)
    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    try {
      await aiAssistantApi.cancelOfferProposal(sessionId, messageId)
      await loadSessionMessages(sessionId)
      await loadSessions()
    } catch (error) {
      console.error('Error cancelling offer proposal:', error)
    } finally {
      if (isCurrentSessionRequest(sessionId, viewToken)) setCancellingOfferId(null)
    }
  }

  const handleRetryFailedRequest = async (errorMessage: Message) => {
    if (!currentSession || isLoading) return

    const retryPayload = errorMessage.metadata?.retry_payload || {}
    const retryContent = retryPayload.content
    const retryMetadata = retryPayload.metadata
    const errorIndex = messages.findIndex((message) => message.id === errorMessage.id)
    const originalUserMessage = errorIndex >= 0
      ? [...messages.slice(0, errorIndex)].reverse().find((message) => (
        message.type === 'user' && !message.metadata?.hidden && !message.metadata?.technical
      ))
      : undefined
    const originalUserMessageId = Number(
      retryMetadata?.retry_of_message_id || originalUserMessage?.id,
    )

    if (!retryContent || typeof retryContent !== 'string' || !Number.isFinite(originalUserMessageId)) return

    const sessionId = currentSession.id
    const viewToken = sessionEpochRef.current
    const setScopedLoading = (loading: boolean) => {
      if (isCurrentSessionRequest(sessionId, viewToken)) setIsLoading(loading)
    }
    try {
      await withGenerationLoading(setScopedLoading, () => {
        return aiAssistantApi.sendMessage(sessionId, retryContent, {
          ...(originalUserMessage ? getMessageContextMetadata(originalUserMessage) : {}),
          retry_of_message_id: originalUserMessageId,
          retry_of_generation_id: retryMetadata?.retry_of_generation_id || errorMessage.metadata?.generation_id,
          retry_attempt: Number(retryMetadata?.retry_attempt || 1),
        })
      })
    } catch (error) {
      console.error('Error retrying failed AI request:', error)
    }
    startNonCriticalRefreshes([
      { name: 'messages', run: () => loadSessionMessages(sessionId) },
      { name: 'sessions', run: loadSessions },
    ])
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const normalizedHistorySearch = historySearch.trim().toLowerCase()
  const filteredSessions = normalizedHistorySearch
    ? sessions.filter((session) =>
      (session.title || `${t('aiAssistant.conversation')} ${session.id}`)
        .toLowerCase()
        .includes(normalizedHistorySearch)
    )
    : sessions

  const getHistorySection = (updatedAt: string) => {
    const date = new Date(updatedAt)
    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const diffDays = Math.floor((startOfToday - startOfDate) / 86400000)

    if (diffDays <= 0) return t('aiAssistant.today', 'Today')
    if (diffDays === 1) return t('aiAssistant.yesterday', 'Yesterday')
    if (diffDays <= 7) return t('aiAssistant.previous7Days', 'Previous 7 days')
    return t('aiAssistant.older', 'Older')
  }

  const starterActions = [
    { id: 'analyze', label: t('aiAssistant.analyzeCandidates'), icon: Users, prompt: t('aiAssistant.quickActionPrompts.analyzeCandidates') },
    { id: 'compare', label: t('aiAssistant.compareProfiles'), icon: Scale, prompt: t('aiAssistant.quickActionPrompts.compareProfiles') },
    { id: 'regenerate-offer', label: t('aiAssistant.offerActions.regenerate'), icon: RefreshCw, prompt: t('aiAssistant.offerActions.prompts.regenerate') },
    { id: 'edit-offer', label: t('aiAssistant.editOfferStarter', 'Modifier une offre'), icon: Pencil, prompt: t('aiAssistant.offerActions.prompts.update') },
    { id: 'create-offer', label: t('aiAssistant.createOfferShortcut'), icon: FileText, prompt: t('aiAssistant.quickActionPrompts.createOffer') },
  ]

  const candidateAnalysisStarterActions = [
    {
      id: 'complete-profile-analysis',
      label: t('aiAssistant.completeProfileAnalysis'),
      prompt: i18n.language.startsWith('fr')
        ? 'Analyse ce candidat de manière complète : parcours, compétences, forces, points de vigilance et recommandation.'
        : 'Analyze this candidate comprehensively: career path, skills, strengths, points requiring attention, and recommendation.',
    },
    {
      id: 'technical-skills-analysis',
      label: t('aiAssistant.technicalSkillsAnalysis'),
      prompt: i18n.language.startsWith('fr')
        ? 'Analyse les compétences techniques déclarées par ce candidat et leur cohérence avec son expérience.'
        : 'Analyze the technical skills declared by this candidate and how consistently they align with their experience.',
    },
  ]

  const candidateComparisonStarterActions = [
    {
      id: 'complete-comparison',
      label: t('aiAssistant.completeCandidateComparison'),
      prompt: i18n.language.startsWith('fr')
        ? 'Compare ces candidats de manière complète sur leur parcours, expérience, compétences, forces et points à clarifier.'
        : 'Compare these candidates comprehensively across their career paths, experience, skills, strengths, and points requiring clarification.',
    },
    {
      id: 'experience-and-seniority',
      label: t('aiAssistant.experienceAndSeniorityComparison'),
      prompt: i18n.language.startsWith('fr')
        ? "Compare le niveau d'expérience et de séniorité de ces candidats à partir des informations disponibles."
        : 'Compare the experience and seniority levels of these candidates using the available information.',
    },
    {
      id: 'custom-comparison',
      label: t('aiAssistant.customCandidateComparison'),
      prompt: i18n.language.startsWith('fr')
        ? 'Aide-moi à comparer ces candidats selon des critères personnalisés. Demande-moi les critères et les informations manquantes avant de réaliser la comparaison.'
        : 'Help me compare these candidates using custom criteria. Ask me for the criteria and any missing information before performing the comparison.',
    },
  ]

  const selectableUserMessages = useMemo(
    () => messages.filter(isSelectableUserMessage),
    [messages],
  )
  const selectedTurnAiIds = useMemo(
    () => collectTurnAiMessageIds(messages, selectedUserMessageId),
    [messages, selectedUserMessageId],
  )
  const hasUserMessages = selectableUserMessages.length > 0
  const selectedUserMessage = useMemo(
    () => selectableUserMessages.find((message) => message.id === selectedUserMessageId) ?? null,
    [selectableUserMessages, selectedUserMessageId],
  )
  const latestSelectableUserMessageId = selectableUserMessages[selectableUserMessages.length - 1]?.id ?? null
  const isLatestUserSelected = !selectedUserMessageId || selectedUserMessageId === latestSelectableUserMessageId
  const generationGroups = useMemo(() => buildGenerationGroups(messages), [messages])
  const latestRegeneratableGenerationGroupId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index]
      if (
        message.type !== 'ai'
        || message.metadata?.hidden
        || message.metadata?.system
        || message.metadata?.context_handoff
        || (message.metadata?.superseded && !message.metadata?.generation_group_id)
      ) continue
      return getGenerationGroupId(message)
    }
    return null
  }, [messages])

  if (isLoadingStatus) {
    return (
      <div className="flex h-[calc(100vh_-_8rem)] items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          {initError ? (
            <>
              <p className="text-sm text-destructive max-w-md text-center">{initError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInitError(null)
                  setIsLoadingStatus(true)
                  initializeAI()
                }}
              >
                {t('common.retry', 'Retry')}
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">{t('aiAssistant.initialization')}</span>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 overflow-hidden">
      <div className="relative flex min-h-0 min-w-0 max-w-full flex-1 items-stretch overflow-hidden">
        {/* Mobile History Overlay */}
        {showHistory && (
          <div
            className="fixed inset-0 z-[45] bg-black/40 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowHistory(false)}
          />
        )}

        <div className={`order-1 flex h-full w-0 min-h-0 min-w-0 max-w-full flex-1 flex-col pr-[4.5rem] [contain:inline-size] ${hasUserMessages ? 'overflow-hidden' : 'overflow-visible'}`}>
          <Card className="flex h-full w-full min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden border-0 bg-transparent shadow-none">
            <CardHeader className="hidden">
              <CardTitle>{t('aiAssistant.chatTitle')}</CardTitle>
              {messages.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNewChat}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('aiAssistant.newConversation')}
                </Button>
              )}
            </CardHeader>
            <CardContent className={`flex h-full w-full min-h-0 min-w-0 max-w-full flex-1 flex-col px-0 py-0 ${hasUserMessages ? 'overflow-hidden' : 'overflow-visible'}`}>
              {/* Messages */}
              <div className={`relative flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 ${hasUserMessages ? 'flex-col overflow-hidden lg:flex-row' : 'flex-col items-center justify-center overflow-visible'}`}>
              <div ref={messagesContainerRef} className={hasUserMessages
                ? 'min-h-0 w-full min-w-0 max-w-full flex-1 space-y-4 overflow-x-hidden overflow-y-auto px-3 py-2 sm:px-4 lg:h-full lg:w-[70%] lg:flex-none lg:px-4 lg:py-1 2xl:pr-6 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] dark:[scrollbar-color:#475569_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 hover:[&::-webkit-scrollbar-thumb]:bg-slate-400 dark:[&::-webkit-scrollbar-thumb]:bg-slate-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-slate-500'
                : 'w-full max-w-5xl shrink-0 space-y-4 overflow-x-hidden px-4 py-2 sm:px-8'
              }>
                {!hasUserMessages && (
                <section className="relative mb-2 flex min-h-[220px] items-center overflow-hidden rounded-[24px] px-4 py-6 sm:min-h-[280px] sm:rounded-[28px] sm:px-6 sm:py-7 md:min-h-[300px] lg:min-h-[320px] lg:px-10">
                  <HeroBackgroundIllustration />
                  <div className={`relative z-20 min-w-0 max-w-[650px] ${showHistory ? 'xl:w-[52%]' : 'xl:w-[56%]'}`}>
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-cyan-50 px-4 py-2 text-sm font-semibold text-teal-600 dark:bg-cyan-950/40 dark:text-cyan-300">
                      <Sparkles className="h-4 w-4" /> {t('aiAssistant.heroBadge')}
                    </div>
                    <h2 className={`max-w-2xl font-extrabold leading-[1.12] tracking-tight text-slate-950 dark:text-white ${showHistory ? 'text-2xl xl:text-[28px] 2xl:text-[32px]' : 'text-2xl sm:text-3xl xl:text-[36px]'}`}>
                      {t('aiAssistant.heroTitleLine1')}<br />
                      {t('aiAssistant.heroTitleLine2')}<br />
                      <span className="text-[rgb(25,166,166)]">{t('aiAssistant.heroTitleLine3')}</span>
                    </h2>
                    <p className="mt-4 max-w-xl text-[15px] leading-6 text-slate-500 dark:text-slate-400">
                      {t('aiAssistant.heroDescriptionLine1')}<br className="hidden sm:block" /> {t('aiAssistant.heroDescriptionLine2')}
                    </p>
                  </div>
                </section>
                )}
                {messageLoadError && currentSession && (
                  <div className="flex items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
                    <span className="text-destructive">{messageLoadError}</span>
                    <button
                      type="button"
                      className="font-semibold text-primary underline"
                      onClick={() => loadSessionMessages(currentSession.id)}
                    >
                      {t('common.retry', 'Réessayer')}
                    </button>
                  </div>
                )}
                {currentSession && contextLimit && contextLimit.level !== 'safe' && (() => {
                  const warningKey = `${currentSession.id}:${contextLimit.level}:${contextLimit.usage_percent}`
                  if (contextLimit.level !== 'hard' && dismissedContextWarningKey === warningKey) return null
                  const critical = contextLimit.level === 'critical' || contextLimit.level === 'hard'
                  return (
                    <div role="alert" aria-live="assertive" className={`sticky top-2 z-40 mx-1 rounded-2xl border p-4 shadow-sm ${critical
                      ? 'border-amber-400/70 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'
                      : 'border-cyan-300/70 bg-cyan-50 text-cyan-950 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-100'
                      }`}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${critical ? 'text-amber-600' : 'text-cyan-600'}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold">
                            {contextLimit.level === 'hard'
                              ? t('aiAssistant.contextHardTitle', 'Limite de contexte atteinte')
                              : contextLimit.level === 'critical'
                                ? t('aiAssistant.contextCriticalTitle', 'Cette conversation est presque à sa limite de contexte')
                                : t('aiAssistant.contextWarningTitle', 'Cette conversation approche de sa limite de contexte')}
                          </p>
                          <p className="mt-1 text-sm opacity-80">
                            {contextLimit.level === 'hard'
                              ? t('aiAssistant.contextHardDescription', 'La prochaine génération ne peut pas tenir en toute sécurité. Continuez dans une nouvelle conversation avec le résumé automatique.')
                              : t('aiAssistant.contextWarningDescription', 'Vous pouvez continuer dans une nouvelle conversation avec un résumé automatique.')}
                            {' '}({contextLimit.usage_percent}%)
                          </p>
                          {continuationError && <p className="mt-2 text-sm font-medium text-destructive">{continuationError}</p>}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button size="sm" onClick={handleContinueInNewSession} disabled={isContinuingSession}>
                              {isContinuingSession ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                              {t('aiAssistant.continueNewSession', 'Continuer dans une nouvelle conversation')}
                            </Button>
                            {contextLimit.can_continue_here ? (
                              <Button size="sm" variant="outline" onClick={() => setDismissedContextWarningKey(warningKey)}>
                                {t('aiAssistant.continueHere', 'Continuer ici')}
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => setInputMessage('')}>
                                {t('common.cancel', 'Annuler')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })()}
                {!hasUserMessages && messages.length > 0 && hasMoreMessages && (
                  <div ref={olderMessagesSentinelRef} className="flex items-center justify-center py-2">
                    {isLoadingOlderMessages ? (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>{t('aiAssistant.loadingOlderMessages', 'Chargement des messages précédents…')}</span>
                      </div>
                    ) : olderMessagesError ? (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-destructive">{olderMessagesError}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setOlderMessagesError(null)
                            const container = userMessagesContainerRef.current || messagesContainerRef.current
                            if (container) {
                              prevScrollHeightRef.current = container.scrollHeight
                            }
                            loadOlderMessages()
                          }}
                          className="font-semibold text-primary underline hover:text-primary/80"
                        >
                          {t('common.retry', 'Réessayer')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
                {messages.length === 0 ? (
                  <div className="hidden" />
                ) : (
                  messages.map((message, messageIndex) => {
                    if (message.metadata?.hidden) return null
                    if (message.type === 'user') return null
                    if (message.metadata?.context_handoff) {
                      const display = message.metadata?.handoff_display || {}
                      const workflow = display.workflow || {}
                      const transferredCandidates = normalizeCandidateIds(message.metadata?.candidate_ids)
                      const transferredOffers = normalizeCandidateIds(message.metadata?.offer_ids)
                      return (
                        <div key={message.id} className="mx-1 rounded-2xl border border-teal-300/70 bg-teal-50/80 p-4 text-teal-950 shadow-sm dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100">
                          <div className="flex items-start gap-3">
                            <ArrowRight className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" />
                            <div className="min-w-0">
                              <p className="font-semibold">{t('aiAssistant.contextTransferred', 'Contexte transféré depuis la conversation précédente')}</p>
                              {display.objective && <p className="mt-1 text-sm opacity-80">{display.objective}</p>}
                              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                {transferredCandidates.length > 0 && <span className="rounded-full bg-white/70 px-2 py-1 dark:bg-black/20">{transferredCandidates.length} candidat(s)</span>}
                                {transferredOffers.length > 0 && <span className="rounded-full bg-white/70 px-2 py-1 dark:bg-black/20">{transferredOffers.length} offre(s)</span>}
                                {Array.isArray(display.decisions) && display.decisions.length > 0 && <span className="rounded-full bg-white/70 px-2 py-1 dark:bg-black/20">{display.decisions.length} décision(s)</span>}
                                {Array.isArray(display.pending_questions) && display.pending_questions.length > 0 && <span className="rounded-full bg-white/70 px-2 py-1 dark:bg-black/20">{display.pending_questions.length} question(s) en attente</span>}
                                {display.preview?.status === 'reference_only' && <span className="rounded-full bg-white/70 px-2 py-1 dark:bg-black/20">aperçu non confirmé transféré</span>}
                                {workflow.type && <span className="rounded-full bg-white/70 px-2 py-1 dark:bg-black/20">{workflow.type} · {workflow.status}</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    const generationGroupId = getGenerationGroupId(message)
                    const generationVersions = message.type === 'ai'
                      ? (generationGroups[generationGroupId] || [message])
                      : []
                    const latestGenerationVersion = generationVersions.length > 0
                      ? getGenerationVersion(generationVersions[generationVersions.length - 1])
                      : 1
                    const selectedGenerationVersion = selectedGenerationVersions[generationGroupId] || latestGenerationVersion
                    const selectedGenerationMessage = generationVersions.find(
                      (entry) => getGenerationVersion(entry) === selectedGenerationVersion
                    ) || generationVersions[generationVersions.length - 1]
                    const selectedGenerationIndex = generationVersions.findIndex(
                      (entry) => entry.id === selectedGenerationMessage?.id
                    )
                    const responseDurationMs = getAiResponseDurationMs(messages, messageIndex)

                    if (message.type === 'ai' && selectedGenerationMessage?.id !== message.id) return null
                    if (message.type === 'ai' && hasUserMessages && !selectedTurnAiIds.has(message.id)) return null
                    if (message.metadata?.superseded && !message.metadata?.generation_group_id) return null

                    const offerIdsFromMsg = normalizeCandidateIds(message.metadata?.offer_ids)
                    const singleOfferId = message.offerId
                    const messageOfferIds = offerIdsFromMsg.length > 0
                      ? offerIdsFromMsg
                      : (singleOfferId ? [singleOfferId] : [])

                    return (
                    <div
                      key={message.id}
                      className={`group/message flex w-full min-w-0 max-w-full flex-col overflow-hidden ${message.type === 'user' ? 'items-end' : 'items-start'} space-y-2`}
                    >
                      {/* Show candidate cards above user message if candidates were selected */}
                      {message.type === 'user' && (message.candidateIds?.length || message.candidateId) && (
                        <div className="max-w-[80%]">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(message.candidateIds?.length ? message.candidateIds : (message.candidateId ? [message.candidateId] : [])).map((cid) => {
                              const candidate = getCandidateById(cid)
                              return (
                                <div key={cid} className="flex items-center space-x-2 px-3 py-2 bg-background/80 border border-primary/30 rounded-lg shadow-sm">
                                  <div className="h-8 w-8 rounded-full overflow-hidden ring-2 ring-primary/20 flex items-center justify-center bg-background shadow-sm flex-shrink-0">
                                    {candidate?.profile_picture ? (
                                      <img
                                        src={getImageUrl(candidate.profile_picture, candidate.name)}
                                        alt={candidate.name}
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      <div className="h-full w-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white text-xs font-bold">
                                        {(candidate?.name || '?')
                                          .split(' ')
                                          .map((n) => n[0])
                                          .join('')
                                          .toUpperCase()
                                          .slice(0, 2)}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-foreground truncate">
                                      {candidate?.name || t('aiAssistant.loadingCandidate', 'Candidat...')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {t('aiAssistant.selectedCandidate')}
                                    </p>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                       )}

                      {/* Show offer chip(s) above user message if offer(s) were selected */}
                      {message.type === 'user' && messageOfferIds.length > 0 && (
                        <div className="max-w-[80%]">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {messageOfferIds.map((oid) => {
                              const offer = getOfferById(oid)
                              return (
                                <div key={oid} className="flex items-center space-x-2 px-3 py-2 bg-background/80 border border-indigo-300/50 rounded-lg shadow-sm">
                                  <div className="h-8 w-8 rounded-lg overflow-hidden ring-2 ring-indigo-200 flex items-center justify-center bg-indigo-50 shadow-sm flex-shrink-0 dark:bg-indigo-950/60 dark:ring-indigo-800">
                                    <Briefcase className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-foreground truncate">
                                      {offer?.job_title || t('aiAssistant.loadingOffer', 'Offre...')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {messageOfferIds.length > 1
                                        ? t('aiAssistant.selectedOfferLabel', 'Offre sélectionnée')
                                        : t('aiAssistant.selectedOfferLabel', 'Offre sélectionnée')}
                                    </p>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {/* Message bubble */}
                      <div className={`flex w-full min-w-0 max-w-full overflow-hidden ${message.type === 'user' ? 'justify-end' : 'items-start gap-2 sm:gap-3'}`}>
                        {message.type === 'ai' && (
                          <div className="mt-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md shadow-cyan-100 dark:shadow-none">
                            <Bot className="h-5 w-5" />
                          </div>
                        )}
                      <div
                        className={`min-w-0 overflow-hidden ${message.type === 'user'
                          ? 'max-w-[88%] rounded-3xl rounded-tr-none bg-primary px-4 py-3 text-primary-foreground sm:max-w-[80%] sm:px-5 sm:py-4 xl:max-w-[76%] 2xl:max-w-[68%] 2xl:px-6'
                          : 'flex-1 max-w-[calc(100%_-_2.75rem)] px-3 py-4 text-foreground sm:max-w-[calc(100%_-_3rem)] sm:px-4 2xl:max-w-[92%] 2xl:px-6 2xl:py-5'
                          } ${message.type === 'ai' ? 'text-left' : ''}`}
                      >
                        {(() => {
                          if (message.type === 'user') {
                            if (editingMessageId === message.id) {
                              return (
                                <div className="space-y-3">
                                  <textarea
                                    value={editedMessageContent}
                                    onChange={(event) => setEditedMessageContent(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Escape') handleCancelEditingMessage()
                                      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                                        event.preventDefault()
                                        handleResendEditedMessage(message)
                                      }
                                    }}
                                    rows={Math.min(8, Math.max(3, editedMessageContent.split('\n').length))}
                                    className="w-full min-w-[260px] resize-y rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm text-white outline-none placeholder:text-white/60 focus:border-white/60 focus:ring-2 focus:ring-white/20 sm:min-w-[360px]"
                                    aria-label={t('aiAssistant.editMessage')}
                                    autoFocus
                                  />
                                  <div className="flex justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={handleCancelEditingMessage}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                                      aria-label={t('aiAssistant.cancelEdit')}
                                      title={t('aiAssistant.cancelEdit')}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleResendEditedMessage(message)}
                                      disabled={!editedMessageContent.trim() || isLoading}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary transition-all hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-50"
                                      aria-label={t('aiAssistant.resendMessage')}
                                      title={t('aiAssistant.resendMessage')}
                                    >
                                      {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                    </button>
                                  </div>
                                </div>
                              )
                            }
                            if (message.metadata?.ui_form_display) {
                              const display = message.metadata.ui_form_display as {
                                title?: string
                                answers?: Array<{ field: string; label: string; value: string }>
                              }
                              return (
                                <div className="space-y-3 text-sm">
                                  <div className="flex items-center gap-2 font-medium text-white">
                                    <CheckCircle className="h-4 w-4 shrink-0 text-emerald-200" />
                                    <span>{message.metadata.friendly_message || (i18n.language.startsWith('fr') ? 'Questionnaire envoyé.' : 'Questionnaire submitted.')}</span>
                                  </div>
                                  <div className="rounded-xl border border-white/25 bg-white/10 px-4 py-3 shadow-inner">
                                    {display.title && <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-white/75">{display.title}</p>}
                                    <dl className="grid gap-2">
                                      {(display.answers || []).map(({ field, label, value }) => (
                                        <div key={field} className="min-w-0 rounded-lg border border-white/15 bg-black/10 px-3 py-2">
                                          <dt className="truncate text-[11px] font-medium uppercase tracking-wide text-white/60">{label}</dt>
                                          <dd className="mt-1 break-words font-medium leading-5 text-white">{value}</dd>
                                        </div>
                                      ))}
                                    </dl>
                                  </div>
                                </div>
                              )
                            }
                            if (message.metadata?.ui_form_submission) {
                              const submission = message.metadata.ui_form_submission as {
                                form_id?: string
                                answers?: Record<string, any>
                                display_answers?: Array<{ field: string; label: string; value: any }>
                              }
                              const sourceFormMessage = messages.find(
                                (entry) => entry.type === 'ai'
                                  && String(entry.id) === String(message.metadata?.form_for || '')
                              )
                              const sourceForm = sourceFormMessage?.metadata?.ui_form as DynamicFormSchema | undefined
                              const fields = sourceForm?.steps?.flatMap((step) => step.fields) || []
                              const fieldsByName = new Map(fields.map((field) => [field.name, field]))
                              const answers = submission.answers || {}
                              const displayAnswers = submission.display_answers || Object.entries(answers).map(([name, value]) => ({
                                field: name,
                                label: fieldsByName.get(name)?.label || name,
                                value,
                              }))
                              const formatAnswer = (name: string, value: any) => {
                                const field = fieldsByName.get(name)
                                if (Array.isArray(value)) {
                                  return value.map((item) => {
                                    const option = (field?.options || []).find((candidate) => candidate.value === item)
                                    return option?.label || String(item)
                                  }).join(', ')
                                }
                                const option = (field?.options || []).find((candidate) => candidate.value === value)
                                return option?.label || String(value)
                              }
                              return (
                                <div className="space-y-3 text-sm">
                                  {message.metadata?.friendly_message && (
                                    <div className="flex items-center gap-2 font-medium text-white">
                                      <CheckCircle className="h-4 w-4 shrink-0 text-emerald-200" />
                                      <span>{message.metadata.friendly_message}</span>
                                    </div>
                                  )}
                                  <div className="rounded-xl border border-white/25 bg-white/10 px-4 py-3 shadow-inner">
                                    {sourceForm?.title && (
                                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-white/75">
                                        {sourceForm.title}
                                      </p>
                                    )}
                                    <dl className="grid gap-2">
                                      {displayAnswers.map(({ field, label, value }) => {
                                        const renderedValue = formatAnswer(field, value)
                                        const valueItems = Array.isArray(value)
                                          ? renderedValue.split(', ').filter(Boolean)
                                          : []
                                        const showLabel = field !== 'selected_fields'
                                        return (
                                          <div key={field} className="min-w-0 rounded-lg border border-white/15 bg-black/10 px-3 py-2">
                                            {showLabel && (
                                              <dt className="truncate text-[11px] font-medium uppercase tracking-wide text-white/60">{label}</dt>
                                            )}
                                            <dd className={`${showLabel ? 'mt-1' : ''} break-words font-medium leading-5 text-white`}>
                                              {valueItems.length > 0 ? (
                                                <span className="flex flex-wrap gap-1.5">
                                                  {valueItems.map((item) => (
                                                    <span key={item} className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs text-white ring-1 ring-inset ring-white/15">
                                                      {item}
                                                    </span>
                                                  ))}
                                                </span>
                                              ) : renderedValue}
                                            </dd>
                                          </div>
                                        )
                                      })}
                                    </dl>
                                  </div>
                                </div>
                              )
                            }
                            if (message.metadata?.technical && message.metadata?.friendly_message) {
                              return <p className="text-sm">{message.metadata.friendly_message}</p>
                            }
                            return <p className="text-sm whitespace-pre-wrap break-words">{normalizeHtmlBreaks(message.content)}</p>
                          }

                          if (message.metadata?.candidate_offer_comparison_error) {
                            return (
                              <div className="space-y-2 rounded-xl border border-rose-200 bg-rose-50/80 p-4 text-rose-900 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-100">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                  <div>
                                    <p className="text-sm font-bold">
                                      {t('aiAssistant.comparisonValidationError', 'Comparaison fondée sur les preuves impossible')}
                                    </p>
                                    <p className="mt-1 text-sm">{message.content}</p>
                                  </div>
                                </div>
                              </div>
                            )
                          }

                          if (message.metadata?.ai_error) {
                            const isRetryable = !!message.metadata?.retryable
                            return (
                              <div className="space-y-3">
                                <ChatMarkdown content={message.content} />
                                {isRetryable && (
                                  <div className="flex items-center">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRetryFailedRequest(message)}
                                      disabled={isLoading}
                                    >
                                      {t('aiAssistant.retryRequest', 'Renvoyer la requête')}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )
                          }

                          if (message.metadata?.matching_style_analysis && message.metadata?.candidate_offer_comparison) {
                            return (
                              <CandidateOfferMatchingCard
                                comparison={message.metadata.candidate_offer_comparison}
                                candidate={message.metadata.candidate_identity}
                                offer={message.metadata.offer_identity}
                                language={i18n.language}
                              />
                            )
                          }

                          const hasOfferResumeSurface = Boolean(
                            message.metadata?.regeneration_quality_analysis
                            || message.metadata?.awaiting_offer_update_instructions
                            || message.metadata?.offer_data
                            || message.metadata?.has_offer_data
                            || message.metadata?.offer_form
                          )

                          if (
                            message.metadata?.offer_created
                            && message.metadata?.offer_id
                            && !hasOfferResumeSurface
                          ) {
                            const offerId = Number(message.metadata.offer_id)
                            const titleFromContent = message.content.match(/(?:offre|offer)\s+\*\*([^*]+)\*\*/i)?.[1]?.trim()
                            return (
                              <OfferSuccessCard
                                kind="created"
                                offerId={offerId}
                                offerTitle={message.metadata?.job_title || getOfferById(offerId)?.job_title || titleFromContent}
                                onOpen={(selectedOfferId) => navigate(`/offers/${selectedOfferId}`)}
                              />
                            )
                          }

                          if (
                            message.metadata?.offer_action_applied
                            && ['offer_update', 'offer_regenerate'].includes(message.metadata?.action)
                            && message.metadata?.offer_id
                            && !hasOfferResumeSurface
                          ) {
                            const offerId = Number(message.metadata.offer_id)
                            const updatedTitle = message.metadata?.job_title || message.metadata?.offer?.job_title
                            return (
                              <OfferSuccessCard
                                kind={message.metadata.action === 'offer_regenerate' ? 'regenerated' : 'updated'}
                                offerId={offerId}
                                offerTitle={updatedTitle || getOfferById(offerId)?.job_title}
                                onOpen={(selectedOfferId) => navigate(`/offers/${selectedOfferId}`)}
                              />
                            )
                          }

                          if (message.metadata?.direct_retrieval) {
                            return (
                              <div className="rounded-lg border border-border/60 bg-background p-3">
                                <ChatMarkdown content={message.content} />
                              </div>
                            )
                          }

                          if (message.metadata?.offer_error) {
                            return <ChatMarkdown content={message.content} />
                          }

                          const structuredEmail = message.metadata?.email_compose || message.metadata
                          const hasStructuredEmailContract = typeof message.metadata?.is_email === 'boolean'
                            || typeof message.metadata?.email_compose?.is_email === 'boolean'
                          const emailDraft = hasStructuredEmailContract && structuredEmail?.is_email
                            && typeof structuredEmail?.subject === 'string'
                            && typeof structuredEmail?.body === 'string'
                            ? {
                              intro: typeof structuredEmail.intro === 'string' ? structuredEmail.intro : '',
                              subject: structuredEmail.subject,
                              body: structuredEmail.body,
                            }
                            : null
                          if (emailDraft) {
                            return <EmailComposerCard messageId={message.id} content={message.content} email={emailDraft} />
                          }

                          if (message.metadata?.regeneration_quality_analysis) {
                            const quality = message.metadata.regeneration_quality_analysis
                            const offerId = Number(message.metadata?.offer_id)
                            const latestRegenerationQualityId = [...messages].reverse().find((entry) => (
                              entry.type === 'ai'
                              && entry.metadata?.regeneration_quality_analysis
                              && String(entry.metadata?.regeneration_state?.status || '') !== 'CANCELLED'
                            ))?.id
                            const isLatestRegenerationSurface = (
                              String(latestRegenerationQualityId || '') === String(message.id)
                              || String(messages[messages.length - 1]?.metadata?.resume_target_message_id || '') === String(message.id)
                            )
                            // Older regeneration turns keep the text only; the live
                            // « Modifications proposées » + résumé stay on the latest turn.
                            if (!isLatestRegenerationSurface) {
                              return <ChatMarkdown content={message.content} />
                            }
                            const offerDetailResolved = Number.isFinite(offerId) && offerId > 0
                              && Object.prototype.hasOwnProperty.call(offerDetailCache, offerId)
                            const selectedOffer = Number.isFinite(offerId) && offerId > 0
                              ? (offerDetailCache[offerId] || undefined)
                              : undefined
                            // Prefer the regenerated proposal from metadata — never hydrate
                            // the résumé with a plain copy of the live offer.
                            const metadataOfferForm = (
                              message.metadata?.offer_form
                              || message.metadata?.regeneration_state?.current_regenerated_proposal
                              || null
                            ) as Record<string, any> | null
                            const regenerationBaseline = (
                              message.metadata?.regeneration_state?.original_offer_data
                              || null
                            ) as Record<string, any> | null
                            const offerSource = metadataOfferForm
                              || (offerDetailResolved ? selectedOffer : null)
                            const offerForm = offerSource
                              ? buildUpdatedOfferPreview(offerSource as Record<string, any>, [])
                              : null
                            const isLatestRegenerationMessage = isLatestRegenerationSurface
                            const regenerationConfirmedOfferId = Number(
                              confirmedOffers[message.id]?.offerId
                              || (
                                message.metadata?.offer_action_applied
                                  ? message.metadata?.offer_id
                                  : 0
                              )
                              || 0
                            )
                            const isRegenerationConfirmed = (
                              Boolean(confirmedOffers[message.id])
                              || Boolean(message.metadata?.offer_action_applied)
                              || String(message.metadata?.regeneration_state?.status || '') === 'CONFIRMED'
                            )
                            const aiAssistPatchForMessage = (
                              regenerationAiAssistPatch
                              && regenerationAiAssistPatch.messageId === message.id
                            )
                              ? {
                                  field: regenerationAiAssistPatch.field,
                                  instruction: regenerationAiAssistPatch.instruction,
                                  token: regenerationAiAssistPatch.token,
                                }
                              : null
                            const draftPatchForMessage = composeResumeDraftPatch(
                              message.id,
                              resumeChatPatch,
                              regenerationDraftPatch,
                            )
                            const qualityProposalsForResume = quality && regenerationBaseline
                              ? buildQualityProposalValues(quality, regenerationBaseline)
                              : {}
                            const liveResumeOffer = (
                              regenerationCurrentDraft?.messageId === message.id
                                ? regenerationCurrentDraft.data
                                : applyQualityProposalsToOffer(
                                    (offerForm || regenerationBaseline || {}) as Record<string, unknown>,
                                    qualityProposalsForResume,
                                  )
                            )
                            return (
                              <div className="space-y-4">
                                <OfferRegenerationWorkspace
                                  quality={quality}
                                  disabled={isLoading}
                                  onRetryAnalysis={() => {
                                    void handleRegenerationCommand(
                                      message,
                                      'retry_analysis',
                                      t('aiAssistant.reanalyzeOffer', 'Réanalyser'),
                                    )
                                  }}
                                  onImproveWithAI={(field, instruction) => {
                                    regenerationAiAssistPatchTokenRef.current += 1
                                    setRegenerationAiAssistPatch({
                                      messageId: message.id,
                                      field,
                                      instruction,
                                      token: regenerationAiAssistPatchTokenRef.current,
                                    })
                                  }}
                                  onApplyChange={(field, value) => {
                                    regenerationDraftPatchTokenRef.current += 1
                                    setRegenerationDraftPatch({
                                      messageId: message.id,
                                      field,
                                      value,
                                      token: regenerationDraftPatchTokenRef.current,
                                    })
                                  }}
                                  baselineOffer={regenerationBaseline}
                                  currentOffer={liveResumeOffer}
                                />
                                {offerForm && Number.isFinite(offerId) && offerId > 0 ? (
                                  <OfferSummaryCard
                                    message={message}
                                    offerData={offerForm}
                                    offerCleaned=""
                                    language={i18n.language}
                                    isLatestMessage={isLatestRegenerationMessage}
                                    isOfferCompleteEnough={hasSubstantiveOfferContent(offerForm)}
                                    isOfferCancelled={false}
                                    isOfferConfirmed={isRegenerationConfirmed}
                                    confirmedInfo={
                                      isRegenerationConfirmed && Number.isFinite(regenerationConfirmedOfferId) && regenerationConfirmedOfferId > 0
                                        ? { offerId: regenerationConfirmedOfferId, status: 'regenerated' }
                                        : undefined
                                    }
                                    isCreating={isLoading}
                                    isCancelling={false}
                                    startInEditMode
                                    resumeAction="regenerate"
                                    includeEmptyFields
                                    baselineOfferData={regenerationBaseline}
                                    qualityAnalysis={quality}
                                    draftPatch={draftPatchForMessage}
                                    aiAssistPatch={aiAssistPatchForMessage}
                                    onDraftChange={(data) => handleRegenerationDraftChange(message.id, data)}
                                    initialHiringTemplateId={readHiringTemplateId(selectedOffer) || readHiringTemplateId(regenerationBaseline)}
                                    editContext={{
                                      offerId,
                                      onSaveEdits: async (_id, edited) => {
                                        await handleApplyRegenerationDraft(message, offerId, edited)
                                      },
                                      onRefineField: handleRefineExistingOfferField,
                                    }}
                                    onConfirm={async () => undefined}
                                    onRefineField={async () => undefined}
                                    onCancel={async () => undefined}
                                    onOpenOffer={(selectedOfferId) => navigate(`/offers/${selectedOfferId}`)}
                                  />
                                ) : (
                                  <div className="flex items-center gap-3 rounded-xl border border-border bg-background/70 px-4 py-5 text-sm text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                    {t('aiAssistant.loadingOfferPreview', 'Préparation de l\'aperçu de l\'offre…')}
                                  </div>
                                )}
                              </div>
                            )
                          }

                          if (message.metadata?.regeneration_retryable && message.metadata?.offer_id) {
                            return (
                              <div className="space-y-3">
                                <ChatMarkdown content={message.content} />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isLoading}
                                  onClick={() => handleRegenerationCommand(message, 'retry_analysis', t('common.retry', 'Réessayer'))}
                                >
                                  <RefreshCw className="mr-2 h-4 w-4" />
                                  {t('common.retry', 'Réessayer')}
                                </Button>
                              </div>
                            )
                          }

                          if (
                            message.metadata?.awaiting_offer_update_instructions
                            && !message.metadata?.offer_action_error
                            && (
                              !message.metadata?.edit_state
                              || message.metadata.edit_state === 'AWAITING_EDIT_INSTRUCTIONS'
                              || message.metadata.edit_state === 'AWAITING_EDIT_CLARIFICATION'
                              || message.metadata.edit_state === 'APPLIED'
                              || message.metadata?.offer_action_applied
                            )
                            && message.metadata?.offer_id
                          ) {
                            const latestModificationResumeId = [...messages].reverse().find((entry) => (
                              entry.type === 'ai'
                              && entry.metadata?.awaiting_offer_update_instructions
                              && !entry.metadata?.offer_action_error
                              && !entry.metadata?.regeneration_quality_analysis
                            ))?.id
                            const isLatestModificationResume = String(latestModificationResumeId || '') === String(message.id)
                            if (!isLatestModificationResume) {
                              return <ChatMarkdown content={message.content} />
                            }
                            const offerId = Number(message.metadata.offer_id)
                            const offerDetailResolved = Object.prototype.hasOwnProperty.call(offerDetailCache, offerId)
                            const selectedOffer = offerDetailCache[offerId]
                              || (offerDetailResolved ? offerCache[offerId] : undefined)
                            const metadataOfferForm = (
                              message.metadata?.offer_form
                              && typeof message.metadata.offer_form === 'object'
                            ) ? message.metadata.offer_form as Record<string, any> : null
                            const currentOfferPreview = metadataOfferForm
                              ? buildUpdatedOfferPreview(metadataOfferForm, [])
                              : selectedOffer
                                ? buildUpdatedOfferPreview(selectedOffer, [])
                                : undefined
                            const modificationConfirmedOfferId = Number(
                              confirmedOffers[message.id]?.offerId
                              || (message.metadata?.offer_action_applied ? message.metadata?.offer_id : 0)
                              || 0
                            )
                            const isModificationConfirmed = (
                              Boolean(confirmedOffers[message.id])
                              || Boolean(message.metadata?.offer_action_applied)
                              || message.metadata.edit_state === 'APPLIED'
                            )

                            if (!currentOfferPreview?.job_title) {
                              return (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-3 rounded-xl border border-border bg-background/70 px-4 py-5 text-sm text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                    {t('aiAssistant.loadingOfferPreview', 'Préparation de l\'aperçu de l\'offre…')}
                                  </div>
                                  <ChatMarkdown content={message.content} />
                                </div>
                              )
                            }

                            const modificationBaselineRaw = (
                              message.metadata?.baseline_offer_data
                              && typeof message.metadata.baseline_offer_data === 'object'
                            ) ? message.metadata.baseline_offer_data as Record<string, any> : null
                            const modificationBaseline = modificationBaselineRaw
                              ? buildUpdatedOfferPreview(modificationBaselineRaw, [])
                              : (selectedOffer ? buildUpdatedOfferPreview(selectedOffer, []) : currentOfferPreview)
                            const modificationQuality = (
                              message.metadata?.resume_quality_analysis
                              && typeof message.metadata.resume_quality_analysis === 'object'
                            ) ? message.metadata.resume_quality_analysis as RegenerationQualityAnalysis : null
                            const modificationDraftPatch = composeResumeDraftPatch(
                              message.id,
                              resumeChatPatch,
                              regenerationDraftPatch,
                            )

                            return (
                              <div className="space-y-4">
                                {modificationBaseline && modificationQuality ? (
                                  <OfferRegenerationWorkspace
                                    quality={modificationQuality}
                                    disabled={isLoading || isModificationConfirmed}
                                    onImproveWithAI={(field, instruction) => {
                                      regenerationAiAssistPatchTokenRef.current += 1
                                      setRegenerationAiAssistPatch({
                                        messageId: message.id,
                                        field,
                                        instruction,
                                        token: regenerationAiAssistPatchTokenRef.current,
                                      })
                                    }}
                                    onApplyChange={(field, value) => {
                                      regenerationDraftPatchTokenRef.current += 1
                                      setRegenerationDraftPatch({
                                        messageId: message.id,
                                        field,
                                        value,
                                        token: regenerationDraftPatchTokenRef.current,
                                      })
                                    }}
                                    baselineOffer={modificationBaseline}
                                    currentOffer={
                                      regenerationCurrentDraft?.messageId === message.id
                                        ? regenerationCurrentDraft.data
                                        : currentOfferPreview
                                    }
                                  />
                                ) : null}
                                <OfferSummaryCard
                                  message={message}
                                  offerData={currentOfferPreview}
                                  offerCleaned=""
                                  language={i18n.language}
                                  isLatestMessage
                                  isOfferCompleteEnough={hasSubstantiveOfferContent(currentOfferPreview)}
                                  isOfferCancelled={false}
                                  isOfferConfirmed={isModificationConfirmed}
                                  confirmedInfo={
                                    isModificationConfirmed && Number.isFinite(modificationConfirmedOfferId) && modificationConfirmedOfferId > 0
                                      ? { offerId: modificationConfirmedOfferId, status: 'updated' }
                                      : undefined
                                  }
                                  isCreating={isLoading}
                                  isCancelling={false}
                                  startInEditMode
                                  resumeAction="modify"
                                  includeEmptyFields
                                  baselineOfferData={modificationBaseline}
                                  qualityAnalysis={modificationQuality}
                                  draftPatch={modificationDraftPatch}
                                  aiAssistPatch={
                                    regenerationAiAssistPatch?.messageId === message.id
                                      ? regenerationAiAssistPatch
                                      : null
                                  }
                                  onDraftChange={(data) => handleRegenerationDraftChange(message.id, data)}
                                  initialHiringTemplateId={readHiringTemplateId(selectedOffer) || readHiringTemplateId(modificationBaseline)}
                                  editContext={{
                                    offerId,
                                    onSaveEdits: async (id, edited) => {
                                      await handleSaveExistingOfferEdits(id, edited, message.id)
                                    },
                                    onRefineField: handleRefineExistingOfferField,
                                  }}
                                  onConfirm={handleConfirmCreateOffer}
                                  onRefineField={handleRefineOfferProposalField}
                                  onCancel={handleCancelOfferProposal}
                                  onOpenOffer={(selectedOfferId) => navigate(`/offers/${selectedOfferId}`)}
                                />
                                <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                                  <ChatMarkdown content={message.content} />
                                </div>
                              </div>
                            )
                          }

                          if (message.metadata?.requires_confirmation && message.metadata?.pending_action_id) {
                            const pendingId = String(message.metadata.pending_action_id)
                            const completed = messages.some((entry) =>
                              String(entry.metadata?.pending_action_id || '') === pendingId
                              && (entry.metadata?.offer_action_applied || entry.metadata?.offer_action_cancelled)
                            )
                            const destructive = false
                            const offerId = Number(message.metadata?.offer_id)
                            const generatedChanges = Array.isArray(message.metadata?.preview?.changes)
                              ? message.metadata.preview.changes as OfferChange[]
                              : []
                            const offerDetailResolved = Object.prototype.hasOwnProperty.call(offerDetailCache, offerId)
                            const selectedOffer = offerDetailCache[offerId]
                              || (offerDetailResolved ? offerCache[offerId] : undefined)
                            const isRegenerationPreview = message.metadata?.action === 'offer_regenerate'
                            const usesRichPreview = usesRichOfferPreview(message.metadata?.action)
                            const regenerationOriginal = message.metadata?.regeneration_state?.original_offer_data
                            const previewBaseOffer = selectedOffer
                              || (isRegenerationPreview && regenerationOriginal ? regenerationOriginal : undefined)
                            const originalOfferPreview = selectedOffer
                              ? buildUpdatedOfferPreview(selectedOffer, [])
                              : (isRegenerationPreview && regenerationOriginal
                                ? buildUpdatedOfferPreview(regenerationOriginal as Record<string, any>, [])
                                : undefined)
                            const showUpdateResume = message.metadata?.action === 'offer_update'
                            const isUpdatePreview = showUpdateResume && !completed
                            const updateQualityAnalysis = isUpdatePreview
                              ? buildQualityAnalysisFromOfferChanges(generatedChanges)
                              : null
                            const offerActionPreview = usesRichPreview && previewBaseOffer
                              ? buildUpdatedOfferPreview(previewBaseOffer, generatedChanges)
                              : undefined
                            const changeSummary = buildOfferChangeSummary(generatedChanges)
                            const regenerationSummary = message.metadata?.regeneration_summary
                            if (usesRichPreview && !offerActionPreview && !offerDetailResolved) {
                              return (
                                <div className="flex items-center gap-3 rounded-xl border border-border bg-background/70 px-4 py-5 text-sm text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                  {t('aiAssistant.loadingOfferPreview', 'Préparation de l\'aperçu de l\'offre…')}
                                </div>
                              )
                            }
                            if (usesRichPreview && !offerActionPreview) {
                              return (
                                <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-100">
                                  <ChatMarkdown content={t('aiAssistant.offerPreviewUnavailable', 'Impossible de charger l’aperçu complet de cette offre. La proposition reste en attente et aucune modification n’a été enregistrée.')} />
                                </div>
                              )
                            }
                            if (offerActionPreview?.job_title) {
                              return (
                                <div className="space-y-4">
                                  {!showUpdateResume && (
                                  <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 p-4 dark:border-teal-900/70 dark:from-teal-950/40 dark:to-cyan-950/30">
                                    <div className="mb-3 flex items-center gap-2">
                                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/70 dark:text-teal-300">
                                        <Check className="h-4 w-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-bold text-foreground">
                                          {isRegenerationPreview
                                            ? t('aiAssistant.regenerationSummary', 'Résumé de la régénération')
                                            : t('aiAssistant.changesDetected', 'Changements détectés')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {t('aiAssistant.reviewChangesBeforeConfirming', 'Vérifiez les modifications avant de confirmer.')}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      {isRegenerationPreview && regenerationSummary ? (
                                        <div className="grid gap-2 md:grid-cols-2">
                                          <div className="rounded-lg border border-teal-200/80 bg-white/80 px-3 py-2 dark:border-teal-900/70 dark:bg-slate-950/30">
                                            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300">
                                              {t('aiAssistant.regenerationImproved', 'Amélioré')}
                                            </p>
                                            {(regenerationSummary.improved || []).map((item: string, index: number) => (
                                              <p key={index} className="mt-1 flex gap-2 text-xs text-foreground"><CheckCircle className="h-3.5 w-3.5 shrink-0 text-teal-600" />{item}</p>
                                            ))}
                                          </div>
                                          <div className="rounded-lg border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/30">
                                            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                                              {t('aiAssistant.regenerationPreserved', 'Conservé')}
                                            </p>
                                            {(regenerationSummary.preserved || []).map((item: string, index: number) => (
                                              <p key={index} className="mt-1 flex gap-2 text-xs text-foreground"><Check className="h-3.5 w-3.5 shrink-0 text-slate-500" />{item}</p>
                                            ))}
                                          </div>
                                        </div>
                                      ) : changeSummary.map((change: any, index: number) => (
                                        <div key={`${change.field || 'change'}-${index}`} className="rounded-lg border border-teal-200/80 bg-white/80 px-3 py-2 dark:border-teal-900/70 dark:bg-slate-950/30">
                                          <div className="flex items-start gap-2">
                                            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400" />
                                            <div className="min-w-0">
                                              <p className="text-sm font-semibold text-foreground">{change.headline || change.label}</p>
                                              {Array.isArray(change.details) && change.details.map((detail: string, detailIndex: number) => (
                                                <p key={detailIndex} className="mt-0.5 break-words text-xs text-muted-foreground">
                                                  {detail}
                                                </p>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  )}

                                  <OfferSummaryCard
                                    message={message}
                                    offerData={offerActionPreview}
                                    offerCleaned=""
                                    language={i18n.language}
                                    isLatestMessage={
                                      String(messages[messages.length - 1]?.metadata?.resume_target_message_id || '') === String(message.id)
                                    }
                                    isOfferCompleteEnough={true}
                                    isOfferCancelled={false}
                                    isOfferConfirmed={showUpdateResume && (
                                      completed
                                      || Boolean(confirmedOffers[message.id])
                                      || Boolean(message.metadata?.offer_action_applied)
                                    )}
                                    confirmedInfo={showUpdateResume && Number.isFinite(offerId) && offerId > 0
                                      ? { offerId, status: 'updated' }
                                      : undefined}
                                    isCreating={executingOfferActionId === pendingId}
                                    isCancelling={false}
                                    presentationOnly={!showUpdateResume}
                                    startInEditMode={showUpdateResume}
                                    resumeAction={showUpdateResume ? 'modify' : undefined}
                                    includeEmptyFields={showUpdateResume}
                                    baselineOfferData={showUpdateResume ? originalOfferPreview : null}
                                    qualityAnalysis={updateQualityAnalysis}
                                    initialHiringTemplateId={showUpdateResume
                                      ? (readHiringTemplateId(selectedOffer) || readHiringTemplateId(originalOfferPreview))
                                      : null}
                                    draftPatch={composeResumeDraftPatch(message.id, resumeChatPatch, regenerationDraftPatch)}
                                    onDraftChange={showUpdateResume
                                      ? (data) => handleRegenerationDraftChange(message.id, data)
                                      : undefined}
                                    editContext={
                                      showUpdateResume && Number.isFinite(offerId)
                                        ? {
                                          offerId,
                                          onSaveEdits: async (id, edited) => {
                                            await handleSaveExistingOfferEdits(id, edited, message.id)
                                          },
                                          onRefineField: handleRefineExistingOfferField,
                                        }
                                        : undefined
                                    }
                                    onConfirm={handleConfirmCreateOffer}
                                    onRefineField={handleRefineOfferProposalField}
                                    onCancel={handleCancelOfferProposal}
                                    onOpenOffer={(offerId) => navigate(`/offers/${offerId}`)}
                                  />

                                  {!showUpdateResume && (
                                  <div className="flex flex-wrap gap-2 border-t border-border/60 pt-3">
                                    <Button
                                      size="sm"
                                      disabled={completed || executingOfferActionId === pendingId}
                                      onClick={() => handlePendingOfferAction(message, 'confirm')}
                                    >
                                      {executingOfferActionId === pendingId
                                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        : <Check className="mr-2 h-4 w-4" />}
                                      {t('aiAssistant.applyOfferAction', 'Confirmer')}
                                    </Button>
                                    {isRegenerationPreview && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={completed || executingOfferActionId === pendingId || isLoading}
                                          onClick={() => handleRegenerationCommand(
                                            message,
                                            'regenerate_again',
                                            t('aiAssistant.regenerateAgain', 'Régénérer à nouveau'),
                                          )}
                                        >
                                          <RefreshCw className="mr-2 h-4 w-4" />
                                          {t('aiAssistant.regenerateAgain', 'Régénérer à nouveau')}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={completed || executingOfferActionId === pendingId || isLoading}
                                          onClick={() => handleRegenerationCommand(
                                            message,
                                            'refine',
                                            t('aiAssistant.refineRegeneration', 'Affiner'),
                                          )}
                                        >
                                          {t('aiAssistant.refineRegeneration', 'Affiner')}
                                        </Button>
                                      </>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={completed || executingOfferActionId === pendingId}
                                      onClick={() => handlePendingOfferAction(message, 'cancel')}
                                    >
                                      {t('common.cancel', 'Annuler')}
                                    </Button>
                                    {completed && (
                                      <span className="inline-flex items-center text-xs text-muted-foreground">
                                        <CheckCircle className="mr-1 h-4 w-4" />
                                        {t('aiAssistant.actionCompleted', 'Action traitée')}
                                      </span>
                                    )}
                                  </div>
                                  )}
                                </div>
                              )
                            }
                            return (
                              <div className="space-y-4">
                                {message.metadata?.action !== 'offer_update' && <ChatMarkdown content={message.content} />}
                                <div className="flex flex-wrap gap-2 border-t border-border/60 pt-3">
                                  <Button
                                    size="sm"
                                    variant={destructive ? 'destructive' : 'default'}
                                    disabled={completed || executingOfferActionId === pendingId}
                                    onClick={() => handlePendingOfferAction(message, 'confirm')}
                                  >
                                    {executingOfferActionId === pendingId
                                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      : <Check className="mr-2 h-4 w-4" />}
                                    {destructive
                                      ? t('aiAssistant.deletePermanently', 'Supprimer définitivement')
                                      : t('aiAssistant.applyOfferAction', 'Confirmer')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={completed || executingOfferActionId === pendingId}
                                    onClick={() => handlePendingOfferAction(message, 'cancel')}
                                  >
                                    {t('common.cancel', 'Annuler')}
                                  </Button>
                                  {completed && (
                                    <span className="inline-flex items-center text-xs text-muted-foreground">
                                      <CheckCircle className="mr-1 h-4 w-4" />
                                      {t('aiAssistant.actionCompleted', 'Action traitée')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          }

                          const dynamicForm = message.metadata?.ui_form as DynamicFormSchema | undefined
                          if (dynamicForm?.version === 1 && Array.isArray(dynamicForm.steps)) {
                            const formCompleted = messages.some(
                              (entry) => entry.type === 'user' && String(entry.metadata?.form_for || '') === String(message.id)
                            )
                            return (
                              <div className="space-y-3">
                                {message.content && <ChatMarkdown content={message.content} />}
                                <DynamicFormCard
                                  form={dynamicForm}
                                  language={i18n.language}
                                  disabled={isLoading}
                                  completed={formCompleted}
                                  loadCandidates={loadDynamicFormCandidates}
                                  onSubmit={(answers) => handleSubmitDynamicForm(message.id, dynamicForm, answers)}
                                />
                              </div>
                            )
                          }

                          const precedingMessage = messages[messageIndex - 1]
                          // Server-owned flag from OfferSetupDetectionAgent. Text
                          // matching on the user message is only a fallback for
                          // older sessions created before that metadata existed.
                          const showOfferQuestionnaire = Boolean(
                            message.metadata?.offer_setup === 'guided'
                            || message.metadata?.has_offer_questionnaire
                            || isOfferQuestionnaireResponse(message.content)
                          )
                          const isCandidateDrivenOffer = Boolean(
                            message.metadata?.candidate_context_authorized
                            || normalizeCandidateIds(message.metadata?.candidate_ids).length > 0
                            || normalizeCandidateIds(precedingMessage?.candidateIds).length > 0
                            || precedingMessage?.candidateId
                            || normalizeCandidateIds(precedingMessage?.metadata?.candidate_ids).length > 0
                            || precedingMessage?.metadata?.candidate_id
                          )
                          if (!isCandidateDrivenOffer && showOfferQuestionnaire) {
                            const questionnaireCompleted = messages
                              .slice(messageIndex + 1)
                              .some((entry) => entry.type === 'user')
                            return (
                              <OfferQuestionnaireWizard
                                language={i18n.language}
                                initialJobTitle={extractRequestedJobTitle(precedingMessage?.content || '')}
                                initialExperienceLevel={extractRequestedExperienceLevel(precedingMessage?.content || '')}
                                disabled={isLoading}
                                completed={questionnaireCompleted}
                                onSubmit={(answers, display) => handleSendMessage(answers, {
                                  ui_form_display: display,
                                  friendly_message: i18n.language.startsWith('fr') ? 'Questionnaire envoyé.' : 'Questionnaire submitted.',
                                })}
                              />
                            )
                          }

                          const { offerData, cleaned: extractedOfferCleaned } = resolveOfferDataForMessage(message)
                          if (offerData) {
                            const latestCreationResumeId = [...messages].reverse().find((entry) => (
                              entry.type === 'ai'
                              && !entry.metadata?.offer_cancelled
                              && !entry.metadata?.regeneration_quality_analysis
                              && !entry.metadata?.awaiting_offer_update_instructions
                              && (
                                Boolean(entry.metadata?.offer_data)
                                || Boolean(entry.metadata?.has_offer_data)
                              )
                            ))?.id
                            const isLatestCreationResume = String(latestCreationResumeId || '') === String(message.id)
                            // Same as regeneration: only the latest résumé turn keeps the card.
                            if (!isLatestCreationResume) {
                              const offerCleaned = (
                                message.metadata?.intent === 'compare_and_create_offer'
                                  ? extractedOfferCleaned
                                  : ''
                              )
                              return offerCleaned
                                ? <ChatMarkdown content={offerCleaned || message.content} />
                                : <ChatMarkdown content={message.content} />
                            }
                            const legacyConfirmedMsg = messages.find(
                              (msg) =>
                                msg.type === 'ai' &&
                                msg.metadata?.offer_created &&
                                msg.metadata?.offer_id &&
                                String(msg.metadata?.source_message_id || '') === String(message.id)
                            )
                            const localConfirmed = confirmedOffers[message.id]
                            const createdOfferId = Number(
                              localConfirmed?.offerId
                              || message.metadata?.created_offer_id
                              || (message.metadata?.offer_created ? message.metadata?.offer_id : 0)
                              || legacyConfirmedMsg?.metadata?.offer_id
                              || 0
                            )
                            const isOfferConfirmed = Boolean(
                              localConfirmed
                              || message.metadata?.offer_created
                              || legacyConfirmedMsg
                            )
                            const isLatestMessage = true
                            const isCreating = creatingOfferId === message.id
                            const isCancelling = cancellingOfferId === message.id
                            const isOfferCancelled = Boolean(message.metadata?.offer_cancelled)
                            const isOfferCompleteEnough = hasSubstantiveOfferContent(offerData)
                            const confirmedInfo = (
                              isOfferConfirmed && Number.isFinite(createdOfferId) && createdOfferId > 0
                                ? { offerId: createdOfferId, status: localConfirmed?.status || 'validated' }
                                : undefined
                            )
                            const startCreationResumeInEditMode = !isOfferCancelled
                            const offerCleaned = (
                              message.metadata?.intent === 'compare_and_create_offer'
                                ? extractedOfferCleaned
                                : ''
                            )
                            const creationBaseline = (
                              message.metadata?.baseline_offer_data
                              && typeof message.metadata.baseline_offer_data === 'object'
                            ) ? message.metadata.baseline_offer_data as Record<string, any> : null
                            const creationQuality = (
                              message.metadata?.resume_quality_analysis
                              && typeof message.metadata.resume_quality_analysis === 'object'
                            ) ? message.metadata.resume_quality_analysis as RegenerationQualityAnalysis : null
                            const creationDraftPatch = composeResumeDraftPatch(
                              message.id,
                              resumeChatPatch,
                              regenerationDraftPatch,
                            )
                            return (
                              <div className="space-y-4">
                                {creationBaseline && creationQuality ? (
                                  <OfferRegenerationWorkspace
                                    quality={creationQuality}
                                    disabled={isCreating || isCancelling || isOfferConfirmed || isOfferCancelled}
                                    onImproveWithAI={(field, instruction) => {
                                      regenerationAiAssistPatchTokenRef.current += 1
                                      setRegenerationAiAssistPatch({
                                        messageId: message.id,
                                        field,
                                        instruction,
                                        token: regenerationAiAssistPatchTokenRef.current,
                                      })
                                    }}
                                    onApplyChange={(field, value) => {
                                      regenerationDraftPatchTokenRef.current += 1
                                      setRegenerationDraftPatch({
                                        messageId: message.id,
                                        field,
                                        value,
                                        token: regenerationDraftPatchTokenRef.current,
                                      })
                                    }}
                                    baselineOffer={creationBaseline}
                                    currentOffer={
                                      regenerationCurrentDraft?.messageId === message.id
                                        ? regenerationCurrentDraft.data
                                        : offerData
                                    }
                                  />
                                ) : null}
                                <OfferSummaryCard
                                  key={message.id}
                                  message={message}
                                  offerData={offerData}
                                  offerCleaned={offerCleaned}
                                  language={i18n.language}
                                  isLatestMessage={isLatestMessage}
                                  isOfferCompleteEnough={isOfferCompleteEnough}
                                  isOfferCancelled={isOfferCancelled}
                                  isOfferConfirmed={isOfferConfirmed}
                                  confirmedInfo={confirmedInfo}
                                  isCreating={isCreating}
                                  isCancelling={isCancelling}
                                  startInEditMode={startCreationResumeInEditMode}
                                  resumeAction="create"
                                  includeEmptyFields={startCreationResumeInEditMode && !isOfferConfirmed}
                                  baselineOfferData={creationBaseline}
                                  qualityAnalysis={creationQuality}
                                  draftPatch={creationDraftPatch}
                                  aiAssistPatch={
                                    regenerationAiAssistPatch?.messageId === message.id
                                      ? regenerationAiAssistPatch
                                      : null
                                  }
                                  onDraftChange={(data) => handleRegenerationDraftChange(message.id, data)}
                                  onConfirm={handleConfirmCreateOffer}
                                  onRefineField={handleRefineOfferProposalField}
                                  onCancel={handleCancelOfferProposal}
                                  onOpenOffer={(offerId) => navigate(`/offers/${offerId}`)}
                                />
                              </div>
                            )
                          }

                          // Check for proposed_fields
                          const { fields, cleaned } = extractProposedFields(message.content)
                          if (fields.length > 0) {
                            const selection = fieldSelections[message.id] || fields
                            const isConfirmed = messages.some(
                              (entry) => entry.metadata?.selection_for === message.id
                            )
                            return (
                              <div className="space-y-3">
                                {cleaned && <ChatMarkdown content={cleaned} />}
                                <div className="rounded-lg border border-border/60 bg-background p-3">
                                  <p className="text-sm font-semibold text-foreground mb-2">
                                    {t('aiAssistant.selectFieldsTitle', 'Selectionnez les champs a analyser')}
                                  </p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {fields.map((field) => (
                                      <label
                                        key={field}
                                        className="flex items-center space-x-2 text-sm"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selection.includes(field)}
                                          disabled={isConfirmed}
                                          onChange={() => {
                                            setFieldSelections((prev) => {
                                              const current = prev[message.id] || fields
                                              const next = current.includes(field)
                                                ? current.filter((item) => item !== field)
                                                : [...current, field]
                                              return { ...prev, [message.id]: next }
                                            })
                                          }}
                                        />
                                        <span>{getFieldLabel(field)}</span>
                                      </label>
                                    ))}
                                  </div>
                                  <div className="mt-3 flex items-center justify-between">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleConfirmFields(message.id, selection)}
                                      disabled={isConfirmed || selection.length === 0 || isLoading}
                                    >
                                      {t('aiAssistant.confirmSelection', 'Confirmer')}
                                    </Button>
                                    {isConfirmed && (
                                      <span className="text-xs text-muted-foreground">
                                        {t('aiAssistant.selectionSent', 'Selection envoyee')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          }

                          return <ChatMarkdown content={message.content} />
                        })()}
                        <div className={`mt-3 ${message.type === 'ai' ? 'flex min-h-7 flex-wrap items-center justify-between gap-x-3 gap-y-1' : ''}`}>
                        {/* Hidden for now: message time + response duration
                        <p className="flex items-center gap-1.5 text-xs opacity-70">
                          <span>{message.timestamp.toLocaleTimeString()}</span>
                          {responseDurationMs !== null && (
                            <span
                              className="inline-flex items-center gap-1"
                              title={i18n.language.startsWith('fr') ? 'Temps de réponse' : 'Response time'}
                            >
                              <span aria-hidden="true">·</span>
                              <span>{formatResponseDuration(responseDurationMs)}</span>
                            </span>
                          )}
                        </p>
                        */}
                        {message.type === 'ai' && (
                          <div className="flex flex-wrap items-center justify-end gap-1 text-muted-foreground">
                            {generationVersions.length > 1 && (
                              <div className="mr-1 inline-flex h-7 items-center rounded-md border border-border/70 bg-background/70">
                                <button
                                  type="button"
                                  onClick={() => setSelectedGenerationVersions((previous) => ({
                                    ...previous,
                                    [generationGroupId]: getGenerationVersion(generationVersions[selectedGenerationIndex - 1]),
                                  }))}
                                  disabled={selectedGenerationIndex <= 0}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-l-md transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-30"
                                  aria-label={t('aiAssistant.previousResponseVersion')}
                                  title={t('aiAssistant.previousResponseVersion')}
                                >
                                  <ChevronLeft className="h-3.5 w-3.5" />
                                </button>
                                <span className="min-w-[34px] px-1 text-center text-[11px] font-medium tabular-nums text-muted-foreground">
                                  {selectedGenerationIndex + 1}/{generationVersions.length}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedGenerationVersions((previous) => ({
                                    ...previous,
                                    [generationGroupId]: getGenerationVersion(generationVersions[selectedGenerationIndex + 1]),
                                  }))}
                                  disabled={selectedGenerationIndex >= generationVersions.length - 1}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-r-md transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-30"
                                  aria-label={t('aiAssistant.nextResponseVersion')}
                                  title={t('aiAssistant.nextResponseVersion')}
                                >
                                  <ChevronRight className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => handleCopyMessage(message)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-foreground"
                              aria-label={copiedMessageId === message.id ? t('aiAssistant.copiedResponse') : t('aiAssistant.copyResponse')}
                              title={copiedMessageId === message.id ? t('aiAssistant.copiedResponse') : t('aiAssistant.copyResponse')}
                            >
                              {copiedMessageId === message.id ? <Check className="h-3.5 w-3.5 text-[rgb(25,166,166)]" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                            {!message.metadata?.system && generationGroupId === latestRegeneratableGenerationGroupId && (
                              <button
                                type="button"
                                onClick={() => handleRegenerateResponse(message, messageIndex)}
                                disabled={isLoading}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label={regeneratingMessageId === message.id ? t('aiAssistant.regeneratingResponse') : t('aiAssistant.regenerateResponse')}
                                title={regeneratingMessageId === message.id ? t('aiAssistant.regeneratingResponse') : t('aiAssistant.regenerateResponse')}
                              >
                                <RefreshCw className={`h-3.5 w-3.5 ${regeneratingMessageId === message.id ? 'animate-spin' : ''}`} />
                              </button>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                      </div>
                      {message.type === 'user' && editingMessageId !== message.id && !message.metadata?.technical && (
                        <button
                          type="button"
                          onClick={() => handleStartEditingMessage(message)}
                          disabled={isLoading}
                          className="mr-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-[opacity,color,background-color] hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(25,166,166)]/30 group-hover/message:opacity-100 group-focus-within/message:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={t('aiAssistant.editMessage')}
                          title={t('aiAssistant.editMessage')}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    )
                  })
                )}
                {hasUserMessages && selectedTurnAiIds.size === 0 && !isLoading && (
                  <div className="flex min-h-[220px] items-center justify-center px-6 text-center text-sm text-muted-foreground">
                    {t('aiAssistant.selectUserMessageHint', 'Sélectionnez un message pour afficher la réponse de l’IA.')}
                  </div>
                )}
                {isLoading && isLatestUserSelected && (
                  <div className="flex justify-start" role="status" aria-live="polite">
                    <div className="ml-5 flex items-center gap-3 py-2 pr-1">
                      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <span className="absolute inset-0 animate-ping rounded-full bg-[rgb(25,166,166)]/15" />
                        <Sparkles className="relative h-4 w-4 animate-pulse" aria-hidden="true" />
                      </div>
                      <div className="flex w-56 flex-col gap-2.5" aria-hidden="true">
                        <div className="ai-generation-bar h-2.5 w-full rounded-full bg-muted-foreground/25" />
                        <div
                          className="ai-generation-bar ai-generation-bar-delayed h-2.5 w-3/4 rounded-full bg-muted-foreground/15"
                        />
                      </div>
                      <span className="sr-only">{t('aiAssistant.thinking')}</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} className="h-1" />
              </div>
                <aside className={hasUserMessages
                  ? 'relative z-10 flex min-h-0 w-full shrink-0 flex-col overflow-visible border-t border-border bg-background lg:h-full lg:w-[30%] lg:border-l lg:border-t-0'
                  : 'relative z-40 mx-auto flex w-full max-w-3xl shrink-0 flex-col overflow-visible bg-transparent px-4 sm:px-6'
                }>
                  {hasUserMessages && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left lg:hidden"
                      onClick={() => setIsMobileUserListOpen((open) => !open)}
                      aria-expanded={isMobileUserListOpen}
                      aria-controls="ai-user-messages-panel"
                    >
                      <MessageSquare className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('aiAssistant.yourMessagesCount', 'Vos messages ({{count}})', { count: selectableUserMessages.length })}
                        </span>
                        {selectedUserMessage && (
                          <span className="mt-0.5 block truncate text-sm text-foreground">
                            {getUserMessagePreview(selectedUserMessage, t('aiAssistant.emptyUserMessage', 'Message'))}
                          </span>
                        )}
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isMobileUserListOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                  <div
                    id="ai-user-messages-panel"
                    ref={userMessagesContainerRef}
                    className={`${hasUserMessages ? (isMobileUserListOpen ? 'flex max-h-[42vh]' : 'hidden') : 'hidden'} min-h-0 flex-col overflow-y-auto [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] dark:[scrollbar-color:#475569_transparent] ${hasUserMessages ? 'lg:flex lg:max-h-none lg:flex-1' : ''}`}
                  >
                  <div className="sticky top-0 z-10 hidden border-b border-border bg-background/95 px-3 py-2.5 lg:block">
                    <p className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('aiAssistant.yourMessages', 'Vos messages')}
                    </p>
                  </div>
                  {hasMoreMessages && (
                    <div ref={olderMessagesSentinelRef} className="flex items-center justify-center py-2">
                      {isLoadingOlderMessages ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span>{t('aiAssistant.loadingOlderMessages', 'Chargement des messages précédents…')}</span>
                        </div>
                      ) : olderMessagesError ? (
                        <div className="flex items-center gap-2 px-2 text-xs">
                          <span className="text-destructive">{olderMessagesError}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setOlderMessagesError(null)
                              const container = userMessagesContainerRef.current
                              if (container) {
                                prevScrollHeightRef.current = container.scrollHeight
                              }
                              loadOlderMessages()
                            }}
                            className="font-semibold text-primary underline hover:text-primary/80"
                          >
                            {t('common.retry', 'Réessayer')}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                  <div className="flex flex-col gap-2 p-2">
                    {selectableUserMessages.map((message) => {
                      const isSelected = selectedUserMessageId === message.id
                      const preview = getUserMessagePreview(message, t('aiAssistant.emptyUserMessage', 'Message'))
                      const offerIdsFromMsg = normalizeCandidateIds(message.metadata?.offer_ids)
                      const messageOfferIds = offerIdsFromMsg.length > 0
                        ? offerIdsFromMsg
                        : (message.offerId ? [message.offerId] : [])
                      const messageCandidateIds = message.candidateIds?.length
                        ? message.candidateIds
                        : (message.candidateId ? [message.candidateId] : [])
                      return (
                        <div
                          key={message.id}
                          id={`ai-user-message-${message.id}`}
                          className={`rounded-xl border transition-colors ${isSelected
                            ? 'border-primary bg-primary/10 shadow-sm'
                            : 'border-transparent bg-muted/50 hover:bg-muted'}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedUserMessageId(message.id)
                              messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
                              setIsMobileUserListOpen(false)
                            }}
                            className="w-full px-3 py-2.5 text-left"
                            aria-pressed={isSelected}
                          >
                            <p className="line-clamp-2 text-sm font-medium leading-5 text-foreground md:line-clamp-4">
                              {preview}
                            </p>
                            {/* Hidden for now: message time
                            <span className="mt-2 block text-[11px] text-muted-foreground">
                              {message.timestamp.toLocaleTimeString()}
                            </span>
                            */}
                          </button>
                          {(messageCandidateIds.length > 0 || messageOfferIds.length > 0) && (
                            <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2.5">
                              {messageCandidateIds.map((cid) => {
                                const candidate = getCandidateById(cid)
                                const name = candidate?.name || t('aiAssistant.loadingCandidate', 'Candidat...')
                                const initials = name
                                  .split(' ')
                                  .map((part) => part[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2)
                                return (
                                  <a
                                    key={`candidate-${cid}`}
                                    href={`/candidates/${cid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    title={name}
                                    className="inline-flex max-w-full items-center gap-1 rounded-full bg-background px-1.5 py-0.5 text-[11px] font-medium text-foreground ring-1 ring-border transition-colors hover:bg-primary/10 hover:text-primary hover:ring-primary/30"
                                  >
                                    <span className="h-4 w-4 shrink-0 overflow-hidden rounded-full bg-primary/10">
                                      {candidate?.profile_picture ? (
                                        <img
                                          src={getImageUrl(candidate.profile_picture, candidate.name)}
                                          alt=""
                                          className="h-full w-full object-cover"
                                        />
                                      ) : (
                                        <span className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-primary/70 text-[8px] font-bold text-white">
                                          {initials || '?'}
                                        </span>
                                      )}
                                    </span>
                                    <span className="truncate">{name}</span>
                                  </a>
                                )
                              })}
                              {messageOfferIds.map((oid) => {
                                const offer = getOfferById(oid)
                                const title = offer?.job_title || t('aiAssistant.loadingOffer', 'Offre...')
                                return (
                                  <a
                                    key={`offer-${oid}`}
                                    href={`/offers/${oid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    title={title}
                                    className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-200 transition-colors hover:bg-indigo-100 hover:ring-indigo-300 dark:bg-indigo-950/60 dark:text-indigo-300 dark:ring-indigo-800 dark:hover:bg-indigo-950"
                                  >
                                    <Briefcase className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{title}</span>
                                  </a>
                                )
                              })}
                            </div>
                          )}
                          {isSelected && editingMessageId !== message.id && !message.metadata?.technical && (
                            <div className="flex justify-end px-2 pb-2">
                              <button
                                type="button"
                                onClick={() => handleStartEditingMessage(message)}
                                disabled={isLoading}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                                aria-label={t('aiAssistant.editMessage')}
                                title={t('aiAssistant.editMessage')}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                          {isSelected && editingMessageId === message.id && (
                            <div className="space-y-2 px-2 pb-2">
                              <textarea
                                value={editedMessageContent}
                                onChange={(event) => setEditedMessageContent(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Escape') handleCancelEditingMessage()
                                  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                                    event.preventDefault()
                                    handleResendEditedMessage(message)
                                  }
                                }}
                                rows={Math.min(6, Math.max(3, editedMessageContent.split('\n').length))}
                                className="w-full resize-y rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                                aria-label={t('aiAssistant.editMessage')}
                                autoFocus
                              />
                              <div className="flex justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={handleCancelEditingMessage}
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
                                  aria-label={t('aiAssistant.cancelEdit')}
                                  title={t('aiAssistant.cancelEdit')}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleResendEditedMessage(message)}
                                  disabled={!editedMessageContent.trim() || isLoading}
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={t('aiAssistant.resendMessage')}
                                  title={t('aiAssistant.resendMessage')}
                                >
                                  {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  </div>

              {/* Input */}
              <div className={`relative z-40 w-full min-w-0 max-w-full shrink-0 space-y-3 overflow-visible px-2 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-3 ${hasUserMessages ? 'mt-auto border-t border-border bg-background' : 'border-0 bg-transparent'}`}>
                {messages.length === 0 && !inputMessage.trim() && (
                  activeStarterAction === 'candidate-analysis' ? (
                    <div className="mx-auto w-[97%] px-2">
                      <div className="mb-1.5 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveStarterAction(null)}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-teal-300"
                          aria-label={t('aiAssistant.backToQuickActions')}
                          title={t('aiAssistant.backToQuickActions')}
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </button>
                        <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                          {t('aiAssistant.chooseCandidateAnalysisType')}
                        </p>
                      </div>
                      <div className="space-y-0.5">
                        {candidateAnalysisStarterActions.map((action) => (
                          <button
                            key={action.id}
                            type="button"
                            onClick={() => {
                              setInputMessage(action.prompt)
                              setPendingQuickActionId(action.id)
                              setIsAnalyzeCandidateEnabled(true)
                              openCandidateDropdown()
                            }}
                            disabled={isLoading || aiStatus?.status !== 'available'}
                            className="group flex min-h-9 w-fit items-center gap-3 rounded-lg py-2 pr-3 text-left text-slate-700 transition-all duration-150 hover:translate-x-0.5 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 disabled:opacity-60 dark:text-slate-200 dark:hover:text-teal-300"
                          >
                            <Users className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-teal-600 dark:text-slate-500 dark:group-hover:text-teal-300" />
                            <span className="text-sm font-medium">{action.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : activeStarterAction === 'candidate-comparison' ? (
                    <div className="mx-auto w-[97%] px-2">
                      <div className="mb-1.5 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveStarterAction(null)}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-teal-300"
                          aria-label={t('aiAssistant.backToQuickActions')}
                          title={t('aiAssistant.backToQuickActions')}
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </button>
                        <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                          {t('aiAssistant.chooseCandidateComparisonType')}
                        </p>
                      </div>
                      <div className="space-y-0.5">
                        {candidateComparisonStarterActions.map((action) => (
                          <button
                            key={action.id}
                            type="button"
                            onClick={() => {
                              setInputMessage(action.prompt)
                              setIsAnalyzeCandidateEnabled(true)
                              openCandidateDropdown()
                            }}
                            disabled={isLoading || aiStatus?.status !== 'available'}
                            className="group flex min-h-9 w-fit items-center gap-3 rounded-lg py-2 pr-3 text-left text-slate-700 transition-all duration-150 hover:translate-x-0.5 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 disabled:opacity-60 dark:text-slate-200 dark:hover:text-teal-300"
                          >
                            <Scale className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-teal-600 dark:text-slate-500 dark:group-hover:text-teal-300" />
                            <span className="text-sm font-medium">{action.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : activeStarterAction === 'offer-management' ? (
                    <div className="mx-auto w-[97%] px-2">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (activeOfferSubmenu === 'edit') {
                                setActiveOfferSubmenu(null)
                              } else {
                                setActiveStarterAction(null)
                              }
                              setPendingOfferQuickAction(null)
                              setIsOfferSelectorOpen(false)
                              setIsDropdownOpen(false)
                            }}
                            className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-teal-300"
                            aria-label={t('aiAssistant.backToQuickActions')}
                            title={t('aiAssistant.backToQuickActions')}
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </button>
                          <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                            {activeOfferSubmenu === 'edit'
                              ? t('aiAssistant.chooseOfferEditAction', 'Choose how to edit the offer')
                              : t('aiAssistant.chooseOfferAction', 'Choose an offer action')}
                          </p>
                        </div>
                        {pendingOfferQuickAction && (
                          <span className="shrink-0 text-[11px] text-indigo-600 dark:text-indigo-300">
                            {pendingOfferQuickAction.requiresCandidate && selectedOfferIds.length === 1
                              ? t('aiAssistant.selectOneCandidate', 'Select a candidate')
                              : t('aiAssistant.selectOneOffer', 'Select an offer')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {(activeOfferSubmenu === 'edit' ? offerEditQuickActions : primaryOfferQuickActions).map((action) => {
                          const Icon = action.id === 'offer-update' ? Pencil
                            : action.id === 'offer-edit-menu' ? Pencil
                            : action.id === 'offer-regenerate' ? RefreshCw
                            : action.id === 'offer-compare-candidate' ? Scale
                            : action.id === 'offer-create' ? Briefcase
                            : FileText
                          return (
                            <button
                              key={action.id}
                              type="button"
                              title={action.tooltip || action.label}
                              onClick={() => handleOfferQuickAction(action)}
                              disabled={isLoading || aiStatus?.status !== 'available' || action.disabled}
                              className="group flex min-h-9 w-fit items-center gap-3 rounded-lg py-2 pr-3 text-left text-slate-700 transition-all duration-150 hover:translate-x-0.5 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 disabled:opacity-60 dark:text-slate-200 dark:hover:text-teal-300"
                            >
                              <Icon className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-teal-600 dark:text-slate-500 dark:group-hover:text-teal-300" />
                              <span className="text-sm font-medium">{action.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-center gap-2 px-1">
                      {starterActions.map((action) => {
                        const Icon = action.icon
                        return (
                          <button
                            key={action.id}
                            type="button"
                            onClick={() => {
                              setPendingQuickActionId(null)
                              if (action.id === 'analyze') {
                                setActiveStarterAction('candidate-analysis')
                              } else if (action.id === 'compare') {
                                setActiveStarterAction('candidate-comparison')
                              } else if (action.id === 'regenerate-offer' || action.id === 'edit-offer') {
                                const quickAction: OfferQuickAction = {
                                  id: action.id === 'regenerate-offer' ? 'offer-regenerate' : 'offer-update',
                                  label: action.label,
                                  prompt: action.prompt,
                                  uiEvent: {
                                    type: 'quick_action',
                                    action: action.id === 'regenerate-offer' ? 'offer.regenerate' : 'offer.edit_content',
                                  },
                                  selection: 'single',
                                }
                                setActiveStarterAction(null)
                                setActiveOfferSubmenu(null)
                                setPendingOfferQuickAction(quickAction)
                                setOfferSelectionMode('single')
                                setSelectedOfferIds([])
                                openOfferDropdown(action.id === 'edit-offer' ? 'draft' : '')
                              } else {
                                handleSendMessage(action.prompt)
                              }
                            }}
                            disabled={isLoading || aiStatus?.status !== 'available'}
                            className="group inline-flex h-10 items-center gap-2 rounded-full border border-slate-200/80 bg-white/70 px-3.5 text-left text-slate-700 shadow-[0_2px_10px_rgba(15,23,42,0.035)] backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-teal-300 hover:bg-teal-50/70 hover:text-teal-700 hover:shadow-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:border-teal-700 dark:hover:bg-teal-950/40 dark:hover:text-teal-300"
                          >
                            <Icon className="h-4 w-4 shrink-0 text-teal-600 transition-transform group-hover:scale-105 dark:text-teal-300" />
                            <span className="whitespace-nowrap text-xs font-semibold sm:text-[13px]">{action.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  )
                )}
                <div className="relative z-50 mx-auto flex w-[97%] min-w-0 max-w-full flex-col gap-2 overflow-visible rounded-[24px] border border-slate-200/90 bg-white/95 p-2 backdrop-blur-xl transition-all duration-200 focus-within:border-teal-400/70 focus-within:ring-2 focus-within:ring-teal-500/10 dark:border-slate-700 dark:bg-slate-900/95">
                  {(selectedCandidateIds.length > 0 || selectedOfferIds.length > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5 px-1 pt-1">
                      {selectedCandidateIds.map((candidateId) => {
                        const candidate = getCandidateById(candidateId)
                        const name = candidate?.name || t('aiAssistant.selectedCandidate', 'Candidat sélectionné')
                        return (
                          <span
                            key={`candidate-badge-${candidateId}`}
                            className="inline-flex h-8 max-w-full items-center gap-1 rounded-full border border-teal-200 bg-teal-50 py-1 pl-1 pr-1.5 text-xs font-semibold text-teal-700 dark:border-teal-800 dark:bg-teal-950/50 dark:text-teal-300"
                          >
                            <a
                              href={`/candidates/${candidateId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={name}
                              className="inline-flex min-w-0 items-center gap-1.5 rounded-full text-teal-700 transition-colors hover:text-teal-900 dark:text-teal-300 dark:hover:text-teal-100"
                            >
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-teal-100 text-[10px] font-bold text-teal-700 dark:bg-teal-900 dark:text-teal-200">
                                {candidate?.profile_picture ? (
                                  <img
                                    src={getImageUrl(candidate.profile_picture, candidate.name)}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <UserRound className="h-3.5 w-3.5" />
                                )}
                              </span>
                              <span className="max-w-[12rem] truncate">{name}</span>
                            </a>
                            <button
                              type="button"
                              onClick={() => setSelectedCandidateIds((prev) => prev.filter((id) => id !== candidateId))}
                              className="rounded-full p-0.5 text-teal-500 transition-colors hover:bg-teal-100 hover:text-teal-800 dark:hover:bg-teal-900 dark:hover:text-teal-100"
                              aria-label={t('aiAssistant.removeCandidate')}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        )
                      })}
                      {selectedOffers.map((offer) => (
                        <span
                          key={`offer-badge-${offer.id}`}
                          className="inline-flex h-8 max-w-full items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 py-1 pl-2.5 pr-1.5 text-xs font-semibold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300"
                        >
                          <a
                            href={`/offers/${offer.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={offer.job_title || `Offre #${offer.id}`}
                            className="inline-flex min-w-0 items-center gap-1.5 rounded-full text-indigo-700 transition-colors hover:text-indigo-900 dark:text-indigo-300 dark:hover:text-indigo-100"
                          >
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            <span className="max-w-[12rem] truncate">{offer.job_title || `Offre #${offer.id}`}</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => handleSelectOffer(offer)}
                            className="rounded-full p-0.5 text-indigo-400 transition-colors hover:bg-indigo-100 hover:text-indigo-700 dark:hover:bg-indigo-900 dark:hover:text-indigo-100"
                            aria-label={t('aiAssistant.removeOffer', 'Retirer l’offre')}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex min-w-0 items-end gap-2">
                  {/* Plus Menu */}
                  <div className="relative" ref={plusMenuRef}>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setIsDropdownOpen(false)
                        setIsPlusMenuOpen(!isPlusMenuOpen)
                      }}
                      disabled={isLoading}
                      aria-expanded={isPlusMenuOpen}
                      aria-haspopup="menu"
                      aria-label={t('aiAssistant.quickMenuTitle')}
                      className={`h-10 w-10 rounded-2xl border-0 bg-slate-100/80 text-slate-600 shadow-none transition-all duration-200 hover:bg-teal-50 hover:text-teal-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-teal-950/60 dark:hover:text-teal-300 ${isPlusMenuOpen ? 'bg-teal-50 text-teal-600 dark:bg-teal-950/60 dark:text-teal-300' : ''}`}
                    >
                      <Plus className={`h-5 w-5 transition-transform duration-200 ease-out ${isPlusMenuOpen ? 'rotate-45' : 'rotate-0'}`} />
                    </Button>
                    <div
                      role="menu"
                      aria-hidden={!isPlusMenuOpen}
                      className={`absolute bottom-full left-0 z-[80] mb-3 max-h-[min(24rem,calc(100vh-8rem))] w-[min(20rem,calc(100vw-2rem))] origin-bottom-left overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-2 shadow-[0_22px_55px_-18px_rgba(15,23,42,0.35)] transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-slate-700/80 dark:bg-slate-900 dark:shadow-black/40 ${isPlusMenuOpen ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-2 scale-[0.97] opacity-0'}`}
                    >
                      <div className="pointer-events-none absolute left-4 top-[calc(100%-1px)] h-2.5 w-5 bg-white [clip-path:polygon(0_0,100%_0,50%_100%)] drop-shadow-sm dark:bg-slate-900" />
                      <div className="space-y-1">
                        <button
                          type="button"
                          role="menuitem"
                          tabIndex={isPlusMenuOpen ? 0 : -1}
                          onClick={() => {
                            setIsAnalyzeCandidateEnabled(true)
                            setIsPlusMenuOpen(false)
                            openCandidateDropdown()
                          }}
                          className="group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-200 hover:bg-teal-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 dark:hover:bg-teal-950/40"
                        >
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-700 transition-transform duration-200 group-hover:scale-105 dark:bg-teal-950/70 dark:text-teal-300">
                            <UserRound className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {t('aiAssistant.enterCandidate')}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-teal-600 dark:group-hover:text-teal-300" />
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          tabIndex={isPlusMenuOpen ? 0 : -1}
                          onClick={() => {
                            openOfferDropdown()
                          }}
                          className="group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-200 hover:bg-indigo-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 dark:hover:bg-indigo-950/40"
                        >
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 transition-transform duration-200 group-hover:scale-105 dark:bg-indigo-950/70 dark:text-indigo-300">
                            <FileText className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {t('aiAssistant.selectOffer', 'Sélectionner une offre')}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-indigo-600 dark:group-hover:text-indigo-300" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Candidate Selector (only while the list is open) */}
                  {isDropdownOpen && (
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isDropdownOpen) {
                            setIsDropdownOpen(false)
                          } else {
                            openCandidateDropdown()
                          }
                        }}
                        aria-expanded={isDropdownOpen}
                        aria-haspopup="dialog"
                        className={`flex h-10 shrink-0 items-center gap-2 rounded-2xl border px-3 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 ${isDropdownOpen ? 'border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950/50 dark:text-teal-300' : 'border-slate-200 bg-slate-50/80 text-slate-600 hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-teal-800 dark:hover:bg-teal-950/50 dark:hover:text-teal-300'}`}
                        disabled={isLoading}
                      >
                        <UserRound className="h-4 w-4 shrink-0" />
                        <span>
                          {selectedCandidateIds.length > 0
                            ? t('aiAssistant.candidatesCount', '{{count}} sélectionné(s)', { count: selectedCandidateIds.length })
                            : t('aiAssistant.chooseCandidate')}
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu - Multi-select */}
                      <div
                        role="dialog"
                        aria-modal="false"
                        aria-hidden={!isDropdownOpen}
                        className={`absolute bottom-full left-[-3rem] z-50 mb-3 flex min-h-0 max-h-[min(28rem,calc(100vh-10rem))] w-[min(22rem,calc(100vw-2rem))] origin-bottom-left flex-col rounded-2xl border border-slate-200/80 bg-white shadow-[0_22px_55px_-18px_rgba(15,23,42,0.35)] transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-slate-700/80 dark:bg-slate-900 dark:shadow-black/40 ${isDropdownOpen ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-2 scale-[0.97] opacity-0'}`}
                      >
                          <div className="pointer-events-none absolute left-14 top-[calc(100%-1px)] h-2.5 w-5 bg-white [clip-path:polygon(0_0,100%_0,50%_100%)] drop-shadow-sm dark:bg-slate-900" />
                          <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3.5 dark:border-slate-700/80">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                {t('aiAssistant.selectCandidatesWithCount', 'Sélectionner les candidats ({{count}})', { count: candidatesTotalCount })}
                              </p>
                              <p className={`mt-0.5 truncate text-[11px] font-medium ${selectedCandidateIds.length >= (selectedOfferIds.length > 0 ? MAX_SELECTED_CANDIDATES_WITH_OFFER : MAX_SELECTED_CANDIDATES) ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                {selectedOfferIds.length > 0
                                  ? t('aiAssistant.candidateLimitWithOffer', '2 candidats maximum avec une offre sélectionnée')
                                  : t('aiAssistant.candidateLimit', '5 candidats maximum')}
                              </p>
                            </div>
                            {selectedCandidateIds.length > 0 && (
                              <button
                                type="button"
                                tabIndex={isDropdownOpen ? 0 : -1}
                                onClick={() => setIsDropdownOpen(false)}
                                className="shrink-0 rounded-lg bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 dark:bg-teal-950/60 dark:text-teal-300 dark:hover:bg-teal-950"
                              >
                                {t('aiAssistant.done', 'Terminé')}
                              </button>
                            )}
                          </div>
                          <div className="space-y-2 border-b border-slate-200/80 px-3 py-3 dark:border-slate-700/80">
                            <div className="relative">
                              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <Input
                                value={candidateSearch}
                                onChange={(e) => setCandidateSearch(e.target.value)}
                                placeholder={t('candidates.search', 'Rechercher...')}
                                tabIndex={isDropdownOpen ? 0 : -1}
                                className="h-10 rounded-xl border-slate-200 bg-slate-50/70 pl-9 text-sm shadow-none focus-visible:border-teal-400 focus-visible:ring-2 focus-visible:ring-teal-500/10 dark:border-slate-700 dark:bg-slate-800/70"
                              />
                            </div>
                            <select
                              value={candidateGroupFilter}
                              onChange={(e) => setCandidateGroupFilter(e.target.value)}
                              tabIndex={isDropdownOpen ? 0 : -1}
                              className="flex h-10 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-700 outline-none transition-colors focus:border-teal-400 focus:ring-2 focus:ring-teal-500/10 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200"
                            >
                              <option value="all">{t('aiAssistant.allGroups')}</option>
                              {teams.filter(team => !team.is_archived).map((team) => (
                                <option key={team.id} value={team.id.toString()}>
                                  {team.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div ref={candidateListRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto rounded-b-2xl p-2 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] dark:[scrollbar-color:#475569_transparent]">
                            {(loadingCandidates || searchingCandidates) && candidates.length === 0 ? (
                              <div className="flex items-center justify-center px-4 py-8">
                                <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
                              </div>
                            ) : candidates.length === 0 ? (
                              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                {t('aiAssistant.noCandidate')}
                              </div>
                            ) : (
                              <>
                                {candidates.map((candidate) => {
                                  const isSelected = selectedCandidateIds.includes(candidate.id)
                                  const candidateLimit = selectedOfferIds.length > 0 ? MAX_SELECTED_CANDIDATES_WITH_OFFER : MAX_SELECTED_CANDIDATES
                                  const isLimitReached = !isSelected && selectedCandidateIds.length >= candidateLimit
                                  return (
                                    <button
                                      key={candidate.id}
                                      type="button"
                                      tabIndex={isDropdownOpen ? 0 : -1}
                                      disabled={isLimitReached}
                                      onClick={() => {
                                        if (isLimitReached) return
                                        setSelectedCandidateIds(prev => {
                                          if (isSelected) return prev.filter(id => id !== candidate.id)
                                          const limit = selectedOfferIds.length > 0 ? MAX_SELECTED_CANDIDATES_WITH_OFFER : MAX_SELECTED_CANDIDATES
                                          if (prev.length >= limit) return prev
                                          return [...prev, candidate.id]
                                        })
                                        setCandidateCache(prev => ({ ...prev, [candidate.id]: candidate }))
                                      }}
                                      className={`group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 disabled:cursor-not-allowed disabled:opacity-40 ${isSelected ? 'border-teal-200 bg-teal-50/80 dark:border-teal-900 dark:bg-teal-950/40' : 'border-transparent hover:border-slate-200 hover:bg-slate-50 disabled:hover:border-transparent disabled:hover:bg-transparent dark:hover:border-slate-700 dark:hover:bg-slate-800/70'}`}
                                    >
                                      {/* Checkbox */}
                                      <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${isSelected
                                        ? 'border-teal-600 bg-teal-600 text-white'
                                        : 'border-slate-300 bg-white group-hover:border-teal-400 dark:border-slate-600 dark:bg-slate-900'
                                        }`}>
                                        {isSelected && <Check className="h-3 w-3" />}
                                      </div>
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-teal-50 text-sm font-semibold text-teal-700 ring-2 ring-teal-100 dark:bg-teal-950/60 dark:text-teal-300 dark:ring-teal-900">
                                        {candidate.profile_picture ? (
                                          <img
                                            src={getImageUrl(candidate.profile_picture, candidate.name)}
                                            alt={candidate.name}
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          candidate.name
                                            .split(' ')
                                            .map((n) => n[0])
                                            .join('')
                                            .toUpperCase()
                                        )}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{candidate.name}</p>
                                        {(candidate.role || candidate.email) && (
                                          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                            {candidate.role || candidate.email}
                                          </p>
                                        )}
                                      </div>
                                    </button>
                                  )
                                })}
                                {hasMoreCandidates && (
                                  <div ref={candidateScrollSentinelRef} className="h-10 flex items-center justify-center">
                                    {loadingMoreCandidates && (
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                      </div>
                    </div>
                  )}

                  {/* Offer Selector (open while choosing; selected offers stay as badges above) */}
                  {isOfferSelectorOpen && (
                    <div ref={offerSelectorRef} className="relative">
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              if (isOfferSelectorOpen) {
                                setIsOfferSelectorOpen(false)
                              } else {
                                openOfferDropdown()
                              }
                            }}
                            aria-expanded={isOfferSelectorOpen}
                            aria-haspopup="dialog"
                            className={`flex h-10 shrink-0 items-center gap-2 rounded-2xl border px-3 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${isOfferSelectorOpen ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300' : 'border-slate-200 bg-slate-50/80 text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-indigo-800 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300'}`}
                            disabled={isLoading}
                          >
                            <FileText className="h-4 w-4 shrink-0" />
                            <span>
                              {selectedOfferIds.length > 0
                                ? t('aiAssistant.offerSelected', 'Offre sélectionnée')
                                : t('aiAssistant.chooseOffer', 'Choisir une offre')}
                            </span>
                            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isOfferSelectorOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Offer dropdown */}
                          <div
                            role="dialog"
                            aria-modal="false"
                            aria-hidden={!isOfferSelectorOpen}
                            className={`absolute bottom-full left-[-3rem] z-50 mb-3 flex min-h-0 max-h-[min(28rem,calc(100vh-10rem))] w-[min(22rem,calc(100vw-2rem))] origin-bottom-left flex-col rounded-2xl border border-slate-200/80 bg-white shadow-[0_22px_55px_-18px_rgba(15,23,42,0.35)] transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-slate-700/80 dark:bg-slate-900 dark:shadow-black/40 ${isOfferSelectorOpen ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-2 scale-[0.97] opacity-0'}`}
                          >
                            <div className="pointer-events-none absolute left-14 top-[calc(100%-1px)] h-2.5 w-5 bg-white [clip-path:polygon(0_0,100%_0,50%_100%)] drop-shadow-sm dark:bg-slate-900" />
                            <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3.5 dark:border-slate-700/80">
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                  {offerStatusFilter === 'draft'
                                    ? t('aiAssistant.selectDraftOffersWithCount', 'Sélectionner un brouillon ({{count}})', { count: offersTotalCount })
                                    : t('aiAssistant.selectOffersWithCount', 'Sélectionner une offre ({{count}})', { count: offersTotalCount })}
                                </p>
                                {!MULTI_OFFER_SELECTION_ENABLED && (
                                  <p className="mt-0.5 truncate text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                    {t('aiAssistant.multiOfferComingSoon', 'La comparaison de plusieurs offres sera bientôt disponible.')}
                                  </p>
                                )}
                              </div>
                              {selectedOfferIds.length > 0 && (
                                <button
                                  type="button"
                                  tabIndex={isOfferSelectorOpen ? 0 : -1}
                                  onClick={() => setIsOfferSelectorOpen(false)}
                                  className="shrink-0 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 dark:bg-indigo-950/60 dark:text-indigo-300 dark:hover:bg-indigo-950"
                                >
                                  {t('aiAssistant.done', 'Terminé')}
                                </button>
                              )}
                            </div>
                            <div className="border-b border-slate-200/80 px-3 py-3 dark:border-slate-700/80">
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <Input
                                  value={offerSearch}
                                  onChange={(e) => setOfferSearch(e.target.value)}
                                  placeholder={t('aiAssistant.searchOffers', 'Rechercher une offre...')}
                                  tabIndex={isOfferSelectorOpen ? 0 : -1}
                                  className="h-10 rounded-xl border-slate-200 bg-slate-50/70 pl-9 text-sm shadow-none focus-visible:border-indigo-400 focus-visible:ring-2 focus-visible:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-800/70"
                                />
                              </div>
                            </div>
                            <div ref={offerListRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto rounded-b-2xl p-2 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] dark:[scrollbar-color:#475569_transparent]">
                              {(loadingOffers || searchingOffers) && offers.length === 0 ? (
                                <div className="flex items-center justify-center px-4 py-8">
                                  <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                                </div>
                              ) : offers.length === 0 ? (
                                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                  {offerStatusFilter === 'draft'
                                    ? t('aiAssistant.noDraftOffers', 'Aucun brouillon trouvé')
                                    : t('aiAssistant.noOffers', 'Aucune offre trouvée')}
                                </div>
                              ) : (
                                <>
                                  {offers.map((offer) => {
                                    const isSelected = selectedOfferIds.includes(offer.id)
                                    return (
                                      <button
                                        key={offer.id}
                                        type="button"
                                        tabIndex={isOfferSelectorOpen ? 0 : -1}
                                        onClick={() => handleSelectOffer(offer)}
                                        className={`group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${isSelected ? 'border-indigo-200 bg-indigo-50/80 dark:border-indigo-900 dark:bg-indigo-950/40' : 'border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-800/70'}`}
                                      >
                                        <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${isSelected
                                          ? 'border-indigo-600 bg-indigo-600 text-white'
                                          : 'border-slate-300 bg-white group-hover:border-indigo-400 dark:border-slate-600 dark:bg-slate-900'
                                          }`}>
                                          {isSelected && <Check className="h-3 w-3" />}
                                        </div>
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-indigo-50 text-sm font-semibold text-indigo-700 ring-2 ring-indigo-100 dark:bg-indigo-950/60 dark:text-indigo-300 dark:ring-indigo-900">
                                          <Briefcase className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{offer.job_title}</p>
                                          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                            {[offer.department, offer.contract_type, offer.experience_level].filter(Boolean).join(' · ')}
                                          </p>
                                        </div>
                                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${offer.status === 'published'
                                          ? 'bg-green-50 text-green-700 dark:bg-green-950/60 dark:text-green-300'
                                          : offer.status === 'draft'
                                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                          }`}>
                                          {getLocalizedOfferStatus(offer.status, t)}
                                        </span>
                                      </button>
                                    )
                                  })}
                                  {hasMoreOffers && (
                                    <div ref={offerScrollSentinelRef} className="h-10 flex items-center justify-center">
                                      {loadingMoreOffers && (
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </>
                    </div>
                  )}

                  {/* Message Input */}
                  <textarea
                    ref={messageInputRef}
                    rows={1}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={aiStatus?.status === 'available' ? t('aiAssistant.inputPlaceholder') : t('aiAssistant.aiNotAvailable')}
                    className="min-h-11 max-h-[5.5rem] min-w-0 flex-1 resize-none overflow-y-auto rounded-2xl border-0 bg-transparent px-3 py-2 text-[15px] leading-6 text-foreground shadow-none placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-0 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] dark:[scrollbar-color:#475569_transparent]"
                    disabled={isLoading || aiStatus?.status !== 'available' || contextLimit?.level === 'hard'}
                  />

                  {/* Send Button - Enhanced Design */}
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputMessage.trim() || isLoading || aiStatus?.status !== 'available' || contextLimit?.level === 'hard'}
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-all duration-200 ${!inputMessage.trim() || isLoading || aiStatus?.status !== 'available' || contextLimit?.level === 'hard'
                      ? 'cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
                      : 'bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-[0_8px_20px_rgba(13,148,136,0.3)] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(13,148,136,0.38)] active:translate-y-0 active:scale-95'
                      }`}
                    aria-label={t('aiAssistant.sendMessage')}
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </button>
                  </div>
                </div>
                <p className="relative z-10 flex items-center justify-center gap-1.5 px-4 text-center text-[11px] text-muted-foreground/80">
                  <Lightbulb className="h-3.5 w-3.5 text-teal-600" />
                  {t('aiAssistant.quickActionsHint')}
                </p>
                {hasUserMessages && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-[calc(100%_-_24px)] z-0 h-40 bg-background"
                />
                )}
              </div>
                </aside>
              </div>
            </CardContent>
          </Card>
        </div>

        <aside className={`fixed z-[50] flex flex-row overflow-hidden transition-all duration-300 ease-out ${showHistory
          ? 'inset-y-0 right-0 w-[min(92vw,380px)] md:w-[clamp(320px,24vw,406px)]'
          : 'right-3 top-24 h-auto w-14 rounded-2xl border border-border bg-background shadow-lg'
        }`}>
          <div className={`order-1 flex h-full min-w-0 flex-col overflow-hidden rounded-l-xl border border-border bg-background text-foreground shadow-2xl shadow-black/10 transition-[width,opacity] duration-300 ease-out dark:shadow-black/40 sm:shadow-xl ${showHistory ? 'flex-1 opacity-100' : 'w-0 shrink-0 opacity-0'}`}>
            <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-4 2xl:px-5">
              <span className="min-w-0 truncate text-sm font-bold text-foreground">{t('aiAssistant.conversationHistory', 'Conversation History')}</span>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={handleNewChat} className="h-8 w-8 rounded-lg border-border bg-background px-0 text-xs font-semibold text-foreground shadow-sm hover:bg-accent 2xl:w-auto 2xl:px-3">
                  <Plus className="h-3.5 w-3.5 text-primary 2xl:mr-1.5" />
                  <span className="hidden 2xl:inline">{t('aiAssistant.newChat', 'New Chat')}</span>
                </Button>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-1 hover:bg-muted rounded transition-all"
                  title={t('aiAssistant.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="border-b border-border px-3 py-4 2xl:px-5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={historySearchRef}
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder={t('aiAssistant.searchConversations', 'Search conversations...')}
                  className="h-10 rounded-lg border-border bg-background pl-9 text-sm text-foreground shadow-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-4 2xl:px-5">
              {isLoadingSessions ? (
                <div className="py-4 text-sm text-muted-foreground">{t('aiAssistant.loading')}</div>
              ) : sessionLoadError ? (
                <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                  <p className="text-destructive">{sessionLoadError}</p>
                  <button
                    type="button"
                    className="mt-2 font-semibold text-primary underline"
                    onClick={() => void loadSessions()}
                  >
                    {t('common.retry', 'Réessayer')}
                  </button>
                </div>
              ) : filteredSessions.length === 0 ? (
                <div className="py-4 text-sm text-muted-foreground">{t('aiAssistant.noConversations')}</div>
              ) : (
                filteredSessions.map((session, index) => {
                  // Hidden for now: session date grouping
                  // const section = getHistorySection(session.updated_at)
                  // const previousSection = index > 0 ? getHistorySection(filteredSessions[index - 1].updated_at) : ''
                  return (
                    <React.Fragment key={session.id}>
                      {/* Hidden for now: session date grouping
                      {section !== previousSection && (
                        <p className={`${index === 0 ? '' : 'mt-5'} mb-2 text-xs font-bold text-muted-foreground`}>
                          {section}
                        </p>
                      )}
                      */}
                      <div
                        className={`group relative flex w-full items-center justify-between gap-2 rounded-lg border-b border-border px-3 py-3 text-foreground transition-colors hover:bg-muted ${currentSession?.id === session.id ? 'border-l-4 border-l-primary bg-primary/10 shadow-sm' : ''
                          }`}
                      >
                        {editingSessionId === session.id ? (
                          <div className="flex-1 flex items-center space-x-2">
                            <Input
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => handleRenameKeyPress(session.id, e)}
                              className="flex-1 h-8 text-sm"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                            <button
                              onClick={(e) => handleSaveRename(session.id, e)}
                              className="p-1 hover:bg-primary/10 hover:text-primary rounded transition-all"
                              title={t('aiAssistant.save')}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCancelRename()
                              }}
                              className="p-1 hover:bg-muted-foreground/10 rounded transition-all"
                              title={t('aiAssistant.cancel')}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleSelectSession(session)}
                              className="flex-1 text-left min-w-0"
                            >
                              <div className="text-sm font-medium truncate">
                                {session.title || `${t('aiAssistant.conversation')} ${session.id}`}
                              </div>
                              {/* Hidden for now: session date
                              <div className="text-xs text-muted-foreground">
                                {new Date(session.updated_at).toLocaleDateString()}
                              </div>
                              */}
                            </button>
                            <div className="flex items-center space-x-1 lg:opacity-0 lg:group-hover:opacity-100">
                              <button
                                onClick={(e) => handleStartRename(session, e)}
                                className="p-1 hover:bg-primary/10 hover:text-primary rounded transition-all"
                                title={t('aiAssistant.rename')}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => handleDeleteSession(session.id, e)}
                                className="p-1 hover:bg-destructive/10 hover:text-destructive rounded transition-all"
                                title={t('aiAssistant.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </React.Fragment>
                  )
                })
              )}
            </div>
          </div>
          <div className={`order-2 w-14 shrink-0 flex-col items-center gap-2 bg-background/95 py-2 text-foreground lg:w-14 lg:justify-start ${showHistory ? 'hidden h-full border-l border-border py-5 md:flex' : 'flex h-auto'}`}>
          <button
            type="button"
            onClick={() => setShowHistory((isOpen) => !isOpen)}
            className={`group flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary ${showHistory ? 'bg-primary/10 text-primary' : ''}`}
            title={showHistory ? t('aiAssistant.hideHistory', 'Hide history') : t('aiAssistant.showHistory', 'Show history')}
            aria-label={showHistory ? t('aiAssistant.hideHistory', 'Hide history') : t('aiAssistant.showHistory', 'Show history')}
          >
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showHistory ? '-rotate-90' : 'rotate-90'}`} />
          </button>
          <div className="h-px w-6 bg-border" />
          <DeveloperContextInspector
            sessionId={currentSession?.id ?? null}
            refreshKey={`${currentSession?.id ?? 'none'}:${messages.length}:${contextInspectorEpoch}`}
            generationInProgress={isLoading}
          />
          <button
            type="button"
            onClick={() => {
              setShowHistory(true)
              window.setTimeout(() => historySearchRef.current?.focus(), 350)
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
            title={t('aiAssistant.searchConversations', 'Search conversations...')}
            aria-label={t('aiAssistant.searchConversations', 'Search conversations...')}
          >
            <Search className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => {
              setHistorySearch('')
              setShowHistory(true)
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
            title={t('aiAssistant.recentChats', 'Recent chats')}
            aria-label={t('aiAssistant.recentChats', 'Recent chats')}
          >
            <History className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={handleNewChat}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
            title={t('aiAssistant.newChat', 'New chat')}
            aria-label={t('aiAssistant.newChat', 'New chat')}
          >
            <Plus className="h-5 w-5" />
          </button>
          </div>
        </aside>
      </div>
    </div>
  )
}
